/* ==========================================================================
   GYM STUDIO — interface
   ==========================================================================
   Depends on ctx-gym-studio.js (window.CTXGymStudio) for catalog, geometry and
   recommendation. This file is the part the customer touches: the plan, the
   tray, swapping, dragging, the money, and save/resume.
   ========================================================================== */
(function () {
  'use strict';

  var S = window.CTXGymStudio;
  if (!S) return;

  var root = document.querySelector('[data-gym-studio]');
  if (!root) return;

  var CATALOG = [];
  var BUILD = [];              /* placed items, in plan order */
  var TRAY = [];               /* reserved; nothing is tray-only any more */
  var SELECTED = null;

  /* ── USAGE TRACKING, 2026-08-12 ────────────────────────────────────────────
     A snapshot dispatched on `document` so a small sibling script
     (ctx-gym-studio-tracking.js) can log it to Airtable without this file
     knowing anything about sessions, webhooks or networking — this file only
     ever describes what happened, it never sends anything itself. */
  var ENTRY_POINT = 'Direct';
  var QUIZ_DONE = false;
  var MACHINES_ADDED = 0;
  var MACHINES_REMOVED = 0;
  var GUARD_COUNT = 0;
  var LAST_ACTION = '';
  var REACHED_CART = false;
  var CART_VALUE = 0;
  var ROOM_TYPE_LABEL = {
    'spare-room': 'Spare Room', 'one-car': 'One-Car Garage', 'two-car': 'Two-Car Garage',
    'basement': 'Basement', 'studio': 'Studio', 'commercial': 'Commercial', 'custom': 'Custom'
  };
  function track(type) {
    try {
      document.dispatchEvent(new CustomEvent('ctx:gym-studio:track', {
        detail: {
          type: type,
          entry_point: ENTRY_POINT,
          room_type: ROOM_TYPE_LABEL[STATE.preset] || 'Custom',
          room_w: STATE.w,
          room_d: STATE.d,
          budget: Math.round((STATE.budget || 0) / 100),
          quiz_completed: QUIZ_DONE,
          machines_added: MACHINES_ADDED,
          machines_removed: MACHINES_REMOVED,
          final_count: BUILD.length,
          final_list: BUILD.map(function (it) { return cleanTitle(it.p.t); }).join(', '),
          guard_count: GUARD_COUNT,
          reached_cart: REACHED_CART,
          cart_value: CART_VALUE,
          last_action: LAST_ACTION
        }
      }));
    } catch (e) {}
  }

  /* THE ONE-RENDER TOKEN, and the reason entry effects are safe here.
     render() replaces the whole plan on every edit, so any animation keyed to a
     class render() emits unconditionally (is-selected, is-clash…) re-fires on
     every unrelated re-render — flip the floor to rubber and back and every
     selected machine re-announces itself. FRESH holds the ONE object that just
     arrived, is read once while building the markup, and is cleared at the
     bottom of render(). Object identity, so five identical benches are five
     distinct objects and only the new one matches. */
  var FRESH = null;

  /* The room itself: doors, columns, stairs. Kept separate from BUILD because
     they are not products — nothing here is priced, added to cart or counted in
     the coverage read. They exist to be designed around. */
  var FEATURES = [];
  var SELECTED_FEAT = null;
  var FEATDEF = window.CTXGymFeatures || {};

  /* ── THE ROOM SHELF, GROUPED ───────────────────────────────────────────────
     29 features in one flat list is a scroll, not a menu. These are the groups,
     in fixed order — doors first, unconditionally, because a door is the only
     feature whose swing actually constrains where equipment can go.

     The order NEVER changes. A shelf that rearranges itself cannot be learned.

     Note the definitions in ctx-gym-models.js carry a `cat` field of "arch" or
     "fix". It is DELIBERATELY IGNORED here: 7 doors against 22 of everything
     else is not a useful division for someone hunting for a toilet. Do not
     "simplify" this table back to that field.

     Anything not listed falls into a trailing "Other" group, so a 30th feature
     added to the model library can never silently vanish from the shelf. */
  var FEAT_GROUPS = [
    { label: 'Doors & openings', keys: [
      'single-door-left-inswing', 'single-door-right-inswing',
      'single-door-left-outswing', 'single-door-right-outswing',
      'double-door-inswing', 'sectional-garage-door-with-tracks',
      'warehouse-coiling-rollup-door'
    ] },
    { label: 'Water & washrooms', keys: [
      'wall-drinking-fountain', 'bottle-filler-drinking-station', 'water-cooler',
      'toilet', 'wall-mounted-sink', 'shower-stall', 'mop-service-sink'
    ] },
    { label: 'Storage & lockers', keys: [
      'locker-bank', 'storage-shelving', 'towel-cubby-station', 'waste-recycling-station'
    ] },
    { label: 'Front desk & seating', keys: [
      'straight-desk-with-chair', 'l-shaped-desk-with-chair',
      'reception-counter-with-chair', 'waiting-bench', 'round-table-four-chairs'
    ] },
    { label: 'Service & equipment', keys: [
      'vending-machine', 'commercial-refrigerator', 'gym-floor-scale',
      'tripod-turnstile', 'cleaning-cart', 'towel-laundry-cart'
    ] }
  ];

  var STATE = {
    preset: 'two-car',
    w: 240, d: 240,            /* inches */
    budget: 1500000,           /* cents; 0 = no limit */
    goal: 'general',
    cardioBias: 0.35,          /* share of budget the flagship cardio may take */
    floor: 'grid',             /* grid | rubber */
    showClear: true            /* walkway clearance dashes */
  };

  var PRESETS = {
    'spare-room':  { label: 'Spare bedroom',  w: 132, d: 144 },
    'one-car':     { label: 'One-car garage', w: 144, d: 240 },
    'two-car':     { label: 'Two-car garage', w: 240, d: 240 },
    'basement':    { label: 'Basement',       w: 300, d: 360 },
    'studio':      { label: 'Studio 1,500sf', w: 360, d: 600 },
    'commercial':  { label: 'Commercial 5,000sf', w: 720, d: 1000 },
    'custom':      { label: 'Custom', w: 240, d: 240 }
  };

  var el = {
    plan:    root.querySelector('[data-gs-plan]'),
    tray:    root.querySelector('[data-gs-tray]'),
    subtotal:root.querySelector('[data-gs-subtotal]'),
    save:    root.querySelector('[data-gs-save]'),
    total:   root.querySelector('[data-gs-total]'),
    tier:    root.querySelector('[data-gs-tier]'),
    nudge:   root.querySelector('[data-gs-nudge]'),
    count:   root.querySelector('[data-gs-count]'),
    area:    root.querySelector('[data-gs-area]'),
    fill:    root.querySelector('[data-gs-fill]'),
    preset:  root.querySelector('[data-gs-preset]'),
    wIn:     root.querySelector('[data-gs-w]'),
    dIn:     root.querySelector('[data-gs-d]'),
    budget:  root.querySelector('[data-gs-budget]'),
    budgetL: root.querySelector('[data-gs-budget-label]'),
    goal:    root.querySelector('[data-gs-goal]'),
    addBtn:  root.querySelector('[data-gs-add]'),
    swap:    root.querySelector('[data-gs-swap]'),
    status:  root.querySelector('[data-gs-status]'),
    shareBtn:root.querySelector('[data-gs-share]'),
    pdfBtn:  root.querySelector('[data-gs-pdf]'),
    floorBtns: root.querySelector('[data-gs-floorbar]'),
    clearBtns: root.querySelector('[data-gs-clearbar]'),
    guard:   root.querySelector('[data-gs-guard-bar]'),
    advice:  root.querySelector('[data-gs-advice]'),
    /* NOT .gs__stagewrap — that class already belongs to the gate's blur
       wrapper, several hundred lines up. Two elements, one name, and the
       handles would have been measured against the wrong box. */
    stageWrap: root.querySelector('.gs__planstage'),
    viewport: root.querySelector('[data-gs-viewport]'),
    zoomIn:  root.querySelector('[data-gs-zoom-in]'),
    zoomOut: root.querySelector('[data-gs-zoom-out]'),
    zoomFit: root.querySelector('[data-gs-zoom-fit]'),
    zoomVal: root.querySelector('[data-gs-zoom-val]'),
    hint:    root.querySelector('[data-gs-hint]'),
    hintOk:  root.querySelector('[data-gs-hint-ok]'),
    handles: root.querySelector('[data-gs-handles]'),
    dragPad: root.querySelector('[data-gs-dragpad]'),
    dragHint: root.querySelector('[data-gs-draghint]'),
    hMove:   root.querySelector('[data-gs-handle-move]'),
    hMoveLbl: root.querySelector('[data-gs-handle-move-label]'),
    hRotate: root.querySelector('[data-gs-handle-rotate]'),
    hRemove: root.querySelector('[data-gs-handle-remove]'),
    hRotateLbl: root.querySelector('[data-gs-handle-rotate-label]'),
    hRemoveLbl: root.querySelector('[data-gs-handle-remove-label]'),
    hName:   root.querySelector('[data-gs-handle-name]'),
    resolve: root.querySelector('[data-gs-resolve]'),
    qty:     root.querySelector('[data-gs-qty]'),
    budgetCap: root.querySelector('[data-gs-budget-cap]'),
    tabs:    root.querySelectorAll('[data-gs-tab]'),
    tabCount: root.querySelector('[data-gs-tab-count]'),
    suggList: root.querySelector('[data-gs-suggest-list]'),
    shopAll: root.querySelector('[data-gs-shop-all]'),
    shopAllLabel: root.querySelector('[data-gs-shop-all-label]'),
    shopAllCount: root.querySelector('[data-gs-shop-all-count]'),
    prefSummary: root.querySelector('[data-gs-pref-summary]'),
    prefsBtn: root.querySelector('[data-gs-edit-prefs]'),
    startOverBtn: root.querySelector('[data-gs-start-over]'),
    help:    root.querySelector('[data-gs-help]'),
    helpBtn: root.querySelector('[data-gs-help-open]'),
    coverage:root.querySelector('[data-gs-coverage]'),
    covNote: root.querySelector('[data-gs-cov-note]'),
    selInfo: root.querySelector('[data-gs-sel-info]'),
    search:  root.querySelector('[data-gs-search]'),
    results: root.querySelector('[data-gs-results]'),
    gate:    root.querySelector('[data-gs-gate]'),
    stage:   root.querySelector('[data-gs-stage]'),
    ctaLabel:root.querySelector('[data-gs-cta-label]'),
    ctaPrice:root.querySelector('[data-gs-cta-price]'),
    /* The email form is built inside the dialog now, so its fields cannot be
       cached here — they do not exist until the dialog opens. openEmailDialog
       queries them from the card it just rendered. */
    emailBtn: root.querySelector('[data-gs-email-open]'),
    bugBtn: root.querySelector('[data-gs-report-bug]'),
    bugNativeForm: root.querySelector('.gs__bugform'),
    bugNativeBody: root.querySelector('[data-gs-bug-native-body]'),
    bugPostedFlag: root.querySelector('[data-gs-bug-posted]')
  };

  /* How far outside the room a machine may be parked while rearranging. Wide
     enough to get a big rig properly out of the way, bounded so the drawing
     cannot be stretched until the room is an unreadable sliver — the canvas grows
     to contain whatever is placed, so an unbounded stage is an unbounded canvas. */
  var STAGE_MARGIN = 96;   /* inches */

  /* Per-item placement state, in BUILD order: 'ok' | 'tight' | 'outside' |
     'collision'. Recomputed by markOverlaps and read by everything that renders. */
  var STATES = [];

  function stateOf(it) {
    var i = BUILD.indexOf(it);
    return i > -1 ? (STATES[i] || 'ok') : 'ok';
  }

  /* The two states worth telling somebody about, in the words they get told. */
  var STATE_COPY = {
    outside: {
      label: 'Outside gym area',
      text: 'You can leave this here temporarily while rearranging.'
    },
    collision: {
      label: 'Overlapping',
      text: 'This is on top of another machine, or in a doorway.'
    },
    tight: {
      label: 'Tight walkway',
      text: 'It fits, but a neighbour is inside the recommended clearance.'
    }
  };

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  /* ── Silhouettes ───────────────────────────────────────────────────────────
     One per equipment class, drawn INSIDE a 0..w by 0..d box in inches. This is
     the whole reason the class work exists: a treadmill and a leg press can
     share a footprint and must not share a shape, or the plan reads as a
     spreadsheet with rounded corners. */

  /* Art wins over vector when it exists for this machine's TYPE, then for its
     class. Checked centrally rather than inside each of the 36 models, so a new
     drawing is one line in the section and needs no JS change at all. */
  var ART_USED = [];   /* [key, url] pairs referenced by the current render */

  function silhouette(cls, w, d, it) {
    if (window.CTXGymArt) {
      var ty = it && it.p ? it.p.ty : '';
      var handle = it && it.p ? it.p.h : '';
      /* Variant SKU rides along for the SKU-pinned tier — the BUILT Strength
         pack keys its renders on exact SKUs, above handle/type/class. */
      var sku = it && it.p ? (it.p.s || '') : '';
      var entry = window.CTXGymArt.entry(cls, ty, handle, sku);
      if (entry) {
        ART_USED.push(entry);
        var a = window.CTXGymArt.markup(cls, ty, w, d, handle, sku);
        if (a) return a;
      }
    }
    var m = S.CLASSES[cls];
    if (m && typeof m.draw === 'function') return m.draw(w, d);
    return legacySilhouette(cls, w, d);
  }

  /* Retained only as a safety net for a class with no model. Everything real is
     drawn by ctx-gym-models.js. */
  function legacySilhouette(cls, w, d) {
    var s = [];
    function r(x, y, ww, dd, extra) {
      s.push('<rect x="' + x + '" y="' + y + '" width="' + ww + '" height="' + dd + '" rx="2" ' + (extra || '') + '/>');
    }
    function line(x1, y1, x2, y2) {
      s.push('<line x1="' + x1 + '" y1="' + y1 + '" x2="' + x2 + '" y2="' + y2 + '" class="gs-det"/>');
    }
    function circle(cx, cy, rr) {
      s.push('<circle cx="' + cx + '" cy="' + cy + '" r="' + rr + '" class="gs-det"/>');
    }

    switch (cls) {
      case 'treadmill':
        r(0, 0, w, d, 'class="gs-body"');
        r(w * 0.15, d * 0.22, w * 0.7, d * 0.66, 'class="gs-belt"');
        r(0, 0, w, d * 0.16, 'class="gs-console"');
        break;

      case 'elliptical':
        r(0, d * 0.1, w, d * 0.8, 'class="gs-body"');
        circle(w / 2, d * 0.16, Math.min(w, d) * 0.11);
        line(w * 0.3, d * 0.3, w * 0.3, d * 0.92);
        line(w * 0.7, d * 0.3, w * 0.7, d * 0.92);
        break;

      case 'bike':
        r(w * 0.1, 0, w * 0.8, d, 'class="gs-body"');
        circle(w / 2, d * 0.25, Math.min(w, d) * 0.16);
        r(w * 0.28, d * 0.62, w * 0.44, d * 0.16, 'class="gs-det-f"');
        break;

      case 'stepper':
        r(0, 0, w, d, 'class="gs-body"');
        for (var i = 1; i <= 4; i++) line(w * 0.15, d * (0.2 * i), w * 0.85, d * (0.2 * i));
        break;

      case 'rower':
        r(w * 0.2, 0, w * 0.6, d, 'class="gs-body"');
        line(w / 2, d * 0.1, w / 2, d * 0.95);
        r(w * 0.32, d * 0.3, w * 0.36, d * 0.12, 'class="gs-det-f"');
        break;

      case 'rack':
        r(0, 0, w, d, 'class="gs-body"');
        var post = Math.min(w, d) * 0.16;
        r(0, 0, post, post, 'class="gs-det-f"');
        r(w - post, 0, post, post, 'class="gs-det-f"');
        r(0, d - post, post, post, 'class="gs-det-f"');
        r(w - post, d - post, post, post, 'class="gs-det-f"');
        line(0, post / 2, w, post / 2);
        break;

      case 'bench':
        r(w * 0.25, 0, w * 0.5, d, 'class="gs-body"');
        r(w * 0.15, d * 0.05, w * 0.7, d * 0.5, 'class="gs-det-f"');
        break;

      case 'selector':
        r(0, 0, w, d, 'class="gs-body"');
        r(0, 0, w * 0.3, d, 'class="gs-stack"');
        for (var j = 1; j <= 5; j++) line(w * 0.03, d * (0.14 * j), w * 0.27, d * (0.14 * j));
        break;

      case 'plate':
        r(0, 0, w, d, 'class="gs-body"');
        circle(w * 0.2, d * 0.25, Math.min(w, d) * 0.13);
        circle(w * 0.8, d * 0.25, Math.min(w, d) * 0.13);
        line(w * 0.2, d * 0.4, w * 0.8, d * 0.4);
        break;

      case 'multi':
        r(0, 0, w, d, 'class="gs-body"');
        r(0, 0, w * 0.22, d * 0.45, 'class="gs-stack"');
        r(w * 0.78, 0, w * 0.22, d * 0.45, 'class="gs-stack"');
        r(0, d * 0.55, w * 0.22, d * 0.45, 'class="gs-stack"');
        r(w * 0.78, d * 0.55, w * 0.22, d * 0.45, 'class="gs-stack"');
        break;

      case 'storage':
        r(0, 0, w, d, 'class="gs-body"');
        for (var k = 1; k <= 3; k++) line(w * (0.25 * k), 0, w * (0.25 * k), d);
        break;

      default:
        r(0, 0, w, d, 'class="gs-body"');
    }
    return s.join('');
  }

  /* ── Render: the plan ──────────────────────────────────────────────────── */

  /* Feet-and-inches, the way a tape measure reads. 240in is "20 ft", 246in is
     "20 ft 6 in" — never "20.5 ft", which nobody measures a room in. */
  function ft(inches) {
    var f = Math.floor(inches / 12);
    var i = Math.round(inches - f * 12);
    if (i === 12) { f += 1; i = 0; }
    return f + '&#8242;' + (i ? ' ' + i + '&#8243;' : '');
  }

  /* THE DRAWING MARGIN — ONE DEFINITION, USED EVERYWHERE.

     It has to scale with the room, because it holds the dimension lines and
     their labels: at 5000 sq ft a fixed 12in gutter is invisible, and at 130 sq
     ft a 60in one swamps the drawing.

     It is a function rather than a constant because it is needed in three
     places — drawing the plan, converting a pointer position into room
     coordinates, and positioning a machine mid-drag. When the margin became
     dynamic those last two were left on the old hardcoded 12, so grabbing a
     machine in a 20x20 room threw it 21.6in up and left the moment the pointer
     moved, and it appeared to land somewhere other than where it was dropped.
     Any future change to this number must stay in this function. */
  function planPad() {
    return Math.max(26, Math.min(STATE.w, STATE.d) * 0.14);
  }

  function render() {
    /* Captured before the DOM is replaced — see the note where these are put
       back at the end of this function. */
    var keepScrollX = el.viewport ? el.viewport.scrollLeft : 0;
    var keepScrollY = el.viewport ? el.viewport.scrollTop : 0;

    var room = { w: STATE.w, d: STATE.d };
    var pad = planPad();
    var vbW = room.w + pad * 2;
    var vbH = room.d + pad * 2;
    var ts = Math.max(7, Math.min(room.w, room.d) * 0.042);   /* label size, inches */

    /* ── THE CANVAS MUST CONTAIN EVERYTHING IT IS ASKED TO DRAW ──────────────
       The packer lays machines in rows and keeps going down the page when it
       runs out of depth. The canvas was only ever room + margin tall, so
       anything past the back wall was drawn OUTSIDE the viewBox and clipped —
       silently. The machine was in the build, priced in the total, listed in the
       tray, and invisible on the floor. Dillon: "items are getting added outside
       of the gym and they just disappear."

       It looked like it only affected certain products because `arrange` sorts
       by zone: cardio is laid first and always fit, so the casualties were
       always the last zones — benches, storage, dumbbells and plates.

       Overflow in this tool is meant to be SHOWN, not prevented — a machine that
       does not fit should be visible and flagged, so the shopper can move it,
       grow the room or take it out. Clipping it is the one outcome that helps
       nobody. The canvas now grows to include anything outside the walls; the
       room itself is still drawn at its true size, so the spill reads as
       equipment sitting outside the building, which is exactly what it is. */
    var spillX = 0, spillY = 0;
    var spillMinX = 0, spillMinY = 0;

    BUILD.forEach(function (it) {
      var e = S.effFp(it);
      spillX = Math.max(spillX, (it.x || 0) + e.w);
      spillY = Math.max(spillY, (it.y || 0) + e.d);
      spillMinX = Math.min(spillMinX, it.x || 0);
      spillMinY = Math.min(spillMinY, it.y || 0);
    });

    /* ── ROOM FEATURES MUST NOT TRIGGER THE OVERFLOW WARNING ──────────────────
       They used to be measured into the spill, and that produced a false alarm
       Dillon photographed: a room with everything comfortably inside still said
       "SOME PIECES WILL NOT FIT THIS ROOM", because a garage door was sitting in
       the wall line.

       Features are ALLOWED outside the room, deliberately — `startFeatureDrag`
       clamps them to ±their own depth precisely so a door can sit in the wall it
       belongs to. So they still have to grow the CANVAS, or a door in the wall
       gets clipped, but they must not count as equipment that does not fit. The
       banner counted `fits === false` (zero) while `hasSpill` came from geometry
       (true), and the mismatch fell through to the word "Some". */
    var featX = 0, featY = 0;
    FEATURES.forEach(function (f) {
      var t = (f.rot === 90 || f.rot === 270);
      featX = Math.max(featX, (f.x || 0) + (t ? f.d : f.w));
      featY = Math.max(featY, (f.y || 0) + (t ? f.w : f.d));
      spillMinX = Math.min(spillMinX, f.x || 0);
      spillMinY = Math.min(spillMinY, f.y || 0);
    });

    /* Equipment-only, so the warning is about equipment. */
    var hasSpill = spillX > room.w + 0.5 || spillY > room.d + 0.5 ||
                   spillMinX < -0.5 || spillMinY < -0.5;

    /* Canvas growth considers features too, so nothing is ever clipped. */
    spillX = Math.max(spillX, featX);
    spillY = Math.max(spillY, featY);

    /* ── THE VIEWBOX ORIGIN MOVES NOW ─────────────────────────────────────────
       It was fixed at "0 0", which was fine while nothing could exist to the left
       of or above the room. Staging changed that: a machine dragged out past the
       left wall has a NEGATIVE x, draws at `pad + x`, and with pad around 30in a
       piece parked 100in out lands at -70 — outside the viewBox and invisible.
       Exactly the disappearing-machine bug this tool already had once, arriving
       from the other direction.

       So the box is computed from the real extent of everything drawn. Drawing
       coordinates are untouched, which matters because `toRoom()` and the drag
       handler both convert through the live screen CTM — the browser applies the
       viewBox offset for them, so moving the origin cannot desynchronise dragging
       the way changing `planPad()` once did. */
    var boxL = Math.min(0, pad + spillMinX - pad * 0.4);
    var boxT = Math.min(0, pad + spillMinY - pad * 0.4);
    var boxR = pad + Math.max(room.w, spillX) + pad;
    var boxB = pad + Math.max(room.d, spillY) + pad;

    vbW = boxR - boxL;
    vbH = boxB - boxT;

    var svg = [];
    svg.push('<svg class="gs-svg" viewBox="' + boxL + ' ' + boxT + ' ' + vbW + ' ' + vbH +
             '" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Top-down plan of your gym">');

    /* Gradients and the contact shadow, defined once for the whole plan. These
       are what give the machines their lit top faces — every model references
       them by name, so restyling the whole library is editing this block. */
    svg.push(
      '<defs>' +
        '<linearGradient id="gsFrame" x1="0" y1="0" x2="0.35" y2="1">' +
          '<stop offset="0" stop-color="#ffffff"/><stop offset="1" stop-color="#dfe4ea"/></linearGradient>' +
        '<linearGradient id="gsConsole" x1="0" y1="0" x2="0.3" y2="1">' +
          '<stop offset="0" stop-color="#f4f6f8"/><stop offset="1" stop-color="#c9d1da"/></linearGradient>' +
        '<linearGradient id="gsIron" x1="0" y1="0" x2="0.4" y2="1">' +
          '<stop offset="0" stop-color="#8e99a7"/><stop offset="1" stop-color="#5d6774"/></linearGradient>' +
        '<linearGradient id="gsPlate" x1="0" y1="0" x2="0.4" y2="1">' +
          '<stop offset="0" stop-color="#a9b2be"/><stop offset="1" stop-color="#6b7684"/></linearGradient>' +
        '<linearGradient id="gsStack" x1="0" y1="0" x2="0.45" y2="1">' +
          '<stop offset="0" stop-color="#c3cad3"/><stop offset="1" stop-color="#8b95a2"/></linearGradient>' +
        '<linearGradient id="gsPad" x1="0" y1="0" x2="0.3" y2="1">' +
          '<stop offset="0" stop-color="#ff3355"/><stop offset="1" stop-color="#c00024"/></linearGradient>' +
        '<filter id="gsDrop" x="-25%" y="-25%" width="160%" height="160%">' +
          '<feDropShadow dx="1.4" dy="2.2" stdDeviation="1.6" flood-color="#0c111d" flood-opacity="0.28"/>' +
        '</filter>' +
        /* Tiled BAG lockup across the floor. Dillon's reason is the honest one:
           these plans are the work product, and an unmarked render is one that
           walks to a competitor. Kept faint on screen so it does not fight the
           equipment while someone is arranging it, and turned up for the export,
           which is the copy that actually leaves the building. */
        /* Tiled BAG lockup, on BOTH floor finishes. Larger and more widely spaced
           than the first attempt — the reference Dillon gave is a big, very faint
           emblem, so the tile has to breathe rather than read as a texture. */
        (window.CTX_GYM_LOGO
          ? '<pattern id="gsWm" width="220" height="150" patternUnits="userSpaceOnUse" patternTransform="rotate(-16)">' +
              '<image href="' + window.CTX_GYM_LOGO + '" xlink:href="' + window.CTX_GYM_LOGO + '" ' +
              'x="18" y="52" width="150" height="35" preserveAspectRatio="xMidYMid meet"/>' +
            '</pattern>'
          : '') +
      '</defs>'
    );

    /* ── Floor finish ─────────────────────────────────────────────────────
       Two looks. The grid is the working drawing — a plan you measure against.
       The rubber is what the room will actually look like once it is built, and
       seeing equipment on black rubber rather than graph paper is most of the
       difference between "a diagram" and "my gym".

       Speckle positions are FIXED, not random: a fresh random scatter on every
       render makes the floor visibly crawl every time anything moves. */
    if (STATE.floor === 'rubber') {
      /* Plain dark rubber. Red flecks were here and Dillon cut them: at plan
         scale a scatter of dots competes with the equipment for attention, and
         the floor is meant to be the quiet thing the machines sit on. The 4ft
         tile seams below carry the material read on their own. */
      svg.push(
        '<defs><pattern id="gsRubber" width="48" height="48" patternUnits="userSpaceOnUse">' +
          '<rect width="48" height="48" class="gs-rubber-base"/>' +
        '</pattern></defs>'
      );
    }

    /* The overflow apron. Drawn first, under everything, so machines pushed past
       the walls sit on a visibly different surface rather than on nothing —
       "outside the building" needs to look like somewhere, or a machine sitting
       there just reads as a rendering fault. */
    if (hasSpill) {
      svg.push('<rect x="' + boxL + '" y="' + boxT + '" width="' + vbW + '" height="' + vbH + '" class="gs-spill"/>');
    }

    /* floor + 12in grid, so size is felt rather than read */
    var rubber = STATE.floor === 'rubber';
    svg.push('<rect x="' + pad + '" y="' + pad + '" width="' + room.w + '" height="' + room.d +
             '" class="gs-floor' + (rubber ? ' gs-floor--rubber' : '') + '"' +
             (rubber ? ' fill="url(#gsRubber)"' : '') + '/>');

    /* 12in graph on the working drawing; 4ft tile seams on rubber, because that
       is the size the tiles actually come in. */
    var step = rubber ? 48 : 12;
    var gcls = rubber ? 'gs-seam' : 'gs-grid';
    for (var gx = step; gx < room.w; gx += step) {
      svg.push('<line x1="' + (pad + gx) + '" y1="' + pad + '" x2="' + (pad + gx) + '" y2="' + (pad + room.d) + '" class="' + gcls + '"/>');
    }
    for (var gy = step; gy < room.d; gy += step) {
      svg.push('<line x1="' + pad + '" y1="' + (pad + gy) + '" x2="' + (pad + room.w) + '" y2="' + (pad + gy) + '" class="' + gcls + '"/>');
    }

    /* Watermark sits above the grid and below the equipment, so machines stay
       fully legible on top of it. */
    if (window.CTX_GYM_LOGO) {
      /* The lockup is dark artwork, so on a dark floor it has to be flipped to
         white or it is simply invisible. Class carries that, not the markup. */
      svg.push('<rect x="' + pad + '" y="' + pad + '" width="' + room.w + '" height="' + room.d +
               '" fill="url(#gsWm)" class="gs-wm' + (rubber ? ' gs-wm--dark' : '') + '"/>');
    }

    /* ── Dimensions, outside the wall line ────────────────────────────────
       Drawn like a set of plans: witness lines running out from each corner, a
       dimension line between them with end ticks, and the measurement sitting on
       it. Outside the walls on purpose — inside, it would sit on top of the
       equipment and read as part of the room.

       This is what lets someone hold the drawing against their actual garage:
       the grid gives them a foot, the dimension gives them the wall, and the
       scale bar ties the two together. */
    var off = pad * 0.55;                    /* how far out the dimension line sits */
    var tick = Math.max(3, ts * 0.42);

    function dimH(y, x1, x2, label) {
      svg.push(
        '<line x1="' + x1 + '" y1="' + y + '" x2="' + x2 + '" y2="' + y + '" class="gs-dim"/>' +
        '<line x1="' + x1 + '" y1="' + (y - tick) + '" x2="' + x1 + '" y2="' + (y + tick) + '" class="gs-dim"/>' +
        '<line x1="' + x2 + '" y1="' + (y - tick) + '" x2="' + x2 + '" y2="' + (y + tick) + '" class="gs-dim"/>' +
        '<text class="gs-dimtxt" x="' + ((x1 + x2) / 2) + '" y="' + (y - ts * 0.45) + '" font-size="' + ts + '">' + label + '</text>'
      );
    }
    function dimV(x, y1, y2, label) {
      svg.push(
        '<line x1="' + x + '" y1="' + y1 + '" x2="' + x + '" y2="' + y2 + '" class="gs-dim"/>' +
        '<line x1="' + (x - tick) + '" y1="' + y1 + '" x2="' + (x + tick) + '" y2="' + y1 + '" class="gs-dim"/>' +
        '<line x1="' + (x - tick) + '" y1="' + y2 + '" x2="' + (x + tick) + '" y2="' + y2 + '" class="gs-dim"/>' +
        '<text class="gs-dimtxt" x="' + (x - ts * 0.45) + '" y="' + ((y1 + y2) / 2) + '" font-size="' + ts +
          '" transform="rotate(-90 ' + (x - ts * 0.45) + ' ' + ((y1 + y2) / 2) + ')">' + label + '</text>'
      );
    }

    /* witness lines from the corners out to the dimension lines */
    svg.push('<line x1="' + pad + '" y1="' + (pad - off * 0.35) + '" x2="' + pad + '" y2="' + pad + '" class="gs-witness"/>');
    svg.push('<line x1="' + (pad + room.w) + '" y1="' + (pad - off * 0.35) + '" x2="' + (pad + room.w) + '" y2="' + pad + '" class="gs-witness"/>');
    svg.push('<line x1="' + (pad - off * 0.35) + '" y1="' + pad + '" x2="' + pad + '" y2="' + pad + '" class="gs-witness"/>');
    svg.push('<line x1="' + (pad - off * 0.35) + '" y1="' + (pad + room.d) + '" x2="' + pad + '" y2="' + (pad + room.d) + '" class="gs-witness"/>');

    dimH(pad - off, pad, pad + room.w, ft(room.w) + ' wide');
    dimV(pad - off, pad, pad + room.d, ft(room.d) + ' deep');

    /* ── Scale bar ─────────────────────────────────────────────────────────
       A 5ft bar in alternating blocks, under the room. Without it the drawing is
       only comparative — with it, it is measurable. */
    var barFt = room.w > 480 ? 10 : 5;
    var barLen = barFt * 12;
    var bx = pad;
    var by = pad + room.d + off * 0.72;
    svg.push('<g class="gs-scale">');
    for (var sb = 0; sb < barFt; sb++) {
      svg.push('<rect x="' + (bx + sb * 12) + '" y="' + by + '" width="12" height="' + (ts * 0.34) +
               '" class="' + (sb % 2 ? 'gs-scale-b' : 'gs-scale-a') + '"/>');
    }
    svg.push('<text class="gs-dimtxt gs-scale-lbl" x="' + (bx + barLen + ts * 0.4) + '" y="' + (by + ts * 0.34) +
             '" font-size="' + (ts * 0.92) + '">' + barFt + '&#8242; scale</text>');
    svg.push('</g>');

    /* ── Clearance layer ───────────────────────────────────────────────────
       Every machine's walkway space, drawn in ROOM coordinates and clipped to
       the room. Dillon: "the user space dashed line should never protrude
       outside of the exterior wall, it will simply collapse down as it travels
       outside the boundary."

       Which is right — clearance outside the building is not clearance, it is a
       wall. Clipping makes the halo shrink as a machine is pushed flush, so the
       drawing stops claiming space that does not exist and people can tightly
       pack a garage the way they actually do. */
    svg.push('<defs><clipPath id="gsRoomClip"><rect x="' + pad + '" y="' + pad +
             '" width="' + room.w + '" height="' + room.d + '"/></clipPath></defs>');

    /* The clearance layer can be switched off entirely. Dillon: people want to
       "look and see what their gym looks like unadulterated" — the walkway
       dashes are a working drawing, and at some point you just want the room. */
    if (STATE.showClear !== false) {
      svg.push('<g class="gs-clearlayer" clip-path="url(#gsRoomClip)">');
      BUILD.forEach(function (it) {
        var eff = S.effFp(it);
        /* DIRECTIONAL since 2026-08-07: the halo now shows where the space is
           actually needed — a treadmill's halo reaches 79in behind it and barely
           past its sides, instead of a misleading even ring. Drawn from the same
           envelope the tight-flags measure, so what the eye sees and what the
           engine judges are one thing. */
        var env = S.envPad(it);
        if (!env.t && !env.b && !env.l && !env.r) return;
        /* The SELECTED machine's walkway is drawn a shade stronger and everything
           else a shade weaker. Forty equally-weighted dashed halos read as a
           second machine edge on every item; one emphasised halo reads as
           information about the thing you are holding. */
        /* No `is-over` red variant any more. A walkway halo turning red was one
           of the ways the drawing announced a problem before the shopper had
           finished making it; the halo's job is to show where space is wanted,
           not to grade the layout. */
        svg.push('<rect class="gs-clear' +
                 (SELECTED === it ? ' is-sel' : '') +
                 '" x="' + (pad + it.x - env.l) + '" y="' + (pad + it.y - env.t) +
                 '" width="' + (eff.w + env.l + env.r) + '" height="' + (eff.d + env.t + env.b) + '"/>');
      });
      svg.push('</g>');
    }

    /* Room features sit UNDER the equipment: they are the room, and a machine
       that overlaps one should visibly sit on top of it so the clash is obvious
       rather than hidden. */
    FEATURES.forEach(function (f, i) {
      var def = FEATDEF[f.k];
      if (!def) return;
      var g = ['<g class="gs-feat' + (SELECTED_FEAT === f ? ' is-selected' : '') +
               (FRESH === f ? ' is-fresh' : '') +
               '" data-fidx="' + i + '" transform="translate(' + (pad + f.x) + ',' + (pad + f.y) +
               ') rotate(' + (f.rot || 0) + ')">'];
      g.push(def.draw(f.w, f.d, f.hinge || 1));
      g.push('<title>' + escapeHtml(def.label) + '</title>');
      g.push('</g>');
      svg.push(g.join(''));
    });

    BUILD.forEach(function (it, idx) {
      var fp = it.fp;                 /* the drawing, always in its own frame */
      var eff = S.effFp(it);          /* the box it occupies on the floor      */
      var pd = S.effPad(it);
      var cls = it.cls;

      /* The placement state is still COMPUTED into STATES by markOverlaps, and
         the advice panel, the tray and the printed plan all read it — but it no
         longer paints the machine at all, which is why nothing reads it in this
         loop any more. `is-clash`, `is-staged` and `is-tight` were three
         different colourings of "something is wrong here" applied to a drawing
         the shopper is still in the middle of making.

         The one state the plan still shows is `is-fresh`, which marks what just
         arrived rather than judging it. */
      var g = ['<g class="gs-item' + (SELECTED === it ? ' is-selected' : '') +
               (FRESH === it ? ' is-fresh' : '') +
               '" data-idx="' + idx + '" transform="translate(' + (pad + it.x) + ',' + (pad + it.y) + ')">'];

      /* THE WHOLE FOOTPRINT IS THE TARGET.

         Selection used to depend on hitting one of the silhouette's paths, so
         the gaps inside a rack — most of its area — were dead, and a treadmill
         could only be grabbed by its belt. This invisible rect covers the entire
         effective box, so anywhere inside the machine's outline works. It is
         drawn first and everything paints over it; both it and the artwork carry
         the same data-idx, so either one selects the same machine. */
      g.push('<rect class="gs-hit" x="0" y="0" width="' + eff.w + '" height="' + eff.d + '"/>');

      /* The clearance halo is drawn in a SEPARATE CLIPPED LAYER below, not here.
         Inside this group it would inherit the item's transform, and a clip path
         cannot then be expressed in room coordinates — so a machine pushed
         against a wall drew its walkway outside the building. */

      /* The silhouette is drawn in its own unrotated frame and then spun about
         the centre of the effective box. Rotating about the centre — rather than
         the origin — is what keeps a turned machine inside the footprint the
         packer reserved for it. */
      var spin = it.rot
        ? ' transform="translate(' + (eff.w / 2) + ',' + (eff.d / 2) + ') rotate(' + it.rot + ') translate(' + (-fp.w / 2) + ',' + (-fp.d / 2) + ')"'
        : '';
      /* .gs-shape carries the contact shadow, so the shadow covers the whole
         machine as one object rather than haloing every individual part. */
      g.push('<g class="gs-shape"' + spin + '>');
      g.push(silhouette(cls, fp.w, fp.d, it));
      /* ── THE INNER AMBER DASHED BOX — WHAT IT ACTUALLY IS ────────────────────
         It is NOT clearance, which is what everyone including the brief assumed.
         It is the "size estimated" flag: this product has no dimension
         metafields, so its footprint was derived from its listed floor area and
         the proportions were inferred from its class. The dashes say "this shape
         is a good estimate, not a measurement".

         Two things made it read as a mystery boundary. It was drawn on EVERY
         estimated machine permanently, and it was drawn HEAVIER than the walkway
         dashes it sat inside — so the softer, more important line looked like the
         annotation and the annotation looked like the machine's real edge.

         It now appears only on the machine that is SELECTED, where the panel
         beside the plan explains it in words at the same moment. The tray still
         carries "size estimated" on every affected row, so the caveat is never
         lost for the ones not currently selected. */
      if (fp.est && SELECTED === it && STATE.showClear !== false) {
        g.push('<rect x="0" y="0" width="' + fp.w + '" height="' + fp.d + '" class="gs-est"/>');
      }
      g.push('</g>');

      /* LABELS. Dillon: "you can't tell which item you're manipulating because
         they're not labeled."

         A number badge keyed to the tray row is the part that always fits — at
         a 5000 sq ft scale a machine is a few millimetres across and no name
         will ever be legible there. The name is drawn as well, but only when the
         shape is genuinely wide enough to hold it, so the plan never turns into
         a wall of overlapping text. Font size is expressed in inches and scales
         with the room exactly like everything else. */
      /* Labels sit OUTSIDE the rotation, on the effective box — text that spins
         with the machine would be upside down half the time. */
      var badgeR = Math.max(4.5, Math.min(9, Math.min(eff.w, eff.d) * 0.20));
      g.push('<g class="gs-badge">');
      g.push('<circle cx="' + (eff.w - badgeR * 0.6) + '" cy="' + (badgeR * 0.6) + '" r="' + badgeR + '"/>');
      g.push('<text x="' + (eff.w - badgeR * 0.6) + '" y="' + (badgeR * 0.6) + '" font-size="' + (badgeR * 1.25) + '">' + (idx + 1) + '</text>');
      g.push('</g>');

      /* ── NO PERMANENT NAME LABEL ON THE FLOOR ──────────────────────────────
         Every machine used to carry its name in text under its footprint, all the
         time. On a busy floor that is a wall of overlapping words sitting on top
         of the drawing, and on a phone it was most of what you could see — the
         labels competed with the equipment they were labelling.

         Dillon, 2026-08-06: get rid of it, and put the name in the
         move/rotate/trash cluster instead. Which is the right place: that cluster
         only exists for the machine you are currently holding, so the name appears
         exactly when it is useful and never when it is not.

         The numbered badge stays, but only for the selected or hovered machine —
         see .gs-badge in the stylesheet. It is the part that always fits at any
         room scale, and it is what ties a shape on the plan to a row in the
         list. */

      /* ── THE LANDING MARK ──────────────────────────────────────────────────
         A red outline traced round whatever just arrived, for one render only.
         Emitted only when FRESH matches, so it cannot re-fire when the plan is
         re-rendered for an unrelated reason.

         It earns its place because addProduct chooses the position, not the
         shopper: a machine is dropped wherever the free-space search found room,
         or parked outside the room entirely, and on a big plan that is a few
         millimetres of drawing. This is the answer to "where did it go".

         Stroke width is in INCHES because the SVG is scaled in inches — a fixed
         pixel width would be invisible on a 5,000 sq ft plan. With reduced motion
         it does not fade, it simply stays until the next render, which is more
         information rather than less. */
      if (FRESH === it) {
        g.push('<rect class="gs-landring" x="0" y="0" width="' + eff.w + '" height="' + eff.d +
               '" rx="2" stroke-width="' + Math.max(1.2, Math.min(eff.w, eff.d) * 0.035) + '"/>');
      }

      /* ── NO WARNING GLYPHS ON THE DRAWING ──────────────────────────────────
         A `!` badge for a clash and a `⤢` badge for something parked outside used
         to be drawn here. Both are gone, and so are the state tints on the shape
         and the red walkway halo.

         Dillon, 2026-08-07: "OUTSIDE GYM AREA and OVERLAPPING are both confusing
         to the user — if it took me a while to figure it out, that's a huge bounce
         hazard for a new user." He is right, and the deeper problem is what a flag
         ON the drawing implies: that the tool has judged the layout and found it
         wanting, mid-sentence, while the shopper is still arranging. It reads as
         an error state in a document that is not finished being written.

         Everything the flags knew is still known — the four placement states are
         computed exactly as before, and nothing about the arithmetic changed. It
         is reported once, calmly, in the "Things to look out for" panel under the
         plan, and again in the printed plan. The shopper decides.

         DO NOT PUT A BADGE BACK HERE. If something needs saying, say it in the
         advice panel where there is room for a sentence. */

      /* The rotate handle used to be drawn HERE, in SVG, sized in inches. That
         meant its real size depended on the room: in a 5,000 sq ft plan it came
         out a couple of CSS pixels across and was untappable on a phone.

         Controls for the selected machine are now HTML, positioned over the
         plan and sized in CSS pixels, so rotate and remove are always a proper
         44px target no matter how big the room is. See positionHandles(). */

      g.push('</g>');
      svg.push(g.join(''));
    });

    /* ── THE BIG RED BANNER IS GONE ────────────────────────────────────────────
       It read "SOME PIECES WILL NOT FIT THIS ROOM" across the bottom of the
       drawing, in the accent red, permanently, whenever anything was outside the
       walls. Three things were wrong with it.

       It was an ERROR about a legitimate action — parking a machine outside the
       room while rearranging is normal, and being shouted at for it is what
       Dillon reported. It was the loudest thing on the canvas, which is meant to
       be the gym. And it said "Some" whenever the count came out at zero, which
       happened on every room with a door in the wall line: `hasSpill` was
       geometric and included room features, while the count only looked at
       equipment, so a perfectly valid room was warned about pieces that did not
       exist.

       Replaced by a small per-machine badge, drawn only on the pieces actually
       affected, plus one quiet line in the selected-item details and a count in
       the In Your Gym tab. Nothing shouts, and nothing is claimed about equipment
       that is where it should be. */
    /* And the small "Staging area — N items outside the gym" caption that
       replaced it is gone too, 2026-08-07. It was calmer than the banner, but it
       was still the drawing counting the shopper's mistakes back at them, in the
       one place they are trying to look at their gym. The count now lives in the
       advice panel under the plan, in a sentence that says parking things outside
       while you rearrange is a perfectly reasonable thing to do. */

    svg.push('</svg>');
    el.plan.innerHTML = svg.join('');

    /* Probe only the drawings this plan actually referenced. If one turns out to
       be missing, that key is marked dead and we redraw once so the vector model
       takes its place — no blank machine, and no 7MB of unused art. */
    if (window.CTXGymArt && ART_USED.length) {
      var used = ART_USED.slice();
      ART_USED.length = 0;
      window.CTXGymArt.probeUsed(used, function () {
        /* NEVER RE-RENDER UNDER A LIVE DRAG. This callback fires from a network
           result — a never-probed art URL that failed to load — and a machine
           freshly dropped from the shelf is exactly the case that introduces a
           never-probed key. Rebuilding the plan here would destroy the node
           startDrag is writing transforms to, mid-gesture. Deferred to the end
           of the drag instead. */
        /* Never re-render under a live drag: this fires from a network result
           (an art URL that failed to load) and would destroy the node the drag
           is writing transforms to. Deferred to the end of the gesture. */
        if (DRAG_LIVE) { RERENDER_PENDING = true; return; }
        render();
      });
    } else {
      ART_USED.length = 0;
    }
    bindPlan();
    /* Zoom AND scroll are re-applied rather than reset. Replacing the plan's
       innerHTML resets the scrolling viewport to the top-left, so without this
       every add, drag and rotate snapped a zoomed-in shopper back to the corner
       of the room — which is the "trapped in a corner" complaint from the other
       direction. Magnification and pan are viewport state and survive edits. */
    applyZoom();
    if (el.viewport) {
      el.viewport.scrollLeft = keepScrollX;
      el.viewport.scrollTop = keepScrollY;
    }
    renderTray();
    renderMoney();

    /* SPENT. FRESH marks one arrival for one render — renderTray above is its
       last reader. Clearing it here is what stops the landing mark re-firing on
       every unrelated re-render: flip the floor finish twice and nothing
       re-announces itself. */
    FRESH = null;
  }

  /* ══ CONSTRAINT RESOLUTION ═══════════════════════════════════════════════
     One staged-edit flow, two constraints.

     Shrinking a room used to apply immediately and let the packer do what it
     could, which on a serious overflow produced a pile of machines stacked on
     each other — technically honest, completely unusable. Lowering the budget
     did nothing at all, so a build could sit invisibly over.

     Both now work the same way: the change is PROPOSED, validated against the
     real build, and if it does not hold, the current state stays exactly as it
     is while the shopper decides what to take out. Nothing is mutated until
     Apply. Cancel restores the proposal's starting point precisely, including
     anything removed inside the panel.

     `RESOLVE` is the whole staged transaction. Its existence is also the guard:
     while it is open, the underlying build is not the live one.  */
  var RESOLVE = null;

  /* Doors and columns the equipment must stay clear of, in the shape fitReport
     expects. Computed here rather than in the geometry file because features
     are a UI concern. */
  function keepouts() {
    return FEATURES.map(featureKeepout).filter(Boolean);
  }

  /* The proposal a room change makes. Applies straight away when the current
     build still fits; otherwise opens the panel and leaves the room alone. */
  function proposeRoom(w, d, preset) {
    var room = { w: w, d: d };

    /* Growing a room, or moving to one at least as large in both directions,
       can never invalidate a layout. Skip the check — and skip the panel. */
    var grew = w >= STATE.w && d >= STATE.d;

    if (grew || S.couldFit(BUILD, room)) {
      applyRoom(w, d, preset);
      return;
    }

    openResolve({
      kind: 'room',
      proposedRoom: room,
      proposedPreset: preset,
      onCancel: function () { syncControls(); }
    });
  }

  function applyRoom(w, d, preset) {
    STATE.w = w; STATE.d = d;
    if (preset) STATE.preset = preset;
    /* The ceiling moves with the room type, so a budget carried over from a
       larger space is brought into the new range before anything reads it. */
    STATE.budget = S.clampBudget(STATE.budget, STATE.preset);
    syncControls();
    syncBudgetSlider();
    relayout();
    renderPrefSummary();
  }

  function openResolve(opts) {
    if (!el.resolve) return;

    RESOLVE = {
      kind: opts.kind,
      proposedRoom: opts.proposedRoom || { w: STATE.w, d: STATE.d },
      proposedPreset: opts.proposedPreset || STATE.preset,
      proposedBudget: opts.proposedBudget != null ? opts.proposedBudget : STATE.budget,
      onCancel: opts.onCancel || function () {},

      /* THE ROLLBACK SNAPSHOT.

         Shallow copies of each item, not references — `arrange` and the drag
         handler both write coordinates onto live objects, so holding references
         would mean the snapshot drifted along with the thing it is supposed to
         be able to undo. */
      snapshot: BUILD.map(function (it) {
        return { p: it.p, cls: it.cls, fp: it.fp, x: it.x, y: it.y, rot: it.rot || 0,
                 moved: it.moved, mx: it.mx, my: it.my };
      }),
      /* The staged build. Removals happen here and only here until Apply. */
      staged: BUILD.map(function (it) {
        return { p: it.p, cls: it.cls, fp: it.fp, x: it.x, y: it.y, rot: it.rot || 0,
                 moved: it.moved, mx: it.mx, my: it.my };
      })
    };

    lockStage(true);
    renderResolve();
  }

  function resolveValid() {
    if (!RESOLVE) return true;
    if (RESOLVE.kind === 'room') return S.couldFit(RESOLVE.staged, RESOLVE.proposedRoom);
    return stagedTotal() <= RESOLVE.proposedBudget;
  }

  function stagedTotal() {
    if (!RESOLVE) return 0;
    return RESOLVE.staged.reduce(function (a, it) { return a + priceOf(it.p); }, 0);
  }

  /* Missing prices are a real state in this catalogue and must not read as
     free — a build "within budget" because three machines priced at nothing is
     the worst possible way to fail. */
  function priceOf(p) {
    var n = Number(p && p.p);
    return isFinite(n) && n > 0 ? n : 0;
  }

  function hasUnpriced(items) {
    return items.some(function (it) { return !priceOf(it.p); });
  }

  function renderResolve() {
    if (!RESOLVE || !el.resolve) return;

    var isRoom = RESOLVE.kind === 'room';
    var valid = resolveValid();

    var head, metrics;

    if (isRoom) {
      var report = S.fitReport(RESOLVE.staged, RESOLVE.proposedRoom, keepouts());
      var stillOut = S.couldFit(RESOLVE.staged, RESOLVE.proposedRoom) ? 0 : report.bad.length;
      head = 'This gym is too full for the selected dimensions. Remove equipment until the remaining layout fits.';
      metrics =
        '<span><b>' + ft(RESOLVE.proposedRoom.w) + ' &times; ' + ft(RESOLVE.proposedRoom.d) + '</b> proposed</span>' +
        '<span><b>' + RESOLVE.staged.length + '</b> piece' + (RESOLVE.staged.length === 1 ? '' : 's') + ' remaining</span>' +
        (valid
          ? '<span class="is-good"><b>Everything fits</b></span>'
          /* Deliberately a count of conflicts, never a square-footage figure.
             The packer can say whether a set of machines fits a room; it cannot
             honestly say how many square feet must go. */
          : '<span class="is-bad"><b>' + (stillOut || 'Some') + '</b> still will not fit</span>');
    } else {
      var total = stagedTotal();
      var over = total - RESOLVE.proposedBudget;
      head = 'Your current gym is over this budget. Remove equipment until the build total is within budget.';
      metrics =
        '<span><b>' + money(RESOLVE.proposedBudget) + '</b> proposed budget</span>' +
        '<span><b>' + money(total) + '</b> build total</span>' +
        (valid
          ? '<span class="is-good"><b>Within budget</b></span>'
          : '<span class="is-bad"><b>' + money(over) + '</b> over</span>');
    }

    var rows = RESOLVE.staged.map(function (it, i) {
      var fp = it.fp;
      var unpriced = !priceOf(it.p);
      return '<li class="gs-rz__row">' +
        '<img src="' + it.p.i + '" alt="" width="46" height="46" loading="lazy">' +
        '<div class="gs-rz__body">' +
          '<span class="gs-rz__name">' + escapeHtml(cleanTitle(it.p.t)) + '</span>' +
          '<span class="gs-rz__meta">' +
            escapeHtml(S.CLASSES[it.cls] ? S.CLASSES[it.cls].label : it.cls) +
            (it.p.cn === 'reman' ? ' · Remanufactured' : ' · New') +
            (fp && fp.w ? ' · ' + Math.round(fp.w) + '&quot; × ' + Math.round(fp.d) + '&quot;' : '') +
            (fp && fp.est ? ' · size estimated' : '') +
          '</span>' +
        '</div>' +
        '<span class="gs-rz__price' + (unpriced ? ' is-unpriced' : '') + '">' +
          (unpriced ? 'Price on request' : money(it.p.p)) + '</span>' +
        '<button type="button" class="gs-rz__x" data-gs-rz-remove="' + i + '" ' +
          'aria-label="Remove ' + escapeHtml(cleanTitle(it.p.t)) + ' from this gym">&times;</button>' +
      '</li>';
    }).join('');

    el.resolve.innerHTML =
      '<div class="gs-rz__card" role="dialog" aria-modal="true" aria-labelledby="gsRzTitle">' +
        '<div class="gs-rz__head">' +
          '<h2 class="gs-rz__title" id="gsRzTitle">' +
            (isRoom ? 'Too much equipment for that room' : 'Over budget') + '</h2>' +
          '<p class="gs-rz__text">' + head + '</p>' +
          '<div class="gs-rz__metrics">' + metrics + '</div>' +
          (!isRoom && hasUnpriced(RESOLVE.staged)
            ? '<p class="gs-rz__warn">Some items have no price yet and are counted as $0 here. ' +
              'Call us for those before relying on this total.</p>'
            : '') +
        '</div>' +
        '<ul class="gs-rz__list">' + (rows || '<li class="gs-rz__none">Nothing left in the gym.</li>') + '</ul>' +
        '<div class="gs-rz__acts">' +
          /* Dillon's ask: shrinking a room into a conflict should offer the
             clean-slate exit, not only one-by-one removal. Staged like every
             other change in this panel — Cancel still restores everything. */
          (RESOLVE.staged.length
            ? '<button type="button" class="gs-rz__clearall" data-gs-rz-clear-all>Clear all equipment</button>'
            : '') +
          '<button type="button" class="gs-rz__cancel" data-gs-rz-cancel>Cancel</button>' +
          '<button type="button" class="gs-rz__apply" data-gs-rz-apply' + (valid ? '' : ' disabled') + '>' +
            (isRoom ? 'Apply new size' : 'Apply new budget') + '</button>' +
        '</div>' +
      '</div>';

    el.resolve.hidden = false;
    var first = el.resolve.querySelector('[data-gs-rz-remove], [data-gs-rz-cancel]');
    if (first) first.focus();
  }

  function closeResolve() {
    RESOLVE = null;
    if (el.resolve) { el.resolve.hidden = true; el.resolve.innerHTML = ''; }
    lockStage(false);
  }

  function bindResolve() {
    if (!el.resolve) return;

    el.resolve.addEventListener('click', function (e) {
      if (!RESOLVE) return;

      var rm = e.target.closest('[data-gs-rz-remove]');
      if (rm) {
        /* Staged only. BUILD is untouched until Apply. */
        RESOLVE.staged.splice(parseInt(rm.dataset.gsRzRemove, 10), 1);
        renderResolve();
        return;
      }

      if (e.target.closest('[data-gs-rz-clear-all]')) {
        /* Empties the STAGED list only, so it behaves like clicking every ×
           at once: the metrics flip to "everything fits", Apply lights up,
           and Cancel still brings the whole gym back. */
        RESOLVE.staged = [];
        renderResolve();
        return;
      }

      /* A click on the dim backdrop — outside the card — is a Cancel. The
         overlay is the event target only when the card was not hit, so this
         cannot swallow clicks on the controls. Dillon's ask: clicking outside
         the error window returns you to the screen you were on, unchanged. */
      if (e.target === el.resolve) {
        var esc = el.resolve.querySelector('[data-gs-rz-cancel]');
        if (esc) esc.click();
        return;
      }

      if (e.target.closest('[data-gs-rz-cancel]')) {
        /* Restore the snapshot wholesale — anything removed inside the panel
           comes back, in its original position. */
        BUILD = RESOLVE.snapshot;
        var cb = RESOLVE.onCancel;
        closeResolve();
        cb();
        markOverlaps();
        render();
        saveState();
        return;
      }

      if (e.target.closest('[data-gs-rz-apply]')) {
        if (!resolveValid()) return;
        BUILD = RESOLVE.staged;
        SELECTED = null;

        if (RESOLVE.kind === 'room') {
          var r = RESOLVE.proposedRoom, pre = RESOLVE.proposedPreset;
          closeResolve();
          applyRoom(r.w, r.d, pre);
        } else {
          STATE.budget = RESOLVE.proposedBudget;
          closeResolve();
          syncBudgetSlider();
          relayout();
          renderPrefSummary();
        }
        return;
      }
    });

    /* Escape cancels, same as the button — a modal that traps you is worse than
       no modal. */
    document.addEventListener('keydown', function (e) {
      if (!RESOLVE || e.key !== 'Escape') return;
      var btn = el.resolve.querySelector('[data-gs-rz-cancel]');
      if (btn) btn.click();
    });
  }

  /* ── Budget slider ─────────────────────────────────────────────────────────
     The input carries a position, 0-1000. All the money lives in the curve, in
     ctx-gym-studio.js, where it can be tested outside a browser. Nothing here
     does arithmetic on dollars. */
  function sliderPos() {
    if (!el.budget) return 0.5;
    var n = parseInt(el.budget.value, 10);
    return isFinite(n) ? n / 1000 : 0.5;
  }

  /* Paint a number without committing to it — used while the handle is moving. */
  function showBudgetPreview(cents) {
    if (el.budgetL) el.budgetL.textContent = money(cents);
    if (el.budget) el.budget.setAttribute('aria-valuetext', money(cents));
    if (el.budgetCap) {
      var over = cents < subtotal();
      el.budgetCap.textContent = over
        ? 'Your gym is ' + money(subtotal() - cents) + ' over this'
        : '';
      el.budgetCap.classList.toggle('is-over', over);
    }
  }

  /* Put the handle where a given amount of money actually sits on this room
     type's curve, and re-label it. Called whenever the budget or the room type
     changes from anywhere other than the slider itself. */
  function syncBudgetSlider() {
    STATE.budget = S.clampBudget(STATE.budget, STATE.preset);
    if (el.budget) el.budget.value = Math.round(S.posForBudget(STATE.budget, STATE.preset) * 1000);
    showBudgetPreview(STATE.budget);
  }

  /* The one place a budget change is accepted.

     Raising it is always immediate — more money never invalidates anything.
     Lowering it below what is already on the floor hands over to the staged
     resolution panel: the old budget stays in force until the shopper has
     decided what to take out. */
  function commitBudget(cents) {
    var next = S.clampBudget(cents, STATE.preset);

    if (next >= subtotal() || !BUILD.length) {
      STATE.budget = next;
      syncBudgetSlider();
      renderMoney();
      renderPrefSummary();
      saveState();
      return;
    }

    /* Snap the handle and the label back to the budget that is STILL IN FORCE
       before opening the panel. Leaving them showing the proposed figure reads
       as though the change had already been accepted, when the whole point of
       this flow is that it has not been. The proposal is named inside the panel,
       where it belongs. */
    syncBudgetSlider();

    openResolve({
      kind: 'budget',
      proposedBudget: next,
      onCancel: function () { syncBudgetSlider(); }
    });
  }

  /* ── Zoom ──────────────────────────────────────────────────────────────────
     The drawing is scaled by setting the width of its holder; the SVG is
     width:100% of that and keeps its own aspect ratio. Nothing about zoom
     touches the model, so positions cannot drift as a side effect of looking
     closer — and toRoom() reads the live screen CTM, so dragging stays exact at
     any magnification.

     ZOOM_FIT is not 100%. 100% means "as wide as the viewport"; fit means "the
     whole room visible", which for a deep room is a smaller number. */
  var ZOOM_MIN = 0.25, ZOOM_MAX = 4, ZOOM_STEP = 0.25;
  var zoom = null;              /* null = follow fit until they choose otherwise */

  function fitZoom() {
    if (!el.viewport) return 1;
    var svg = el.plan.querySelector('svg');
    if (!svg) return 1;
    var vb = (svg.getAttribute('viewBox') || '').split(/\s+/);
    var vw = parseFloat(vb[2]), vh = parseFloat(vb[3]);
    if (!vw || !vh) return 1;

    var availW = el.viewport.clientWidth;
    var availH = el.viewport.clientHeight;
    if (!availW || !availH) return 1;

    /* At width:100% the drawing would be availW wide and availW*(vh/vw) tall.
       Shrink only if that overflows the viewport's height. */
    var naturalH = availW * (vh / vw);
    var z = naturalH > availH ? (availH / naturalH) : 1;
    return Math.max(ZOOM_MIN, Math.min(1, z));
  }

  function applyZoom() {
    if (!el.viewport || !el.plan) return;
    var z = zoom == null ? fitZoom() : zoom;
    el.plan.style.width = (z * 100) + '%';
    if (el.zoomVal) el.zoomVal.textContent = Math.round(z * 100) + '%';
    if (el.zoomOut) el.zoomOut.disabled = z <= ZOOM_MIN + 0.001;
    if (el.zoomIn) el.zoomIn.disabled = z >= ZOOM_MAX - 0.001;
    if (el.zoomFit) el.zoomFit.classList.toggle('is-on', zoom == null);

    /* THE GRAB HAND ONLY WHEN THERE IS SOMETHING TO GRAB.
       Dragging empty floor pans the plan — but only while the plan is bigger
       than its window. At fit-to-screen there is nothing to pan, so the hand was
       promising a drag that does nothing, over the whole canvas, which is what
       Dillon reported. Measured here rather than assumed from the zoom number,
       because a wide-and-short room can overflow one axis and not the other. */
    var canPan = el.viewport.scrollWidth > el.viewport.clientWidth + 1 ||
                 el.viewport.scrollHeight > el.viewport.clientHeight + 1;
    el.viewport.classList.toggle('is-pannable', canPan);

    positionHandles();
    positionDragHint();
  }

  function setZoom(z) {
    zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
    applyZoom();
  }

  function bindZoom() {
    /* Stepping lands on clean multiples of 25%. Adding the step to an odd fit
       value (98%) gave 123%, 148%, 173% — technically correct and it reads like
       a bug every time. */
    if (el.zoomIn) el.zoomIn.addEventListener('click', function () {
      var z = zoom == null ? fitZoom() : zoom;
      setZoom((Math.floor(z / ZOOM_STEP + 0.001) + 1) * ZOOM_STEP);
    });
    if (el.zoomOut) el.zoomOut.addEventListener('click', function () {
      var z = zoom == null ? fitZoom() : zoom;
      setZoom((Math.ceil(z / ZOOM_STEP - 0.001) - 1) * ZOOM_STEP);
    });
    if (el.zoomFit) el.zoomFit.addEventListener('click', function () {
      /* Fit resets BOTH. Zoom alone would leave someone who had panned away
         looking at a correctly-scaled view of the wrong part of the room — and
         this control is the guarantee that the gym can always be recovered. */
      zoom = null;
      applyZoom();
      if (el.viewport) { el.viewport.scrollLeft = 0; el.viewport.scrollTop = 0; }
    });
    onViewportResize();
    bindPan();
  }

  /* ── RESIZE HANDLING, AND WHY IT IS NOT A PLAIN LISTENER ────────────────────
     There were two unthrottled `resize` listeners on window — one running
     `applyZoom`, one running `positionHandles`. Both force synchronous layout:
     applyZoom writes the plan's width and then reads rects, positionHandles reads
     getBoundingClientRect and offsetWidth on a large SVG.

     On a phone that is a scroll-performance bug, not a resize handler. **Mobile
     browsers fire `resize` while you scroll**, because the URL bar collapses and
     expands and that changes the viewport height. So every scroll gesture over
     this page fired a storm of forced-layout passes on a drawing with hundreds of
     nodes — which is what "the results freeze while scrolling" actually was.

     Two fixes, and the second is the one that matters:

       1. Coalesce both handlers into one, throttled to a single animation frame,
          so a burst of events costs one layout instead of twenty.
       2. IGNORE EVENTS WHERE THE WIDTH DID NOT CHANGE. A URL bar collapsing
          changes only the height, and nothing here depends on viewport height —
          `fitZoom` reads the viewport's own clientHeight, which is unaffected by
          browser chrome appearing. A genuine rotation or window resize changes
          the width and still gets through.

     Height-only changes are not ignored entirely: the handle cluster is
     positioned from measured rects, so it is repositioned. That is one cheap
     read, not a re-layout of the plan. */
  function onViewportResize() {
    var lastW = window.innerWidth;
    var queued = false;

    function run() {
      queued = false;
      var w = window.innerWidth;
      if (w !== lastW) {
        lastW = w;
        applyZoom();          /* this calls positionHandles itself */
      } else {
        positionHandles();
        positionDragHint();
      }
    }

    window.addEventListener('resize', function () {
      if (queued) return;
      queued = true;
      if (window.requestAnimationFrame) window.requestAnimationFrame(run);
      else setTimeout(run, 16);
    });

    /* Rotating a phone reports the new width a frame or two late on iOS, so the
       first read after an orientation change can be the OLD width. */
    window.addEventListener('orientationchange', function () {
      setTimeout(function () { lastW = -1; run(); }, 250);
    });
  }

  /* ── Panning, and who owns a gesture ───────────────────────────────────────
     Zooming past fit used to pin the shopper to the top-left corner with no way
     to reach the rest of the floor. Panning is scroll on the viewport — not a
     transform — so it costs nothing, works with a trackpad and a scrollbar for
     free, and cannot possibly move a machine, because it never touches the
     model.

     The gesture rules, in the order they are decided:

       · pointer landed on a machine or a feature  → that element's drag wins
       · MOUSE on empty canvas                     → pan
       · ONE FINGER on empty canvas                → the page scrolls, untouched
       · TWO FINGERS                               → pan and pinch the plan

     The one-finger case is the important one. `touch-action: none` on the SVG
     meant a finger anywhere over the plan swallowed the scroll, so on a phone
     the builder was a wall you had to scroll around via a sliver at its edge.
     Touch action is now `pan-y`, which hands ordinary vertical scrolling back to
     the browser and keeps horizontal and multi-touch for us. */
  function bindPan() {
    if (!el.viewport) return;

    var svg = null;
    var panning = false;
    var sx = 0, sy = 0, sl = 0, st = 0;

    /* True when the pointer is over empty floor rather than something draggable.
       `.gs-hit` is the machine's own full-footprint target, so testing for these
       classes is the same question the drag handler asks. */
    function onEmptyCanvas(target) {
      return !(target.closest && (target.closest('.gs-item') || target.closest('.gs-feat')));
    }

    el.viewport.addEventListener('pointerdown', function (e) {
      /* Touch is handled by the two-finger path below; a single touch must fall
         through to the browser so the page scrolls. */
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      if (!onEmptyCanvas(e.target)) return;

      panning = true;
      sx = e.clientX; sy = e.clientY;
      sl = el.viewport.scrollLeft; st = el.viewport.scrollTop;
      el.viewport.classList.add('is-panning');
    });

    window.addEventListener('pointermove', function (e) {
      if (!panning) return;
      el.viewport.scrollLeft = sl - (e.clientX - sx);
      el.viewport.scrollTop = st - (e.clientY - sy);
    });

    function endPan() {
      if (!panning) return;
      panning = false;
      el.viewport.classList.remove('is-panning');
    }
    window.addEventListener('pointerup', endPan);
    window.addEventListener('pointercancel', endPan);

    /* ── Two-finger pan and pinch, anchored to the fingers ─────────────────
       Handled with raw touch events rather than pointer events because the
       browser only reports the two-finger relationship here. Nothing is
       preventDefault-ed until a second finger is down, so a one-finger scroll is
       never intercepted.

       THE OLD VERSION TREATED PAN AND ZOOM AS TWO SEPARATE ANSWERS. It set the
       scroll from how far the midpoint had moved, and set the zoom from how far
       the fingers had spread — independently. But changing the zoom changes the
       plan's width, which changes what every scroll offset MEANS, and nothing
       reconciled the two. Pinch in place and the plan inflated from its own
       top-left rather than from your fingers; the spot you were looking at slid
       away, usually off the right edge — Dillon's "no way to move the view to
       the right, hiding half the build".

       A pinch is ONE statement: "keep the part of the drawing between my fingers
       between my fingers." So both scroll and zoom are now derived together from
       that single invariant, every frame:

         1. At gesture start, record which point OF THE PLAN sits under the
            fingers' midpoint — as a fraction of the plan's size, so it survives
            the plan changing width.
         2. Each move: zoom = startZoom × (spread / startSpread), clamped.
         3. Resize the plan, then set the scroll so that recorded plan-point sits
            under the CURRENT midpoint.

       Pure pan is the k=1 case of the same formula, so fingers moving together
       track the plan 1:1 by construction — no separate pan code to disagree.

       The centering offset matters and is easy to forget: while the plan is
       narrower than the viewport it sits centered (margin auto), so its left
       edge is not at scroll-x zero. The anchor is measured from the plan's own
       rect and re-derived from the computed offset after resizing, which handles
       centered→overflowing transitions mid-gesture. */
    var G2 = null;

    function dist(t) {
      var dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }
    function mid(t) {
      return { x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 };
    }

    el.viewport.addEventListener('touchstart', function (e) {
      dismissDragHint();
      if (e.touches.length !== 2) return;
      e.preventDefault();

      /* A second finger means this was never a tap — a pending selection must
         not fire when the fingers lift. */
      cancelTap();

      var planEl = el.plan;
      var pr = planEl.getBoundingClientRect();
      if (!pr.width || !pr.height) return;
      var m = mid(e.touches);

      G2 = {
        d0: Math.max(20, dist(e.touches)),          /* guard: two touches reported at one spot */
        z0: zoom == null ? fitZoom() : zoom,
        /* The anchored plan point, as fractions — scale-independent. */
        fx: (m.x - pr.left) / pr.width,
        fy: (m.y - pr.top) / pr.height,
        planW0: pr.width,
        planH0: pr.height
      };
      dismissGestureHint();
    }, { passive: false });

    /* One frame per screen refresh, not one per touch event. touchmove can fire
       far faster than the display paints, and each pass here writes the plan
       width then reads rects back — a forced synchronous layout. Uncoalesced,
       that is the pinch stutter on cheaper phones (same family as the resize
       storm fixed earlier). preventDefault still happens per event, outside the
       frame, or the browser would start scrolling between our frames. */
    var pinchFrame = 0;

    /* ── THE STUCK-FINGER BUG, FOUND ────────────────────────────────────────
       Dillon: the plan and the page both still occasionally froze under a
       finger mid-scroll, "on the floor and off it", after this had already
       been fixed several times.

       The remaining gap: real pinches almost never end with both fingers
       lifting in the same frame. One always lifts first. The old code read
       `e.touches.length !== 2` as its entire test for "is this gesture still
       happening" — the instant the first finger left, it stopped doing
       anything with the second one, which was still down and still moving.

       That is not merely "nothing happens next" — a touch that had
       preventDefault called on it earlier in its life does not get handed
       back to the browser's native scrolling just because a later event
       stops calling preventDefault. Browsers commit a touch to whichever
       side is handling it near the start of the gesture and do not
       renegotiate mid-stream. So that second finger was neither controlled
       by us (we had stopped reading it) nor scrollable natively (the browser
       had already ceded it to us) — a genuinely stuck touch, wherever it
       happened to be when the first finger let go.

       The fix is to never let go first: when a pinch drops from two touches
       to one, the remaining finger keeps driving the SAME gesture as a plain
       1:1 pan, anchored on its position the moment the frame before, so there
       is no jump. Only when the last finger actually lifts does the gesture
       end. */
    el.viewport.addEventListener('touchmove', function (e) {
      if (!G2) return;
      e.preventDefault();

      /* Latest positions land on G2 every event; the pending frame reads them
         from there, so a coalesced frame always uses the newest fingers rather
         than the ones that happened to schedule it. */
      G2.touches = Array.prototype.map.call(e.touches, function (t) {
        return { clientX: t.clientX, clientY: t.clientY };
      });
      if (pinchFrame) return;

      pinchFrame = requestAnimationFrame(function () {
        pinchFrame = 0;
        if (!G2 || !G2.touches || !G2.touches.length) return;
        var touches = G2.touches;
        var vp = el.viewport;
        var vr = vp.getBoundingClientRect();

        if (touches.length >= 2) {
          var m = mid(touches);

          var zNew = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, G2.z0 * (dist(touches) / G2.d0)));
          /* k from the CLAMPED zoom, so hitting the zoom ceiling degrades into a
             plain pan instead of the anchor maths drifting off the real size. */
          var k = zNew / G2.z0;

          zoom = zNew;
          applyZoom();

          /* Where the plan's left edge sits in scroll space after the resize:
             centered while narrower than the viewport, zero once it overflows. */
          var planW = G2.planW0 * k;
          var planH = G2.planH0 * k;
          var offX = Math.max(0, (vp.clientWidth - planW) / 2);

          vp.scrollLeft = offX + G2.fx * planW - (m.x - vr.left);
          vp.scrollTop = G2.fy * planH - (m.y - vr.top);

          /* The handoff point for a finger that lifts on the very next frame:
             where the gesture's anchor actually was on screen, and the scroll
             position that put it there. */
          G2.lastScreen = m;
          G2.lastScrollLeft = vp.scrollLeft;
          G2.lastScrollTop = vp.scrollTop;
        } else {
          /* One finger left mid-gesture. Pan only, from the last real anchor —
             never from d0/z0, which describe a pinch this no longer is. */
          if (!G2.lastScreen) {
            G2.lastScreen = { x: touches[0].clientX, y: touches[0].clientY };
            G2.lastScrollLeft = vp.scrollLeft;
            G2.lastScrollTop = vp.scrollTop;
            return;
          }
          vp.scrollLeft = G2.lastScrollLeft - (touches[0].clientX - G2.lastScreen.x);
          vp.scrollTop = G2.lastScrollTop - (touches[0].clientY - G2.lastScreen.y);
        }
      });
    }, { passive: false });

    el.viewport.addEventListener('touchend', function (e) {
      if (e.touches.length === 0) G2 = null;
    });
    el.viewport.addEventListener('touchcancel', function () { G2 = null; });

    /* KEEP THE PAD GLUED TO THE MACHINE THROUGH ANY SCROLL.
       The drag pad and the handle cluster are siblings of the viewport, not
       children of it, so scrolling the viewport moves the machine underneath
       them without moving them. Pinch-zoom stays synced because it drives
       applyZoom() every frame, which repositions both — but the ONE-FINGER
       scroll this page deliberately hands back to the browser (see the rule
       above) fires no code of ours at all.

       That gap is "dragging does not work" on a real phone: scroll the plan
       with one finger to bring a machine into view, then try to drag it —
       the pad is still sitting at the pre-scroll position, the finger lands
       on the plan instead, and the browser reads the gesture as its own
       scroll or pinch. The machine never moves and nothing looks like it
       even tried to respond.

       rAF-throttled and passive, same as the resize handler below. */
    var scrollFrame = 0;
    el.viewport.addEventListener('scroll', function () {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(function () {
        scrollFrame = 0;
        positionHandles();
        positionDragHint();
      });
    }, { passive: true });
  }

  /* Shown once. Two-finger gestures are not discoverable, and the alternative —
     making one finger pan — is what broke page scrolling in the first place. The
     zoom buttons remain the route for anyone who cannot do the gesture. */
  var HINT_KEY = 'ctxGymStudio.gestureHint';

  function maybeShowGestureHint() {
    if (!el.hint) return;
    if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;
    try { if (localStorage.getItem(HINT_KEY)) return; } catch (e) {}
    el.hint.hidden = false;
  }

  function dismissGestureHint() {
    if (!el.hint || el.hint.hidden) return;
    el.hint.hidden = true;
    try { localStorage.setItem(HINT_KEY, '1'); } catch (e) {}
  }

  /* ── THE DRAG HINT — a wiggle, not a walkthrough ───────────────────────────
     Dillon, after a user could not move machines on a phone (fixed separately,
     see the viewport scroll listener above) and could not tell that the drag
     pad even existed: rather than a first-run tutorial with slides, "make
     this — like a little wiggle or 'try dragging me' — does the job without
     a popup."

     So: one small label, parked over whichever machine is already in the room
     when the plan first appears, that wiggles until you touch anything on the
     plan. No button, no dismiss to miss, no explanation of the two-finger
     gesture — that is .gs__hint's job, a few lines up. This is only about
     the one thing a new touch user could not tell: that the machine itself
     is the handle.

     Touch only (matchMedia mirrors maybeShowGestureHint above), shown once
     ever per browser, and gone the moment a real touch lands on the plan —
     which includes the touch that starts an actual drag, so succeeding at
     the gesture is itself what ends the hint. */
  var DRAG_HINT_KEY = 'ctxGymStudio.dragHintShown';
  var dragHintTimer = null;
  var dragHintOffered = false;

  /* Repositions the hint over whichever machine it is pinned to, same rect
     math as the drag pad. With no `force`, this only ever adjusts a hint
     that is ALREADY showing — called from the same scroll/resize/zoom paths
     that keep the drag pad glued in place, it must never resurrect a hint
     the shopper already dismissed. `force` is for the one moment that is
     allowed to turn it on: maybeShowDragHint below. */
  function positionDragHint(force) {
    if (!el.dragHint || !el.stageWrap || !el.plan) return;
    if (!force && el.dragHint.hidden) return;

    var node = el.plan.querySelector('.gs-item');
    if (!node) { el.dragHint.hidden = true; return; }
    var box = node.getBoundingClientRect();
    var wrap = el.stageWrap.getBoundingClientRect();
    if (!box.width && !box.height) { el.dragHint.hidden = true; return; }

    el.dragHint.style.left = Math.round(box.left - wrap.left) + 'px';
    el.dragHint.style.top = Math.round(box.top - wrap.top) + 'px';
    el.dragHint.style.width = Math.round(box.width) + 'px';
    el.dragHint.hidden = false;
  }

  function dismissDragHint() {
    if (dragHintTimer) { clearTimeout(dragHintTimer); dragHintTimer = null; }
    if (!el.dragHint || el.dragHint.hidden) return;
    el.dragHint.hidden = true;
    try { localStorage.setItem(DRAG_HINT_KEY, '1'); } catch (e) {}
  }

  /* Called from every point in the boot sequence that can leave the shopper
     looking at an interactive, unlocked room — a resumed session, a fresh
     visit that skipped the gate, and finishing the quiz on one that did not.
     Safe to call more than once: dragHintOffered makes every call after the
     first a no-op. */
  function maybeShowDragHint() {
    if (dragHintOffered) return;
    if (!el.dragHint) return;
    if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;
    try { if (localStorage.getItem(DRAG_HINT_KEY)) return; } catch (e) {}
    if (!BUILD.length) return;

    dragHintOffered = true;
    positionDragHint(true);
    if (!el.dragHint.hidden) dragHintTimer = setTimeout(dismissDragHint, 6000);
  }

  /* ── Controls for the selected machine ─────────────────────────────────────
     Sized in CSS pixels and parked just outside the machine's own box, so they
     are a real 44px target at any room size and never cover the thing they act
     on. Kept inside the plan wrapper so they scroll with it. */
  function positionHandles() {
    if (!el.handles || !el.stageWrap) return;

    /* Serves whichever thing is selected — a machine or a room feature. A door
       needs moving on a phone exactly as much as a leg press does, and without
       this it could only be dragged, which is the gesture we just gave back to
       the browser. */
    var node = null, name = '';

    if (SELECTED) {
      var idx = BUILD.indexOf(SELECTED);
      node = idx > -1 ? el.plan.querySelector('.gs-item[data-idx="' + idx + '"]') : null;
      name = cleanTitle(SELECTED.p.t);
    } else if (SELECTED_FEAT) {
      var fi = FEATURES.indexOf(SELECTED_FEAT);
      node = fi > -1 ? el.plan.querySelector('.gs-feat[data-fidx="' + fi + '"]') : null;
      var def = FEATDEF[SELECTED_FEAT.k];
      name = def ? def.label : 'this feature';
    }

    if (!node) {
      el.handles.hidden = true;
      positionDragPad(null);
      /* Cleared as well as hidden. A stale name left in the DOM would flash the
         previous machine's label for a frame the next time the cluster appears,
         because it is unhidden before the new name is written. */
      if (el.hName) el.hName.textContent = '';
      return;
    }

    var box = node.getBoundingClientRect();
    var wrap = el.stageWrap.getBoundingClientRect();
    if (!box.width && !box.height) { el.handles.hidden = true; positionDragPad(null); return; }

    /* The drag surface tracks the same node the buttons do. */
    positionDragPad(box, wrap);

    var where = SELECTED ? ' from gym' : ' from the room';
    if (el.hMoveLbl) el.hMoveLbl.textContent = 'Move ' + name;
    if (el.hRotateLbl) el.hRotateLbl.textContent = 'Rotate ' + name;
    if (el.hRemoveLbl) el.hRemoveLbl.textContent = 'Remove ' + name + where;
    if (el.hMove) el.hMove.setAttribute('aria-label', 'Move ' + name);
    if (el.hRotate) el.hRotate.setAttribute('aria-label', 'Rotate ' + name + ' 90 degrees');
    if (el.hRemove) el.hRemove.setAttribute('aria-label', 'Remove ' + name + where);

    /* The visible name. `shortName` for a machine — it strips the brand and series
       prefixes every BUILT product repeats, so a 168px label says what the machine
       IS rather than three words of "BUILT Strength Foundation". Features use their
       own label, which is already short. */
    if (el.hName) {
      el.hName.textContent = SELECTED ? shortName(SELECTED) : name;
    }

    el.handles.hidden = false;

    /* Measure AFTER unhiding — a hidden element has no size, and positioning
       against zero put the cluster in the top-left corner every time. */
    var hw = el.handles.offsetWidth || 96;
    var hh = el.handles.offsetHeight || 44;

    /* Above the machine by preference, below it when there is no room above,
       and always kept inside the drawing rather than hanging off the edge. */
    var left = box.left - wrap.left + (box.width / 2) - (hw / 2);
    var top  = box.top - wrap.top - hh - 8;
    if (top < 0) top = box.bottom - wrap.top + 8;

    left = Math.max(4, Math.min(wrap.width - hw - 4, left));
    top  = Math.max(4, Math.min(wrap.height - hh - 4, top));

    el.handles.style.left = Math.round(left) + 'px';
    el.handles.style.top = Math.round(top) + 'px';
  }

  /* Lay the transparent drag surface exactly over the selected thing, or hide
     it. Sized with a small outward margin so a fingertip landing just off the
     edge of a narrow machine still grabs it — 44px minimum in both directions,
     because a plate tree drawn at 6px across is otherwise ungrabbable. */
  function positionDragPad(box, wrap) {
    var pad = el.dragPad;
    if (!pad) return;
    if (!box) { pad.hidden = true; return; }

    var w = Math.max(44, box.width);
    var h = Math.max(44, box.height);
    var left = box.left - wrap.left - (w - box.width) / 2;
    var top = box.top - wrap.top - (h - box.height) / 2;

    pad.style.left = Math.round(left) + 'px';
    pad.style.top = Math.round(top) + 'px';
    pad.style.width = Math.round(w) + 'px';
    pad.style.height = Math.round(h) + 'px';
    pad.hidden = false;
  }

  /* ── DRAGGING THE SELECTED THING, BY TOUCHING IT ───────────────────────────
     Dillon: "when you click to select an item, calling the three buttons, you
     should stay engaged on this item and be able to drag and drop the item
     within the gym builder. Currently you have to use the 4-way arrow button."

     Correct, and the four-way button existed only because `touch-action` is
     ignored on SVG children — a press on the machine itself could not be
     claimed from the browser's page scroll. The pad is an HTML element, which
     does honour touch-action, so it can.

     The four-way button STAYS. It is the keyboard and screen-reader path, it is
     what a shopper who has zoomed out to a 5,000 sq ft plan can still hit, and
     removing a working control because a better one now exists beside it is how
     you strand people. */
  function bindDragPad() {
    if (!el.dragPad) return;

    el.dragPad.addEventListener('pointerdown', function (e) {
      dismissDragHint();
      /* Two fingers is a pinch, not a drag — let the viewport's own gesture
         handlers have it. */
      if (e.isPrimary === false) return;

      if (SELECTED) {
        var idx = BUILD.indexOf(SELECTED);
        var node = idx > -1 ? el.plan.querySelector('.gs-item[data-idx="' + idx + '"]') : null;
        if (!node) return;
        e.preventDefault();
        startDrag(e, idx, node);
        return;
      }
      if (SELECTED_FEAT) {
        var fi = FEATURES.indexOf(SELECTED_FEAT);
        var fnode = fi > -1 ? el.plan.querySelector('.gs-feat[data-fidx="' + fi + '"]') : null;
        if (!fnode) return;
        e.preventDefault();
        startFeatureDrag(e, fi, fnode);
      }
    });
  }

  /* ── PRESS AND HOLD: REMOVED ─────────────────────────────────────────────
     A 400ms hold used to select and pick up an unselected machine in one
     gesture. Dillon, after trying it on a real phone: "hold and drag seems
     like something that isn't going to work out, which is fine."

     He is right, and the reason is structural rather than a tuning problem.
     The gesture starts on the SVG, whose touch-action hands vertical movement
     to the page; a hold that then claims the finger is always racing the
     browser's own scroll, and on a list-shaped page a 400ms press with no
     visible affordance is indistinguishable from a slow scroll.

     The replacement is simpler and it is what he asked for: TAP to select,
     then drag either the four-way handle OR the machine itself. Both are
     immediate, both are visible, and the machine-as-handle is the .gs__dragpad
     below. TOUCH_DRAG_LIVE stays — the pad still needs it to stop the browser
     turning the drag into a page scroll. */


  /* ── Remove ────────────────────────────────────────────────────────────────
     THE UNDO TOAST IS GONE, ON PURPOSE. Dillon, 2026-08-06.

     It sat over the bottom of the plan for five seconds after every deletion,
     which on a phone is the part of the screen the plan is actually in, and while
     it was up it covered the controls you needed to carry on tidying. Removing
     one machine out of forty is not a decision that needs a safety net; it is one
     tap to add it back from a list that is already open beside the floor.

     Nothing is stashed any more either. A deferred restore that outlives the
     toast is a way for a deleted machine to reappear later, and there is no
     honest moment for that to happen.

     BULK clears keep their confirmation — see confirmClearGym. That is the
     destructive action worth stopping someone on. */
  function removeItem(idx) {
    var it = BUILD[idx];
    if (!it) return;

    BUILD.splice(idx, 1);
    if (SELECTED === it) SELECTED = null;
    MACHINES_REMOVED++;
    LAST_ACTION = 'Removed ' + cleanTitle(it.p.t);
    track('remove');

    packKeepingManual();
    markOverlaps();
    render();
    saveState();
    /* No "X removed." line. The row vanishing from the list, the count dropping
       and the total falling all say it already — see the note on setStatus. */
  }

  /* ── Clear Gym ─────────────────────────────────────────────────────────────
     The one deliberately destructive control. Needed for real: someone who
     rushes the quiz, or arrives from a product page, lands on a furnished floor
     they did not choose and had no obvious way to empty.

     Scope is stated in the dialog rather than implied, because the whole reason
     to hesitate here is not knowing what else goes with it. Room size, budget,
     goal, floor finish, zoom and room features are all KEPT — only the equipment
     goes. And the Shopify cart is untouched, which has to be said out loud since
     the builder has an Add To Cart button three inches away. */
  function clearGym() {
    BUILD = [];
    SELECTED = null;
    /* FEATURES is deliberately untouched. Doors, columns and stairs are the room,
       not the gym — clearing the equipment must not delete the constraints
       someone measured out and placed in order to design around them. */
    markOverlaps();
    render();
    saveState();
    /* The confirmation dialog already said the room size and preferences would be
       kept, and the empty floor says the rest. Repeating it afterwards is the
       narration this line is being freed from. */
  }

  function confirmClearGym(returnFocusTo) {
    if (!BUILD.length) return;
    if (!el.qty) { clearGym(); return; }

    el.qty.innerHTML =
      '<div class="gs-qty__card" role="dialog" aria-modal="true" aria-labelledby="gsClearTitle">' +
        '<h2 class="gs-qty__title" id="gsClearTitle">Remove all equipment from this gym?</h2>' +
        '<p class="gs-qty__text">Your room dimensions and preferences will be kept. ' +
          'Anything already in your shopping cart is not affected.</p>' +
        '<p class="gs-qty__which">' + BUILD.length + ' item' + (BUILD.length === 1 ? '' : 's') +
          ' &middot; ' + money(subtotal()) + '</p>' +
        '<div class="gs-qty__acts">' +
          '<button type="button" class="gs-qty__no" data-gs-clear-cancel>Cancel</button>' +
          '<button type="button" class="gs-qty__yes gs-qty__yes--danger" data-gs-clear-yes>Clear Gym</button>' +
        '</div>' +
      '</div>';
    el.qty.hidden = false;

    var no = el.qty.querySelector('[data-gs-clear-cancel]');
    var yes = el.qty.querySelector('[data-gs-clear-yes]');
    if (no) no.focus();

    function close() {
      el.qty.hidden = true;
      el.qty.innerHTML = '';
      document.removeEventListener('keydown', onKey);
      if (returnFocusTo && returnFocusTo.focus && document.contains(returnFocusTo)) returnFocusTo.focus();
    }
    function onKey(e) { if (e.key === 'Escape') close(); }

    document.addEventListener('keydown', onKey);
    if (no) no.addEventListener('click', close);
    if (yes) yes.addEventListener('click', function () { close(); clearGym(); });
  }

  /* Hidden rather than disabled when the floor is empty: a permanently greyed
     destructive button is clutter on a phone, and there is nothing to explain. */
  function syncClearGym() {
    var b = root.querySelector('[data-gs-clear-floor]');
    if (b) b.hidden = !BUILD.length;
  }

  /* Centre-preserving 90° turn for a room feature, same as the equipment — a
     door that jumps along the wall when you rotate it is just as disorienting as
     a treadmill that does. Pulled out of the feature bar's click handler so the
     bar and the on-plan handle share one implementation. */
  function rotateFeature(f) {
    if (!f) return;
    var rot0 = ((f.rot || 0) % 360 + 360) % 360;
    var turned0 = (rot0 === 90 || rot0 === 270);
    var w0 = turned0 ? f.d : f.w;
    var d0 = turned0 ? f.w : f.d;
    var cx = f.x + w0 / 2, cy = f.y + d0 / 2;

    f.rot = (rot0 + 90) % 360;
    var turned1 = (f.rot === 90 || f.rot === 270);
    var w1 = turned1 ? f.d : f.w;
    var d1 = turned1 ? f.w : f.d;
    f.x = cx - w1 / 2;
    f.y = cy - d1 / 2;

    markOverlaps();
    render();
    saveState();
  }

  function bindHandles() {
    /* The move handle drives the SAME drag as a mouse press on the machine —
       `startDrag` tracks a delta rather than a grab point, so it does not care
       that the pointer is over a button 8px above the machine rather than over
       the machine itself. */
    if (el.hMove) {
      el.hMove.addEventListener('pointerdown', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (SELECTED) {
          var idx = BUILD.indexOf(SELECTED);
          var node = idx > -1 ? el.plan.querySelector('.gs-item[data-idx="' + idx + '"]') : null;
          if (node) startDrag(e, idx, node);
        } else if (SELECTED_FEAT) {
          var fi = FEATURES.indexOf(SELECTED_FEAT);
          var fnode = fi > -1 ? el.plan.querySelector('.gs-feat[data-fidx="' + fi + '"]') : null;
          if (fnode) startFeatureDrag(e, fi, fnode);
        }
      });
    }

    if (el.hRotate) {
      el.hRotate.addEventListener('click', function () {
        if (SELECTED) {
          var i = BUILD.indexOf(SELECTED);
          if (i > -1) rotateItem(i);
        } else if (SELECTED_FEAT) {
          rotateFeature(SELECTED_FEAT);
        }
      });
    }
    if (el.hRemove) {
      el.hRemove.addEventListener('click', function () {
        if (SELECTED) {
          var i = BUILD.indexOf(SELECTED);
          if (i > -1) removeItem(i);
        } else if (SELECTED_FEAT) {
          var fi = FEATURES.indexOf(SELECTED_FEAT);
          if (fi > -1) FEATURES.splice(fi, 1);
          SELECTED_FEAT = null;
          markOverlaps(); render(); saveState();
        }
      });
    }
    /* Re-measured on resize only. Both rects are taken from the same viewport,
       so scrolling moves them together and needs no listener — one on every
       scroll event over a 40-machine plan is exactly the sort of thing that made
       this page freeze before.

       THE RESIZE LISTENER THAT USED TO BE HERE IS GONE. It was the second of two
       unthrottled resize handlers, and mobile browsers fire resize continuously
       while scrolling as the URL bar collapses. Both are now coalesced into the
       single rAF-throttled handler in onViewportResize(). */
  }

  /* ── Render: the tray ──────────────────────────────────────────────────── */

  function renderTray() {
    var rows = BUILD.map(function (it, idx) {
      var fp = it.fp;
      /* The per-row "Outside gym area" / "Overlapping" pills and the tinted rows
         went with the on-canvas badges, 2026-08-07. They were the same two
         confusing labels in a third place, and a red-tinted row in a shopping
         list reads as "this item is a problem" rather than "this item is
         somewhere unusual on the drawing". All of it is said once, in sentences,
         in the advice panel under the plan. */

      return '<li class="gs-row' + (SELECTED === it ? ' is-selected' : '') +
             (FRESH === it ? ' is-fresh' : '') + '" data-idx="' + idx + '">' +
        '<span class="gs-row__num">' + (idx + 1) + '</span>' +
        '<img src="' + it.p.i + '" alt="" width="52" height="52" loading="lazy">' +
        '<div class="gs-row__body">' +
          /* THE TITLE IS THE LINK. There used to be a separate "View" button in
             the actions row, which is a second control doing what the thing it
             sits beside should already do — people expect a product name to be
             clickable. NEW TAB, deliberately: a shopper mid-build who taps
             through to a product page and loses the room they just arranged does
             not come back and rebuild it. The build does survive in the URL, but
             a new tab means they never have to discover that. */
          '<a class="gs-row__name" href="' + pdpUrl(it.p) + '" target="_blank" rel="noopener">' +
            escapeHtml(cleanTitle(it.p.t)) +
          '</a>' +
          /* The condition rides as a badge on its own line rather than as a
             " · New" tail on the end of the dimensions, which is how it reads in
             the search list too. Half this catalogue is remanufactured; it is not
             a footnote. */
          '<span class="gs-row__meta">' + S.CLASSES[it.cls].label +
            ' · ' + Math.round(fp.w) + '&quot; × ' + Math.round(fp.d) + '&quot;' +
            (fp.est ? ' <em class="gs-est-tag">size estimated</em>' : '') +
          '</span>' +
          condBadge(it.p) +
        '</div>' +
        '<div class="gs-row__right">' +
          '<span class="gs-row__price">' + money(it.p.p) + '</span>' +
          /* Two actions, both of which change the BUILD. Rotate has gone from
             here: it is a positioning tweak, it belongs on the plan beside the
             thing being turned, and it still lives in the on-plan control cluster
             and on the R key. Four small glyph buttons per row was most of the
             visual noise in this panel. View has gone too — the product title
             above is the link now. */
          '<span class="gs-row__acts">' +
            '<button type="button" class="gs-mini" data-gs-swap-btn="' + idx + '">Swap</button>' +
            '<button type="button" class="gs-mini gs-mini--x" data-gs-remove="' + idx + '" aria-label="Remove">&times;</button>' +
          '</span>' +
        '</div>' +
      '</li>';
    });

    /* An empty floor needs a way back. Clearing the gym is now a deliberate
       choice, and the shopper who makes it should not be left with a blank room
       and no obvious next move. */
    el.tray.innerHTML = rows.join('') ||
      '<li class="gs-empty">' +
        '<span>Nothing placed yet.</span>' +
        '<button type="button" class="gs__ghost gs-empty__cta" data-gs-suggest>Suggest a gym for me</button>' +
      '</li>';

    var usedSqft = BUILD.reduce(function (a, it) { return a + (it.fp.w * it.fp.d) / 144; }, 0);
    var area = S.roomArea({ w: STATE.w, d: STATE.d });
    el.count.textContent = BUILD.length + (BUILD.length === 1 ? ' machine' : ' machines');
    el.area.textContent = Math.round(usedSqft) + ' of ' + Math.round(area) + ' sq ft used';
    if (el.fill) el.fill.style.width = Math.min(100, Math.round(usedSqft / area * 100)) + '%';

    if (el.tabCount) el.tabCount.textContent = BUILD.length;

    renderCoverage();
    renderGuard();
    renderAdvice();
    syncRoomCount();
    renderPrefSummary();
    renderSelInfo();
    syncClearGym();
    syncCartSyncBtn();
  }

  /* ── Canvas selection ⇄ In Your Gym ────────────────────────────────────────
     Selecting a machine on the plan should show you what it is and what it
     costs, and on a floor with forty items the matching row is rarely on screen.

     Matched by OBJECT IDENTITY, not product id: five identical benches are five
     separate rows and the highlight has to land on the one that was tapped.
     `SELECTED` already holds the item object, so the index in BUILD is the
     instance key and duplicates cannot be confused.

     Scrolls the PANEL, never the page — `scrollIntoView` on a row inside a
     scrolling column will happily scroll the document as well, which on a phone
     throws the plan off screen the moment you tap a machine. Setting scrollTop
     on the scroller itself cannot do that. */
  function panelScroller() {
    if (!el.tray) return null;
    var n = el.tray.parentNode;
    while (n && n !== document.body) {
      var oy = getComputedStyle(n).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 2) return n;
      n = n.parentNode;
    }
    return null;
  }

  /* Guards the reverse direction. Without it, revealing a row scrolls the panel,
     a scroll handler re-renders, the re-render reveals again — the "jumpy menu"
     symptom in its purest form. */
  var REVEALING = false;

  function revealInPanel(it) {
    if (!it || REVEALING) return;
    var idx = BUILD.indexOf(it);
    if (idx < 0) return;

    /* The row only exists if the In Your Gym pane is the visible one. Switching
       panes on selection would yank the catalogue out from under someone who is
       mid-browse, so this reveals when the panel is already showing and does
       nothing when it is not. */
    var row = el.tray && el.tray.querySelector('.gs-row[data-idx="' + idx + '"]');
    if (!row || !row.offsetParent) return;

    var box = panelScroller();
    if (!box) return;

    var rTop = row.offsetTop;
    var rBot = rTop + row.offsetHeight;
    var vTop = box.scrollTop;
    var vBot = vTop + box.clientHeight;

    /* ONLY when it is actually out of view. Scrolling every time is what makes a
       list feel possessed. */
    if (rTop >= vTop && rBot <= vBot) return;

    var target = rTop < vTop
      ? Math.max(0, rTop - 12)
      : Math.max(0, rBot - box.clientHeight + 12);

    REVEALING = true;
    var smooth = !window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (smooth && box.scrollTo) {
      box.scrollTo({ top: target, behavior: 'smooth' });
    } else {
      box.scrollTop = target;
    }
    setTimeout(function () { REVEALING = false; }, smooth ? 400 : 0);
  }

  /* ── The equipment drawer: catalog vs build ────────────────────────────────
     Two panes, one visible at a time, each labelled for what it actually is. */
  function showPane(which) {
    if (!el.tabs || !el.tabs.length) return;
    Array.prototype.forEach.call(el.tabs, function (t) {
      var on = t.dataset.gsTab === which;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      var pane = root.querySelector('#' + t.getAttribute('aria-controls'));
      if (pane) pane.hidden = !on;
    });
    if (which === 'room') renderShelf();
    if (which === 'browse') {
      renderSuggestions();
      /* Desktop-only. On a phone this focus() ran inside the tap handler, so
         every visit to the tab popped the keyboard and iOS lurched the page to
         keep the input in view — the "search grabs my phone" report. A phone
         shopper who wants to type will tap the box; a mouse user still gets the
         caret for free. */
      /* preventScroll, and it matters: without it the browser scrolls the input
         into view, which on a page where the plan is framed above the drawer
         drags the whole floor out of shot the instant you change tabs. The
         caret is a convenience; moving the shopper's viewport is not. */
      if (el.search && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        try { el.search.focus({ preventScroll: true }); }
        catch (e) { el.search.focus(); }
      }
    }
  }

  function bindTabs() {
    if (!el.tabs) return;
    Array.prototype.forEach.call(el.tabs, function (t) {
      t.addEventListener('click', function () { showPane(t.dataset.gsTab); });
    });
  }

  /* One row shape for every catalog list — suggestions, browse-all, search — so
     a product looks the same wherever it is offered.

     THE WHOLE ROW USED TO BE ONE BUTTON. That is why clicking anywhere on a card
     — the image, the name, the empty space beside the price — added a machine to
     the floor, and why there was no way to open the product page at all. A row
     is not a single action; it is a link to the product and a button that adds
     it, and those are now two separate elements. Nesting a link inside a button
     is invalid anyway, and browsers disagree about which one fires. */
  /* `sug` is optional and carries the suggestion metadata — why it is being
     offered, and whether it can actually be placed right now. Present only for
     the suggestions pane; search and Browse All pass nothing and render as
     before. */
  function catalogRow(p, cls, sug) {
    var n = countOf(p.v);

    /* AN HONEST ADD BUTTON. Offering "Add to gym" for something the placement
       engine has already established has nowhere to go is how the builder lost
       trust: the tap appeared to work and then produced an error. The row still
       offers the product — it just says what will happen. */
    var blocked = sug && sug.fits === false;

    /* ── SENTENCES ON DESKTOP, ICONS ON A PHONE ────────────────────────────────
       Dillon: "there's still too much info on mobile, it's tough to read. Remove
       the text about things fitting or price and stick with small icons that
       make sense."

       Both are emitted, and CSS picks. A 375px row cannot carry "Adds depth to
       conditioning training · tighter than the recommended walkway" AND "Over
       your remaining budget" AND a name AND a price — that is what was wrapping
       to five lines and setting a 431px minimum width on the whole column.

       The icons are the same three facts, at a glance, each with a real
       aria-label so nothing is lost to a screen reader. What they mean is
       spelled out once, as a key, in the help panel — which is why the key
       exists. Deliberately NOT emoji: a glyph in the tool's own type renders
       identically everywhere and cannot arrive as a colour picture. */
    /* ── SENTENCES ON DESKTOP, ONE MARK ON A PHONE ─────────────────────────
       There were three marks. Dillon: "the box and the $ icon — remove those,
       they're confusing." Both are gone.

       He is right about why: a □ meaning "only fits if you move something" and
       a $ meaning "over your remaining budget" are each a whole sentence
       compressed into a shape nobody can guess, and neither stops the shopper
       doing anything. The budget is already on screen and the over-budget row
       is still perfectly addable.

       What survives is the ONE mark that describes something the shopper cannot
       see for themselves — that the machine will sit closer to its neighbour
       than we would normally leave. Explained in the help key. */
    var note = '', chips = '';
    if (sug) {
      if (blocked) {
        note = '<span class="gs-res__why gs-res__why--blocked">Fits if you remove or rearrange equipment</span>';
      } else if (sug.reason) {
        note = '<span class="gs-res__why">' + escapeHtml(sug.reason) +
               (sug.tight ? ' &middot; tighter than the recommended walkway' : '') + '</span>';
        if (sug.tight) {
          chips += '<span class="gs-res__chip gs-res__chip--tight" role="img" ' +
                   'aria-label="Fits, but a tighter squeeze than we would usually leave">&#8596;</span>';
        }
      }
      if (sug.overBudget) {
        note += '<span class="gs-res__why gs-res__why--over">Over your remaining budget</span>';
      }
    }
    if (chips) chips = '<span class="gs-res__chips">' + chips + '</span>';

    var title = cleanTitle(p.t);

    /* ── ONE COMPACT ROW ───────────────────────────────────────────────────────
       Image, then title on line one, then condition and price on line two, then
       the action. The price used to sit in its own flex column between the link
       and the button, which on a phone left the product name about 78px and made
       every row wrap to four lines. It is now part of the second text line, where
       it reads as a property of the product rather than as a third column.

       The visible label is "Add" on mobile and "Add to gym" on desktop, done in
       CSS from one piece of markup — two different strings would mean two
       different accessible names for the same control. The accessible name always
       carries the full product name, because "Add" on its own is meaningless in a
       list of forty rows read aloud. */
    var action;
    if (n > 0) {
      /* Once one is in the gym the control becomes a stepper for THIS variant, so
         adjusting quantity no longer means finding the machine on the floor. */
      action =
        '<span class="gs-res__qty" role="group" aria-label="Quantity of ' + escapeHtml(title) + ' in your gym">' +
          '<button type="button" class="gs-res__step" data-gs-qty-down="' + escapeHtml(p.h) + '"' +
            ' aria-label="Remove one ' + escapeHtml(title) + ' from your gym">&minus;</button>' +
          '<span class="gs-res__n" aria-live="polite">' + n + '</span>' +
          '<button type="button" class="gs-res__step" data-gs-qty-up="' + escapeHtml(p.h) + '"' +
            ' aria-label="Add another ' + escapeHtml(title) + ' to your gym">+</button>' +
        '</span>';
    } else {
      action =
        '<button type="button" class="gs-res__add" data-gs-add-handle="' + escapeHtml(p.h) + '"' +
          ' aria-label="Add ' + escapeHtml(title) + ' to my gym">' +
          /* "my gym", not "gym" — it is the shopper's room being furnished, and
             the phrase matches the panel it lands in ("In Your Gym") and the cart
             button ("Add my gym to cart"). The short label is what shows below
             749px; the button has a 60px min-width there, so it stays "Add". */
          '<span class="gs-res__add__long">Add to my gym</span>' +
          '<span class="gs-res__add__short" aria-hidden="true">Add</span>' +
        '</button>';
    }

    return '<li class="gs-res' + (blocked ? ' is-blocked' : '') + '">' +
      /* The LINK covers the image and the text only — never the whole row. A row
         that is entirely a link means every tap on whitespace navigates away
         mid-build; a row that is entirely a button was the older bug, where
         tapping the image added a machine. */
      '<a class="gs-res__link" href="' + pdpUrl(p) + '" target="_blank" rel="noopener">' +
        /* Dimensions on the img AND an aspect-ratio box in CSS. Without reserved
           space every image that decoded shoved the list down under the reader's
           thumb — the "jumpy menu" complaint, most of it in one line. */
        '<img src="' + p.i + '" alt="" width="34" height="34" loading="lazy" decoding="async">' +
        '<span class="gs-res__body">' +
          '<span class="gs-res__name">' + escapeHtml(title) + '</span>' +
          '<span class="gs-res__meta">' +
            /* A BADGE, not a word trailing the price. As plain grey text it ran
               straight into the price ("New$4,295") and read as part of it —
               and condition is the single most important thing about a product
               in this catalogue, since half of it is remanufactured. Same two
               colours the condition badge uses everywhere else on the store:
               green for new, orange for remanufactured. */
            condBadge(p) +
            '<span class="gs-res__price">' + money(p.p) + '</span>' +
            chips +
          '</span>' +
          note +
        '</span>' +
      '</a>' +
      action +
    '</li>';
  }

  /* The condition chip used by both lists in the drawer, so a machine looks the
     same whether you are about to add it or have already added it. Mirrors
     snippets/ctx-condition-badge.liquid, which cannot be used here because these
     rows are built in JS from the catalogue feed rather than from Liquid.

     `cn` is resolved server-side by ctx-condition (metafield first, title as the
     fallback) — never from tags, which lie. The feed already excludes `used`, so
     there are only two cases to draw. */
  function condBadge(p) {
    var reman = p.cn === 'reman';
    return '<span class="gs-res__cond gs-res__cond--' + (reman ? 'reman' : 'new') + '">' +
             (reman ? 'Remanufactured' : 'New') +
           '</span>';
  }

  /* "More Treadmills", not "More Treadmill's" and not "More Cardio's".

     The class labels are already written as plurals where a plural is natural
     ("Power racks", "Adjustable benches"), so the correct behaviour is to trust
     the label rather than to pluralise it a second time. Only the handful that
     are genuinely singular get an ending, and the rule is spelled out rather
     than guessed. */
  function pluralLabel(label) {
    var s = String(label || '').trim();
    if (!s) return 'items';
    if (/s$/i.test(s)) return s;                    /* already plural */
    if (/(ch|sh|ss|x|z)$/i.test(s)) return s + 'es';
    if (/[^aeiou]y$/i.test(s)) return s.slice(0, -1) + 'ies';
    return s + 's';
  }

  /* Group a flat product list by class and cap each group, with an inline
     expander per group. Expansion is held in `expandedGroups` so it survives a
     re-render — a build change must not silently collapse a list someone opened. */
  var PER_GROUP = 5;
  var expandedGroups = {};

  /* On a phone these lists are PAGE CONTENT, not a dropdown — the mobile CSS
     deliberately removed the inner scroller (see the long note in the CSS) and
     it must not come back. So the budget lives in the content instead: measured
     live, a "bench" search rendered 1,769px of rows mid-page and shoved the gym
     list and totals off into the distance. Fewer rows per group and a smaller
     search cap keep the worst case to roughly a screenful; the per-group
     expanders still reach everything. */
  function perGroup() {
    return window.matchMedia('(pointer: coarse)').matches ? 3 : PER_GROUP;
  }
  function searchLimit() {
    return window.matchMedia('(pointer: coarse)').matches ? 24 : 60;
  }

  function groupedCatalogHtml(list, keyPrefix) {
    var groups = [];
    var byKey = {};

    list.forEach(function (row) {
      var label = S.CLASSES[row.cls] ? S.CLASSES[row.cls].label : row.cls;
      if (!byKey[label]) { byKey[label] = { label: label, rows: [] }; groups.push(byKey[label]); }
      byKey[label].rows.push(row);
    });

    return groups.map(function (g) {
      var id = keyPrefix + ':' + g.label;
      var open = !!expandedGroups[id];
      var shown = open ? g.rows : g.rows.slice(0, perGroup());
      var hiddenCount = g.rows.length - shown.length;

      return '<li class="gs-suggroup">' + escapeHtml(g.label) +
               ' <span class="gs-suggroup__n">' + g.rows.length + '</span></li>' +
             shown.map(function (r) { return catalogRow(r.p, r.cls); }).join('') +
             (hiddenCount > 0 || open
               ? '<li class="gs-suggmore">' +
                   '<button type="button" class="gs-more" data-gs-more="' + escapeHtml(id) + '">' +
                     (open ? 'Show fewer ' + escapeHtml(pluralLabel(g.label).toLowerCase())
                           : 'More ' + escapeHtml(pluralLabel(g.label)) + ' (' + hiddenCount + ')') +
                   '</button>' +
                 '</li>'
               : '');
    }).join('');
  }

  var shopAllOpen = false;
  var shopAllShown = 0;
  var SHOP_PAGE = 40;

  /* ══ THE ROOM SHELF ═══════════════════════════════════════════════════════
     One row per feature, grouped, each with a draggable artwork grip on the left
     and a plain Add button on the right.

     THE GRIP IS THE ARTWORK, at 48px, and that is the whole discoverability
     argument: a picture of the actual door with a move glyph on it reads as
     "hold this and move it" in a way a column of dots never does. It is also a
     real <button>, so a tap on it adds — the grip is never a dead control for
     anyone who does not drag.

     Reuses .gs-res wholesale so a feature row and a product row are visibly the
     same kind of object. The body is a <span>, not an <a>: a feature has no
     product page and must not pretend to. */
  var featFilter = '';

  function shelfRow(key, def) {
    var art = (window.CTX_FEAT_ART || {})[key] || '';
    var swings = !!(window.CTX_FEAT_PATH || {})[key];
    var label = def.label;

    return '<li class="gs-res gs-res--feat">' +
      /* The artwork, plain. It used to be a drag grip; the drag is gone (see the
         note where that machinery was removed) and the picture stays, because a
         top-down drawing of the actual door is how you recognise it. */
      (art ? '<span class="gs-res__art"><img src="' + art + '" alt="" width="44" height="44" ' +
             'loading="lazy" decoding="async"></span>' : '') +
      '<span class="gs-res__link gs-res__link--static">' +
        '<span class="gs-res__body">' +
          '<span class="gs-res__name">' + escapeHtml(label) + '</span>' +
          '<span class="gs-res__meta">' +
            '<span class="gs-res__dims">' + Math.round(def.w) + '&Prime; &times; ' + Math.round(def.d) + '&Prime;</span>' +
          '</span>' +
          (swings ? '<span class="gs-res__why">Depth includes the door swing</span>' : '') +
        '</span>' +
      '</span>' +
      '<button type="button" class="gs-res__add" data-gs-feat-add="' + escapeHtml(key) + '"' +
        ' aria-label="Add ' + escapeHtml(label) + ' to the plan">' +
        '<span class="gs-res__add__long">Add to plan</span>' +
        '<span class="gs-res__add__short" aria-hidden="true">Add</span>' +
      '</button>' +
    '</li>';
  }

  function renderShelf() {
    var listEl = root.querySelector('[data-gs-shelf]');
    if (!listEl) return;

    var q = String(featFilter || '').trim().toLowerCase();
    var placed = {};
    FEAT_GROUPS.forEach(function (grp) {
      grp.keys.forEach(function (k) { placed[k] = 1; });
    });
    /* Trailing catch-all, so a feature added to the model library still appears
       even if nobody remembers to list it above. */
    var leftovers = Object.keys(FEATDEF).filter(function (k) { return !placed[k]; });
    var groups = FEAT_GROUPS.concat(leftovers.length ? [{ label: 'Other', keys: leftovers }] : []);

    var html = groups.map(function (grp) {
      var keys = grp.keys.filter(function (k) {
        var def = FEATDEF[k];
        if (!def) return false;
        return !q || def.label.toLowerCase().indexOf(q) > -1;
      });
      if (!keys.length) return '';

      /* A filter that matched something must SHOW it — a hit hidden inside a
         collapsed group is a search that appears to have failed. */
      var id = 'feat:' + grp.label;
      var open = q ? true : !!expandedGroups[id];
      var shown = open ? keys : keys.slice(0, perGroup());
      var hidden = keys.length - shown.length;

      return '<li class="gs-suggroup">' + escapeHtml(grp.label) +
               ' <span class="gs-suggroup__n">' + keys.length + '</span></li>' +
             shown.map(function (k) { return shelfRow(k, FEATDEF[k]); }).join('') +
             ((hidden > 0 || (open && !q))
               ? '<li class="gs-suggmore">' +
                   '<button type="button" class="gs-more" data-gs-more="' + escapeHtml(id) + '">' +
                     (open ? 'Show fewer' : 'More (' + hidden + ')') +
                   '</button>' +
                 '</li>'
               : '');
    }).join('');

    listEl.innerHTML = html ||
      '<li class="gs-empty"><span>Nothing matches that. Try a shorter word &mdash; ' +
      '&ldquo;door&rdquo;, &ldquo;sink&rdquo;, &ldquo;locker&rdquo;.</span></li>';
  }

  /* The count on the Room tab. textContent only — no layout, no re-render. */
  function syncRoomCount() {
    var n = root.querySelector('[data-gs-room-count]');
    if (n) n.textContent = FEATURES.length;
  }

  /* What the builder would pick next, minus anything already on the floor.
     Gives the Browse pane something to show before a single key is typed —
     an empty search box is not a catalog. */
  function renderSuggestions() {
    if (!el.suggList) return;
    if (!CATALOG.length) { el.suggList.innerHTML = ''; return; }
    if (shopAllOpen) { renderShopAll(); return; }

    /* S.suggest, not S.recommend. `recommend` designs a gym for an EMPTY room;
       using it here meant the panel generated a whole gym from scratch and then
       subtracted what was on the floor, so it could not distinguish a gym with no
       leg machine from one with three, and it ran dry after six picks — which is
       why a two-machine build in a half-empty room was told "your gym already
       covers our suggestions". */
    var picks = S.suggest(CATALOG, {
      room: { w: STATE.w, d: STATE.d },
      have: BUILD,
      budget: STATE.budget,
      goal: STATE.goal,
      keepouts: keepouts(),
      limit: 8
    });

    if (!picks.length) {
      el.suggList.innerHTML = '<li class="gs-empty"><span>Nothing left to suggest &mdash; ' +
        'you have one of everything we would recommend. Search above, or shop the full catalogue.</span></li>';
      return;
    }

    el.suggList.innerHTML =
      '<li class="gs-sugghead">Suggested next for your gym</li>' +
      picks.map(function (s) { return catalogRow(s.p, s.cls, s); }).join('');
  }

  function renderShopAllList() {
    var list = shopAllList();
    el.suggList.innerHTML =
      '<li class="gs-sugghead">All equipment · ' + list.length + ' machines</li>' +
      groupedCatalogHtml(list, 'all');
  }

  /* The whole sellable catalogue, grouped the way the storefront groups it and
     paged so a 335-row list does not have to exist in the DOM all at once. */
  function shopAllList() {
    var list = CATALOG.filter(function (p) { return S.eligible(p); }).map(function (p) {
      return { p: p, cls: S.classify(p) };
    });
    list.sort(function (a, b) {
      var la = (S.CLASSES[a.cls] ? S.CLASSES[a.cls].label : a.cls);
      var lb = (S.CLASSES[b.cls] ? S.CLASSES[b.cls].label : b.cls);
      if (la !== lb) return la < lb ? -1 : 1;
      return (b.p.p || 0) - (a.p.p || 0);
    });
    return list;
  }

  /* Paging by a flat count is gone. The catalogue is now grouped by equipment
     type with five per group and its own expander per group, which is what
     someone scanning for "a treadmill" actually needs — a flat 40-at-a-time list
     buried the second type below the fold. */
  function renderShopAll() {
    if (!el.suggList) return;
    renderShopAllList();
  }

  function bindShopAll() {
    if (el.shopAll) {
      el.shopAll.addEventListener('click', function () {
        shopAllOpen = !shopAllOpen;
        el.shopAll.setAttribute('aria-pressed', shopAllOpen ? 'true' : 'false');
        el.shopAll.classList.toggle('is-on', shopAllOpen);
        if (el.shopAllLabel) {
          el.shopAllLabel.textContent = shopAllOpen ? 'Back to suggestions' : 'Browse all equipment';
        }
        renderSuggestions();
      });
    }

    /* Expanding a group must not disturb anything else — not the build, not the
       search term, not the scroll position, not the zoom. It only rewrites this
       one list, and the scroll offset is put back because replacing the list's
       innerHTML resets it. */
    if (el.suggList) {
      el.suggList.addEventListener('click', function (e) {
        var b = e.target.closest('[data-gs-more]');
        if (!b) return;
        var id = b.dataset.gsMore;
        /* Was: save scrollTop on `.gs__pane` only. That element is not the
           scroller in the stacked phone layout — the document is — so on a phone
           expanding a group still jumped to the top. keepScroll records every
           scrollable ancestor plus the page, so it does not matter which one the
           current layout happens to use. */
        keepScroll(el.suggList, function () {
          expandedGroups[id] = !expandedGroups[id];
          renderSuggestions();
        });
      });
    }

    if (el.results) {
      el.results.addEventListener('click', function (e) {
        var b = e.target.closest('[data-gs-more]');
        if (!b) return;
        expandedGroups[b.dataset.gsMore] = !expandedGroups[b.dataset.gsMore];
        renderResults(searchCatalog(el.search ? el.search.value : '', searchLimit()));
      });
    }
  }

  /* A plain-language read of the current answers, so "what is this built from?"
     never needs a dropdown to answer. */
  function renderPrefSummary() {
    if (!el.prefSummary) return;
    var goalLabel = {
      general: 'All-round', strength: 'Strength', muscle: 'Muscle',
      cardio: 'Conditioning', commercial: 'Commercial floor'
    }[STATE.goal] || STATE.goal;
    el.prefSummary.innerHTML =
      '<span>' + ft(STATE.w) + ' &times; ' + ft(STATE.d) + '</span>' +
      '<span>' + (STATE.budget ? money(STATE.budget) : 'No budget set') + '</span>' +
      '<span>' + escapeHtml(goalLabel) + '</span>';
  }

  /* ── Guard rails ───────────────────────────────────────────────────────────
     Dillon: people need to know WHY the builder stopped adding — that they have
     run out of floor, or run out of budget. Without it a build just quietly
     stops growing and reads as the tool being finished, when actually one slider
     is holding it back.

     The test is empirical rather than a rule of thumb: take a typical machine,
     ask the real packer whether it would fit, and ask the budget whether it
     could be paid for. Whichever says no is the binding constraint, and the
     message comes with the control that releases it. */
  var PROBE = { w: 48, d: 66 };

  /* MUST NOT TOUCH THE REAL BUILD.

     `arrange()` writes x/y onto the objects it is given, and `Array.concat`
     copies the array while sharing the objects inside it. The first version of
     this passed `BUILD.concat([probe])`, so simply asking "is there room for one
     more?" re-laid every machine back onto the auto-arranged grid — on every
     render, because the guard rail runs from renderMoney().

     The effect was that a dragged machine appeared to land correctly and then
     jumped back to its old position at the next interaction, which is exactly
     what Dillon reported. Shallow COPIES of each item are handed to the packer
     so it can scribble on them freely. */
  /* Asked directly of the free space now, rather than by re-packing a copy of
     the whole floor. Re-packing answered a subtly different question — "could a
     from-scratch shelf layout of this build plus one more fit?" — which said
     "your room is full" about rooms that were half empty, because the zone bands
     each cost a row break. This asks the only question the message claims to
     answer: is there anywhere a typical machine physically goes?

     Coarse step on purpose: this runs from renderMoney on every render. */
  /* MEMOISED ON THE GEOMETRY IT DEPENDS ON. render() runs on every selection
     tap, and this search probes up to ~20,000 candidate positions on a big
     commercial floor — measurable jank on a phone for a tap that moved
     nothing. The answer only changes when the room, the door swings, or the
     machines' places change, so the key is exactly those. */
  var spaceMemo = { key: '', val: true };

  function roomHasSpace() {
    var key = STATE.w + '|' + STATE.d + '|' + FEATURES.length + '|' +
      BUILD.map(function (it) {
        return it.p.v + ',' + Math.round(it.x) + ',' + Math.round(it.y) + ',' + (it.rot || 0);
      }).join(';');
    if (key === spaceMemo.key) return spaceMemo.val;

    var room = { w: STATE.w, d: STATE.d };
    var onFloor = BUILD.filter(function (it) { return it.fits !== false; });
    var probe = { cls: 'selector', fp: { w: PROBE.w, d: PROBE.d, est: true }, rot: 0, x: 0, y: 0 };
    spaceMemo.key = key;
    spaceMemo.val = !!S.findSpot(probe, onFloor, room, keepouts(), { step: 12 });
    return spaceMemo.val;
  }

  /* The catalogue never changes within a session, so the cheapest price is
     computed once and remembered — this was a full catalogue scan per render. */
  var cheapestMemo = -1;

  function cheapestAddable() {
    if (cheapestMemo >= 0) return cheapestMemo;
    var min = Infinity;
    for (var i = 0; i < CATALOG.length; i++) {
      var p = CATALOG[i];
      if (!S.eligible(p)) continue;
      if (p.p > 0 && p.p < min) min = p.p;
    }
    cheapestMemo = min === Infinity ? 0 : min;
    return cheapestMemo;
  }

  function renderGuard() {
    if (!el.guard) return;

    var spend = subtotal();
    var budgetLeft = STATE.budget ? (STATE.budget * 1.05 - spend) : Infinity;
    var space = roomHasSpace();
    var cheapest = cheapestAddable();

    var kind, text, action = '', label = '';

    if (!space && budgetLeft < cheapest) {
      kind = 'both';
      text = 'Room full and budget spent.';
      action = 'room'; label = 'Add 4 ft each way';
    } else if (!space) {
      kind = 'space';
      /* Naming the unspent budget is what turns this from a status line into a
         reason to act — "full" alone just reads as finished. */
      text = (budgetLeft !== Infinity && budgetLeft > cheapest)
        ? 'Room full, with <b>' + money(budgetLeft) + '</b> still unspent.'
        : 'Room full &mdash; nothing else fits.';
      action = 'room'; label = 'Add 4 ft each way';
    } else if (budgetLeft < cheapest) {
      kind = 'budget';
      text = 'You are at your budget.';
      action = 'budget'; label = 'Raise by $5,000';
    } else {
      kind = 'ok';
      text = budgetLeft === Infinity
        ? 'Room for more &mdash; add anything you like.'
        : '<b>' + money(budgetLeft) + '</b> of budget and floor space left.';
    }

    el.guard.className = 'gs__guard is-' + kind;
    el.guard.innerHTML =
      '<span class="gs__guard__txt">' + text + '</span>' +
      (action ? '<button type="button" class="gs__guard__btn" data-gs-guard="' + action + '">' + label + '</button>' : '');
  }

  /* ══ THINGS TO LOOK OUT FOR — CUT BACK HARD, 2026-08-07 ═══════════════════
     Dillon, on the first version: "significantly reduce this. We might want to
     rethink doing this at all. Only what would specifically add conversion value
     here I think would be best. Anything that would hinder them from buying,
     let's stay away from."

     He is right, and the first version failed that test badly. It reported six
     kinds of thing, and four of them were the tool second-guessing a drawing the
     shopper was still making: two machines overlapping, a walkway tighter than
     recommended, a footprint we had estimated. None of that costs anybody money.
     All of it puts doubt in front of someone about to spend five figures, in a
     panel sitting directly under the thing they are proud of having built.

     WHAT SURVIVES, AND THE TEST IT HAD TO PASS: would ignoring this cost real
     money, to them or to us?

       1. Equipment that does not fit inside the room. Ordering a gym that will
          not fit is a return, a freight bill both ways, and a customer who will
          not come back. Worth saying — and it is framed toward the answer that
          keeps the sale, which is usually a bigger room rather than less gym.

       2. Equipment standing where a door has to open. A delivery crew cannot
          install around it, and the door is the one thing in the room that
          cannot be moved instead.

     WHAT IS GONE FROM HERE: overlaps, tight walkways, estimated footprints. All
     three are still measured and still shown on the selected machine if somebody
     goes looking for them. Overlaps and estimated footprints are also still
     marked per row on the PRINTED plan, which is the right place for a caveat —
     that sheet gets handed to an installer. Tight walkways are on the selected
     machine only. None of it is narrated back at a shopper mid-build.

     DO NOT ADD CATEGORIES BACK WITHOUT THE MONEY TEST. Everything here has to
     survive "would ignoring this cost real money?" — and if the honest answer is
     no, it belongs on the printed plan, not in front of the buy button. */
  function adviceReport() {
    var room = { w: STATE.w, d: STATE.d };
    var out = [];
    var nameOf = function (i) { return cleanTitle(BUILD[i].p.t); };

    var fit = S.fitReport(BUILD, room, keepouts());

    /* ONE SENTENCE EACH. Dillon: "significantly reduce the length of messaging
       throughout the tool, getting straight to the point in one sentence when
       possible." These two are the only advisories left, and they earn their
       place by naming a real cost — so they should say it and stop. */

    /* 1. Outside the room outline. */
    if (fit.outside.length) {
      var n = fit.outside.length;
      out.push({
        key: 'outside',
        head: n === 1 ? '1 piece is outside the room' : n + ' pieces are outside the room',
        body: 'Drag ' + (n === 1 ? 'it' : 'them') + ' inside, or add a few feet to the room, before you order.',
        items: fit.outside
      });
    }

    /* 2. Standing in a doorway. */
    if (fit.blocked && fit.blocked.length) {
      out.push({
        key: 'blocked',
        head: 'In the way of a door',
        body: 'The delivery crew can work around most things, but not a door.',
        items: fit.blocked
      });
    }

    return out;
  }

  /* The panel itself. The <details> shell lives in the Liquid and is never
     replaced — only its innards are rewritten — so the open/closed state the
     shopper chose survives every re-render. Rebuilding the shell would snap it
     shut mid-drag. */
  function renderAdvice() {
    if (!el.advice) return;

    var list = adviceReport();
    if (!list.length) {
      el.advice.hidden = true;
      el.advice.innerHTML = '';
      return;
    }

    var chips = function (idxs) {
      if (!idxs || !idxs.length) return '';
      return '<span class="gs-adv__chips">' + idxs.map(function (i) {
        return '<button type="button" class="gs-adv__chip" data-gs-adv-item="' + i + '">' +
                 '<span class="gs-adv__chipnum">' + (i + 1) + '</span>' +
                 escapeHtml(cleanTitle(BUILD[i].p.t)) +
               '</button>';
      }).join('') + '</span>';
    };

    el.advice.innerHTML =
      '<summary class="gs-adv__head">' +
        'Things to look out for' +
        '<span class="gs-adv__n">' + list.length + '</span>' +
      '</summary>' +
      '<ul class="gs-adv__list" role="list">' +
        list.map(function (a) {
          return '<li class="gs-adv' + (a.note ? ' gs-adv--note' : '') + '">' +
                   '<span class="gs-adv__title">' + a.head + '</span>' +
                   '<span class="gs-adv__body">' + a.body + '</span>' +
                   chips(a.items) +
                 '</li>';
        }).join('') +
      '</ul>';

    el.advice.hidden = false;

    /* Open on first appearance only when the list is short. A wall of eight
       advisories springing open under the plan is the same intimidation the
       badges caused. Once the shopper has touched it, ADVICE_OPEN holds their
       choice for the session. */
    if (ADVICE_OPEN === null) el.advice.open = list.length <= 3;
    else el.advice.open = ADVICE_OPEN;
  }

  var ADVICE_OPEN = null;

  /* ── What the selected machine is, and what its outlines mean ──────────────
     Answers the question the plan was silently raising: there are two kinds of
     dashed box on this drawing and neither said what it was.

     Only rendered while something is selected, so it is information on demand
     rather than a permanent legend competing with the plan. */
  function renderSelInfo() {
    if (!el.selInfo) return;

    var it = SELECTED;
    if (!it) {
      el.selInfo.hidden = true;
      el.selInfo.innerHTML = '';
      return;
    }

    var eff = S.effFp(it);
    var envSpec = S.envFor(it.cls);
    var bits = [];

    bits.push('<span class="gs-si__name">' + escapeHtml(cleanTitle(it.p.t)) + '</span>');
    bits.push('<span class="gs-si__dim">' + Math.round(eff.w) + '&#8243; &times; ' +
              Math.round(eff.d) + '&#8243; &middot; ' + money(it.p.p) + '</span>');

    /* Clearance, named per face now that the halo is directional. The treadmill
       case gets its own sentence because 79in behind is a safety number, not a
       comfort one, and saying which is which is the whole point. */
    /* ONE LINE EACH. These sit under a drawing the shopper is reading, not a
       document they are studying — every one of them used to run to two or
       three sentences saying the same thing twice. */
    if (envSpec.f || envSpec.b || envSpec.s) {
      var clearLine;
      if (it.cls === 'treadmill') {
        clearLine = '<b>Clearance</b> &mdash; ' + envSpec.b + '&#8243; behind the belt is a safety zone, ' +
          envSpec.s + '&#8243; at the sides.';
      } else {
        clearLine = '<b>Clearance</b> &mdash; ' + envSpec.f + '&#8243; in front' +
          (envSpec.s ? ', ' + envSpec.s + '&#8243; at the sides' : '') + ', suggested not required.';
      }
      bits.push('<span class="gs-si__tag gs-si__tag--clear">' + clearLine + '</span>');
    }

    var st = stateOf(it);
    if (st === 'outside') {
      bits.push('<span class="gs-si__tag gs-si__tag--staged">' +
        'Outside the room &mdash; still in your build and your total.' +
      '</span>');
    } else if (st === 'collision') {
      bits.push('<span class="gs-si__tag gs-si__tag--bad">' +
        'Drawn over another piece &mdash; drag either one to open a gap.' +
      '</span>');
    } else if (st === 'tight') {
      bits.push('<span class="gs-si__tag gs-si__tag--tight">' +
        'A tighter squeeze than we would usually leave, but it fits.' +
      '</span>');
    }

    if (it.fp && it.fp.est) {
      bits.push('<span class="gs-si__tag gs-si__tag--est">' +
        '<b>Size estimated</b> &mdash; close, not measured.' +
      '</span>');
    }

    el.selInfo.innerHTML = bits.join('');
    el.selInfo.hidden = false;
  }

  /* The persuasion layer. A total on its own is a number to flinch at; the same
     total beside a filled-in coverage read is evidence the gym does the job. And
     an empty bar is the most natural upsell there is, because it is advice. */
  function renderCoverage() {
    if (!el.coverage) return;
    var cov = S.coverage(BUILD);

    /* UPDATED IN PLACE, NOT REBUILT. This runs from every render, and rebuilding
       the innerHTML would restart each bar from zero on every unrelated redraw —
       so the one moment worth animating (a bar climbing because a rack was just
       added) would be lost in a constant twitch. Keeping the nodes means the CSS
       width transition fires only when the number genuinely changed, which is
       exactly when the shopper should notice it. */
    var rebuild = el.coverage.children.length !== cov.length;
    if (rebuild) {
      el.coverage.innerHTML = cov.map(function (a) {
        return '<li class="gs-cov">' +
          '<span class="gs-cov__label">' + a.label + '</span>' +
          '<span class="gs-cov__track"><span class="gs-cov__fill" style="width:0"></span></span>' +
          '<span class="gs-cov__val"></span>' +
        '</li>';
      }).join('');
    }

    cov.forEach(function (a, i) {
      var li = el.coverage.children[i];
      if (!li) return;
      li.className = 'gs-cov ' + (a.pct >= 100 ? 'is-full' : (a.pct > 0 ? 'is-part' : 'is-none'));
      li.children[0].textContent = a.label;
      li.children[1].firstChild.style.width = a.pct + '%';
      li.children[2].textContent = a.pct >= 100 ? 'Covered' : (a.n ? 'Light' : 'None');
    });

    var gaps = cov.filter(function (a) { return a.n === 0; });
    if (el.covNote) {
      el.covNote.textContent = gaps.length
        ? 'Nothing here trains ' + gaps.map(function (g) { return g.label.toLowerCase(); }).join(', ') + '.'
        : 'This gym covers every major movement.';
    }
  }

  /* A plan label has room for a few words, not a full catalogue title. Strip the
     brand and series prefixes that every BUILT product repeats and keep the part
     that says what the machine actually is. */
  function shortName(it) {
    var t = cleanTitle(it.p.t)
      .replace(/^BUILT Strength\s+/i, '')
      .replace(/\b(Foundation|Fractal|Infinite|Throwback|OG)\s+Series\s+/i, '')
      .replace(/\s*w\/.*$/i, '')
      .replace(/\s*\(.*?\)\s*/g, ' ')
      .trim();
    if (t.length > 26) t = t.slice(0, 25).replace(/\s+\S*$/, '') + '…';
    return t || S.CLASSES[it.cls].label;
  }

  /* Mirrors ctx-product-title.liquid — the condition lives in a badge, not in
     the middle of a sentence. */
  function cleanTitle(t) {
    return String(t || '').replace(/\s*\((?:new|remanufactured|used)\)\s*/gi, ' ').trim();
  }
  /* Free-weight SETS ride the feed as one row per variant, so their `h` is a
     compound key ("<handle>--v<id>") that is unique inside the builder but is
     not a URL. `ph` carries the real product handle, and the variant is named in
     the query string so the product page opens on the set that was placed. */
  function pdpUrl(p) {
    var handle = p.ph || p.h;
    var url = '/products/' + encodeURIComponent(handle);
    if (p.ph && p.v) url += '?variant=' + encodeURIComponent(p.v);
    return url;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ── Money ─────────────────────────────────────────────────────────────── */

  function subtotal() {
    /* priceOf, not raw p.p — one unpriced product in the catalogue would
       otherwise turn every money figure on the page into NaN. The resolve
       panel already treats missing prices as $0 and says so; the live total
       must count the same way. */
    return BUILD.concat(TRAY).reduce(function (a, it) { return a + priceOf(it.p); }, 0);
  }

  function renderMoney() {
    var sub = subtotal();
    var tier = S.tierFor(sub);
    var save = Math.round(sub * tier.pct / 100);

    el.subtotal.textContent = money(sub);
    el.save.textContent = '−' + money(save);
    el.total.textContent = money(sub - save);
    el.tier.textContent = tier.pct + '% bundle discount';

    var nxt = S.nextTier(sub);
    if (nxt && el.nudge) {
      el.nudge.style.display = '';
      el.nudge.innerHTML = 'Add <strong>' + money(nxt.min - sub) + '</strong> to unlock <strong>' + nxt.pct + '% off</strong>';
    } else if (el.nudge) {
      el.nudge.style.display = 'none';
    }

    if (el.addBtn) el.addBtn.disabled = BUILD.length + TRAY.length < 2;
    if (el.ctaPrice) el.ctaPrice.textContent = sub > 0 ? money(sub - save) : '';
    showBudgetPreview(STATE.budget);
  }

  /* ── Build / rebuild ───────────────────────────────────────────────────── */

  function rebuild(keepManual) {
    var room = { w: STATE.w, d: STATE.d };
    if (!keepManual) {
      BUILD = S.recommend(CATALOG, {
        room: room, budget: STATE.budget, goal: STATE.goal, cardioBias: STATE.cardioBias
      });
    }
    var laid = S.arrange(BUILD, room);
    BUILD = laid.placed;
    render();
    saveState();
  }

  /* Run the packer without throwing away anything the shopper placed by hand.

     This is the correct response to the ROOM changing — a different size, a
     different shape. The build is the shopper's; only its arrangement is ours.
     Anything that no longer fits is flagged red rather than deleted, because a
     machine vanishing because a slider moved is the single most alarming thing
     this tool could do.

     `arrange` overwrites x/y on the very objects it is handed, so reading them
     afterwards reads the packer's answer, not the shopper's — the positions have
     to be taken first. `mx`/`my` used to be written only by the saved-link
     decoder, so anything dragged in this session had nothing to fall back to and
     silently lost its place every time the room was resized or a machine added.

     Everything auto-placed is still free to move; only what someone chose is
     protected. */
  function packKeepingManual() {
    var room = { w: STATE.w, d: STATE.d };

    BUILD.forEach(function (it) {
      if (it.moved) { it.mx = it.x; it.my = it.y; }
    });

    S.arrange(BUILD, room, { keepouts: keepouts() });

    /* Clamped into the walls so a machine placed against the old back wall lands
       against the new one rather than floating outside the room. Clamped on the
       machine, not its clearance, exactly as dragging is — a piece may sit flush
       to a wall. */
    BUILD.forEach(function (it) {
      if (!it.moved || it.mx == null) return;
      var eff = S.effFp(it);
      it.x = Math.max(0, Math.min(room.w - eff.w, it.mx));
      it.y = Math.max(0, Math.min(room.d - eff.d, it.my));
      it.mx = it.x; it.my = it.y;
      it.fits = eff.w <= room.w && (it.y + eff.d) <= room.d;
    });

    rescueOverflow();
  }

  /* ── THE OVERFLOW RESCUE ───────────────────────────────────────────────────
     `arrange` runs its own free-space search, but it can only see the items IT
     placed — a hand-dragged machine is restored to its own coordinates
     afterwards, so the packer never knew that floor was taken. This pass runs
     once the whole floor is known and asks the same question with the full
     picture: is anything sitting outside the room that the room could actually
     hold?

     Scope is deliberately narrow. It only moves pieces that are OUTSIDE the
     walls and were NOT placed by hand. An overlapping machine is left alone,
     because in this builder overlap is shown rather than corrected — snapping
     someone's machine away from where they dropped it is the tool arguing with
     them. And a hand-placed piece is never relocated for any reason. */
  function rescueOverflow() {
    var room = { w: STATE.w, d: STATE.d };
    var ko = keepouts();

    function outside(it) {
      var e = S.effFp(it);
      return (it.x || 0) < -0.01 || (it.y || 0) < -0.01 ||
             (it.x || 0) + e.w > room.w + 0.01 || (it.y || 0) + e.d > room.d + 0.01;
    }

    var stranded = BUILD.filter(function (it) { return !it.moved && outside(it); });
    if (!stranded.length) return;

    /* Smallest first. A big piece grabbing the last clear bay would strand three
       small ones that could all have fitted in it. */
    stranded.sort(function (a, b) {
      var ea = S.effFp(a), eb = S.effFp(b);
      return (ea.w * ea.d) - (eb.w * eb.d);
    });

    stranded.forEach(function (it) {
      var settled = BUILD.filter(function (q) { return q !== it && !outside(q); });
      var spot = S.findSpot(it, settled, room, ko, { step: 6 });
      if (!spot) return;                       /* genuinely will not fit — stays in the apron */
      it.x = spot.x;
      it.y = spot.y;
      it.rot = spot.rot;
      it.fits = true;
      it.tight = !!spot.tight;
    });
  }

  function relayout() {
    packKeepingManual();
    markOverlaps();
    render();
    saveState();
  }

  /* Adding a machine to an already-full room pushes the ones at the end past the
     back wall. Overflow is the right thing to SHOW when the shopper caused it by
     dragging, but not when we caused it by seeding their gym from a product page
     — that just looks broken on first load. So when the builder adds something
     itself, it makes room by dropping from the end, never touching index 0,
     which is the machine they actually came here for. */
  function trimToFit(protectFirst) {
    var room = { w: STATE.w, d: STATE.d };
    var guard = 0;
    while (guard++ < 60) {
      S.arrange(BUILD, room);
      var bad = -1;
      for (var i = BUILD.length - 1; i >= (protectFirst ? 1 : 0); i--) {
        if (BUILD[i].fits === false) { bad = i; break; }
      }
      if (bad < 0) break;
      BUILD.splice(bad, 1);
    }
    S.arrange(BUILD, room);
  }

  /* ── Interaction ───────────────────────────────────────────────────────── */

  /* ── ON TOUCH, SELECTION IS DECIDED AT FINGER-UP, NEVER AT FINGER-DOWN ─────
     THE RULE THAT MAKES TWO-FINGER PAN/PINCH POSSIBLE AT ALL.

     Touch events keep firing on the element the finger FIRST landed on, and they
     reach our viewport handler by bubbling up from it. Selecting on pointerdown
     called render(), and render() rebuilds the SVG's innerHTML — which removes
     that element from the document. A removed element has nothing to bubble
     through, so every subsequent touchmove of that gesture silently vanished.

     The consequence on a real phone: put ONE finger down anywhere meaningful —
     a machine, a door, even empty floor (which deselected and also re-rendered)
     — and the browser had already lost the gesture before the second finger
     arrived. Two-finger pan and pinch were therefore dead almost everywhere on a
     zoomed-in plan, which is exactly "there's no way to move the view to the
     right". The gesture maths downstream never even ran.

     So on touch, pointerdown only RECORDS what was pressed. Nothing re-renders.
     The tap is committed on pointerup, and only if it stayed a tap: one finger
     throughout, and under 12px of travel. A second finger or real movement means
     it was the start of a pan/pinch, and the pending selection is thrown away.

     Mouse and pen keep the immediate path — they never had the problem, and a
     mouse drag genuinely must start on the down event. */
  var TAP = null;   /* { kind:'item'|'feat'|'clear', idx, x, y, id } */
  var TAP_SLOP = 12;

  function cancelTap() { TAP = null; }

  function commitTap(e) {
    if (!TAP || e.pointerId !== TAP.id) return;
    var t = TAP; TAP = null;
    if (Math.abs(e.clientX - t.x) > TAP_SLOP || Math.abs(e.clientY - t.y) > TAP_SLOP) return;

    if (t.kind === 'feat') {
      SELECTED_FEAT = FEATURES[t.idx] || null;
      SELECTED = null;
      render();
    } else if (t.kind === 'item') {
      SELECTED = BUILD[t.idx] || null;
      SELECTED_FEAT = null;
      render();
      revealInPanel(SELECTED);
    } else {
      /* Tap on empty floor: deselect — the same thing pointerdown used to do
         immediately, moved to the moment we know it was a tap. */
      if (SELECTED || SELECTED_FEAT) {
        SELECTED = null; SELECTED_FEAT = null;
        render();
      }
    }
  }

  function bindPlan() {
    var svg = el.plan.querySelector('svg');
    if (!svg) return;

    svg.addEventListener('pointerdown', function (e) {
      dismissDragHint();
      /* Features are tested first because they render UNDER the equipment: if
         the pointer landed on one, nothing is stacked on top of it there. */
      var fg = e.target.closest ? e.target.closest('.gs-feat') : null;
      if (fg) {
        var fi = parseInt(fg.dataset.fidx, 10);
        if (e.pointerType === 'touch') {
          TAP = { kind: 'feat', idx: fi, x: e.clientX, y: e.clientY, id: e.pointerId };
          return;
        }
        SELECTED_FEAT = FEATURES[fi];
        SELECTED = null;
        e.preventDefault();
        startFeatureDrag(e, fi, fg);
        return;
      }

      var g = e.target.closest ? e.target.closest('.gs-item') : null;
      if (!g) {
        if (e.pointerType === 'touch') {
          TAP = { kind: 'clear', idx: -1, x: e.clientX, y: e.clientY, id: e.pointerId };
          return;
        }
        SELECTED = null; SELECTED_FEAT = null; render();
        return;
      }
      SELECTED_FEAT = null;

      var idx = parseInt(g.dataset.idx, 10);

      /* ON TOUCH, A PRESS ON A MACHINE ONLY SELECTS IT — and now only once the
         finger comes back up. See the note above bindPlan. Deliberately NOT
         preventDefault-ed: preventing the default here is what claims the
         gesture, and the browser needs it for page scroll. */
      if (e.pointerType === 'touch') {
        TAP = { kind: 'item', idx: idx, x: e.clientX, y: e.clientY, id: e.pointerId };
        return;
      }

      /* THE FIX FOR "dragging isn't working".
         Without preventDefault the browser starts its own gesture — text
         selection on desktop — and then fires `pointercancel`, which silently
         ends the drag. It looked like the handler was never running; it was
         running and being cancelled a frame later. */
      e.preventDefault();

      SELECTED = BUILD[idx];
      revealInPanel(SELECTED);
      startDrag(e, idx, g);
    });

    /* The commit half of the tap. On the SVG rather than window, so a finger
       that wanders off the plan before lifting simply abandons the tap. */
    svg.addEventListener('pointerup', function (e) {
      if (e.pointerType === 'touch') commitTap(e);
    });
    svg.addEventListener('pointercancel', cancelTap);
  }

  /* ── Deselecting ───────────────────────────────────────────────────────────
     Pressing empty space anywhere around the plan drops the selection and takes
     the move/rotate/remove cluster with it. Pressing INSIDE the plan already did
     this; the dead area around the drawing — which is most of the screen on a
     phone, and the obvious place to tap — did not, so the cluster hung over the
     gym with no way to dismiss it.

     Bound on `click`, not `pointerdown`. On pointerdown this handler would run
     before the cluster's own buttons and re-render the plan out from under them,
     so rotate and remove would stop working — a control that dismisses itself
     before it fires is worse than one that never dismisses. By click time every
     inner handler has already had the event.

     Bound to the SECTION, not the document: a tap on the header or the footer is
     navigation, not a statement about the gym. */
  function bindDeselect() {
    /* Everything that either IS the selection or acts on it. A click in here is
       never a request to deselect. */
    var KEEP = '.gs-item, .gs-feat, .gs__handles, .gs-row, .gs-swap, .gs-qty,' +
               ' .gs-resolve, .gs-gate, .gs__results, .gs-res, .gs-sheet,' +
               ' [role="dialog"], button, a, input, select, textarea, label, summary';

    root.addEventListener('click', function (e) {
      if (!SELECTED && !SELECTED_FEAT) return;
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest(KEEP)) return;
      SELECTED = null;
      SELECTED_FEAT = null;
      render();
    });

    /* Escape, on desktop. Skipped while any of the builder's own dialogs is open,
       because those already bind Escape to close themselves and stealing it would
       mean one press did two things. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!SELECTED && !SELECTED_FEAT) return;
      if (RESOLVE) return;
      if (el.qty && !el.qty.hidden) return;
      if (el.gate && !el.gate.hidden) return;
      if (el.swap && !el.swap.hidden) return;
      SELECTED = null;
      SELECTED_FEAT = null;
      render();
    });
  }

  /* Dragging works with pointer events so it covers mouse, pen and touch from
     one code path. `touch-action: none` is set on the SVG only — the rest of the
     page still scrolls normally, which matters because most of this traffic is
     phones and a plan that eats the scroll gesture is worse than no dragging. */
  function startDrag(e, idx, node) {
    var svg = el.plan.querySelector('svg');
    var it = BUILD[idx];
    if (!it || !svg) return;

    var start = toRoom(svg, e.clientX, e.clientY);
    var ox = it.x, oy = it.y;
    var moved = false;
    var PAD = planPad();

    /* Capture on the SVG, not the shape. The shape survives this drag because
       nothing re-renders during it, but capturing on the container means a fast
       flick that outruns the cursor still delivers its moves here. */
    try { svg.setPointerCapture(e.pointerId); } catch (err) {}

    node.classList.add('is-dragging');
    /* The control cluster is positioned from a measured rect, so it would hang
       at the machine's old spot for the whole drag. Hide it and let the render
       on release put it back where the machine actually landed. */
    if (el.handles) el.handles.hidden = true;

    /* Only the transform moves while dragging. The previous version called the
       full render() on every pointermove, which rebuilt the entire SVG — every
       machine, every label — dozens of times a second and destroyed the node
       the gesture had started on. Moving one attribute is both the performance
       fix and the correctness fix. */
    function move(ev) {
      var pt = toRoom(svg, ev.clientX, ev.clientY);
      var nx = ox + (pt.x - start.x);
      var ny = oy + (pt.y - start.y);

      /* NO SNAPPING. A 3in grid snap lived here, and it is what made a machine
         "jump" the moment you pressed and moved: `arrange` places items at
         fractional coordinates, so the very first pointermove rounded the
         position and the machine hopped up to 1.5in before the pointer had
         really gone anywhere. It also meant a piece never came to rest exactly
         where it was released. A snap is a nicety; following the pointer exactly
         is the whole contract of dragging.

         ── AND IT IS NO LONGER CLAMPED TO THE WALLS ────────────────────────────
         It used to be: `nx = max(0, min(STATE.w - eff.w, nx))`. That made it
         physically impossible to drag a machine out of the room, which sounds
         protective and is actually the thing Dillon reported. People shuffle a
         floor by lifting something out of the way, rearranging what is left and
         putting it back — and the builder simply refused, pinning the machine to
         the wall while the finger kept moving.

         Staging outside the room is now allowed and flagged, not prevented. The
         bound is a generous apron rather than the wall line, so a machine can be
         parked clear of the room but can still never be flung somewhere the
         canvas will not draw. */
      var eff = S.effFp(it);
      var margin = STAGE_MARGIN;
      nx = Math.max(-(eff.w + margin), Math.min(STATE.w + margin, nx));
      ny = Math.max(-(eff.d + margin), Math.min(STATE.d + margin, ny));

      it.x = nx; it.y = ny;
      /* Flagged so the saved link carries this machine's coordinates. Everything
         auto-arranged is re-laid by the packer on open instead of being stored,
         which is what keeps a 51-machine build inside a QR code. */
      it.moved = true;
      /* Kept in step with x/y so a later room resize restores where this was
         actually dropped, not where it sat the last time the link was opened. */
      it.mx = nx; it.my = ny;
      moved = true;
      node.setAttribute('transform', 'translate(' + (PAD + nx) + ',' + (PAD + ny) + ')');
    }

    function finish() {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      try { svg.releasePointerCapture(e.pointerId); } catch (err) {}
      node.classList.remove('is-dragging');
      markOverlaps();
      render();
      if (moved) saveState();
    }

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  }

  /* Rotate in 90° steps, then nudge back inside the walls: turning a 35x84
     treadmill near the back wall makes it 84 wide, which can push it out of the
     room. Clamping here means rotation never produces an illegal position the
     shopper then has to fix. */
  function rotateItem(idx) {
    var it = BUILD[idx];
    if (!it) return;

    /* SPIN ON THE SPOT.

       x/y is the top-left of the machine's effective box, and rotating swaps its
       width and depth. Holding the top-left fixed therefore slid the machine
       diagonally across the floor as it turned — which is precisely what "rotates
       45 degrees and doesn't pivot around a single point" looks like. Nothing was
       wrong with the rotation itself; it was anchored to the wrong point.

       Keeping the CENTRE fixed makes it turn where it stands. */
    var before = S.effFp(it);
    var cx = it.x + before.w / 2;
    var cy = it.y + before.d / 2;

    it.rot = ((it.rot || 0) + 90) % 360;
    SELECTED = it;

    var eff = S.effFp(it);
    it.x = cx - eff.w / 2;
    it.y = cy - eff.d / 2;

    /* Only then pull it back inside the walls, so a machine turning near an edge
       nudges in rather than leaping. */
    it.x = Math.max(0, Math.min(STATE.w - eff.w, it.x));
    it.y = Math.max(0, Math.min(STATE.d - eff.d, it.y));
    it.moved = true;
    it.mx = it.x; it.my = it.y;

    markOverlaps();
    render();
    saveState();
  }

  /* Features drag exactly like machines, and snap harder — 6in — because a door
     wants to sit ON a wall, not near one. */
  function startFeatureDrag(e, idx, node) {
    var svg = el.plan.querySelector('svg');
    var f = FEATURES[idx];
    if (!f || !svg) return;

    var start = toRoom(svg, e.clientX, e.clientY);
    var ox = f.x, oy = f.y;
    var PAD = planPad();
    var moved = false;

    try { svg.setPointerCapture(e.pointerId); } catch (err) {}
    node.classList.add('is-dragging');
    /* The control cluster is positioned from a measured rect, so it would hang
       at the machine's old spot for the whole drag. Hide it and let the render
       on release put it back where the machine actually landed. */
    if (el.handles) el.handles.hidden = true;

    function move(ev) {
      var pt = toRoom(svg, ev.clientX, ev.clientY);
      var nx = Math.round((ox + (pt.x - start.x)) / 6) * 6;
      var ny = Math.round((oy + (pt.y - start.y)) / 6) * 6;
      /* Allowed slightly outside the room so a door can sit in the wall line. */
      f.x = Math.max(-f.d, Math.min(STATE.w + f.d, nx));
      f.y = Math.max(-f.d, Math.min(STATE.d + f.d, ny));
      moved = true;
      node.setAttribute('transform', 'translate(' + (PAD + f.x) + ',' + (PAD + f.y) + ') rotate(' + (f.rot || 0) + ')');
    }

    function finish() {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      try { svg.releasePointerCapture(e.pointerId); } catch (err) {}
      node.classList.remove('is-dragging');
      markOverlaps();
      render();
      if (moved) saveState();
    }

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  }

  /* renderFeatPreview() lived here — the desktop preview panel inside the old
     room-features accordion. Deleted with that accordion 2026-08-07: the Room
     shelf shows the artwork on every row at all times, so a separate preview of
     whatever is selected in a dropdown has nothing left to preview.

     The door note it carried survives on the shelf rows, and so does the way it
     decided WHICH features get it, which was got wrong twice before measuring:
     not "has a keepout" (all of them do) and not "keepout bigger than the
     footprint" (a door is 36x36 and its keepout is 36x36 — the declared depth IS
     the swing arc). The honest test is whether the feature has swing artwork in
     CTX_FEAT_PATH, which is populated for exactly the doors. */

  /* `at` is optional. Without it the feature lands centred on the top wall — the
     most common place for a door, and immediately visible rather than hidden
     under equipment. With it (a drop from the shelf) it lands under the pointer,
     PRE-ROUNDED to the same 6in grid startFeatureDrag snaps to: seed it
     unrounded and the feature hops up to 3in on the first pointer move. */
  function addFeature(kind, at) {
    var def = FEATDEF[kind];
    if (!def) return -1;
    var f = {
      k: kind, w: def.w, d: def.d,
      x: at ? Math.round((at.x - def.w / 2) / 6) * 6
            : Math.max(0, Math.round((STATE.w - def.w) / 2 / 6) * 6),
      y: at ? Math.round((at.y - def.d / 2) / 6) * 6 : 0,
      rot: 0, hinge: 1
    };
    FEATURES.push(f);
    SELECTED_FEAT = f;
    FRESH = f;
    markOverlaps();
    render();
    saveState();
    return FEATURES.length - 1;
  }

  function toRoom(svg, cx, cy) {
    var pt = svg.createSVGPoint();
    pt.x = cx; pt.y = cy;
    var m = svg.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    var p = pt.matrixTransform(m.inverse());
    var pad = planPad();
    return { x: p.x - pad, y: p.y - pad };   /* strip the drawing margin */
  }

  /* Overlap is shown, not prevented. Snapping a machine back where it came from
     feels like the tool arguing with you; a red outline says the same thing and
     leaves them in control. */
  /* The floor a feature claims, in room coordinates, after rotation. For a swing
     door this is the quarter-circle the leaf sweeps — the thing people forget
     and then park a leg press in. */
  function featureKeepout(f) {
    var def = FEATDEF[f.k];
    if (!def) return null;
    var k = def.keepout(f.w, f.d);
    if (!k.w || !k.d) return null;

    var rot = ((f.rot || 0) % 360 + 360) % 360;
    /* Rotation is about the feature's own origin, matching how it is drawn. */
    if (rot === 0)   return { x: f.x,        y: f.y,        w: k.w, d: k.d };
    if (rot === 90)  return { x: f.x - k.d,  y: f.y,        w: k.d, d: k.w };
    if (rot === 180) return { x: f.x - k.w,  y: f.y - k.d,  w: k.w, d: k.d };
    return               { x: f.x,        y: f.y - k.w,  w: k.d, d: k.w };
  }

  /* `blockedByFeature` lived here and duplicated the box test that fitReport now
     owns. Removed rather than left in place: two copies of an overlap rule drift
     the moment one is corrected, which is exactly how the wall check went
     missing from the old markOverlaps. */

  /* One definition of "does this fit", shared with the geometry file and its
     tests. This used to be a private copy that checked overlaps and door swings
     but NOT the walls, so a machine pushed outside the room came back green —
     and `arrange` had already set `fits` correctly a moment earlier, only for
     this to overwrite it. Blocking a door is as much a failure as overlapping a
     machine, and a good deal more annoying to discover after delivery. */
  function markOverlaps() {
    var room = { w: STATE.w, d: STATE.d };
    STATES = S.placementStates(BUILD, room, keepouts());

    /* `fits` is retained because the packer writes it and the saved-link decoder
       reads it, but it is no longer what the UI draws from — STATES is. Keeping
       one boolean as the source of truth is what made "outside the room" and
       "on top of something" indistinguishable, and outside-the-room is now a
       legitimate thing to be. */
    BUILD.forEach(function (it, i) {
      it.fits = STATES[i] !== 'outside' && STATES[i] !== 'collision';
      it.tight = STATES[i] === 'tight';
    });
  }

  /* ── Swap panel ────────────────────────────────────────────────────────── */

  function openSwap(idx) {
    var it = BUILD[idx];
    if (!it) return;
    var alts = S.alternatives(CATALOG, it, 10);

    el.swap.innerHTML =
      '<div class="gs-swap__head">' +
        '<strong>Swap ' + escapeHtml(cleanTitle(it.p.t)) + '</strong>' +
        '<button type="button" class="gs-mini" data-gs-swap-close>Close</button>' +
      '</div>' +
      '<ul class="gs-swap__list">' +
        alts.map(function (a, i) {
          var delta = a.p.p - it.p.p;
          return '<li><button type="button" data-gs-swap-pick="' + idx + '" data-alt="' + i + '">' +
            '<img src="' + a.p.i + '" alt="" width="44" height="44" loading="lazy">' +
            '<span class="gs-swap__name">' + escapeHtml(cleanTitle(a.p.t)) + '</span>' +
            '<span class="gs-swap__delta">' + (delta === 0 ? 'same price' : (delta > 0 ? '+' : '−') + money(Math.abs(delta))) + '</span>' +
          '</button></li>';
        }).join('') +
      '</ul>';

    el.swap.dataset.alts = JSON.stringify(alts.map(function (a) { return a.p.h; }));
    el.swap.hidden = false;

    /* ── AND SHOW IT TO THEM ────────────────────────────────────────────────
       Swap "did nothing" — Dillon's report — and the panel was working the
       whole time. Measured: it opens 2,168px down the page while the shopper is
       at 534px, because it lives below the whole In Your Gym panel. So the
       button appeared dead, and the alternatives sat unseen a screen and a half
       further down.

       A control that produces its result off-screen has not produced a result.
       Nothing else on this page moves the viewport, so this is deliberate and
       narrow: only on the explicit Swap tap, and only far enough to bring the
       panel into view. */
    if (el.swap.scrollIntoView) {
      var r = el.swap.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) {
        el.swap.scrollIntoView({
          block: 'nearest',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      }
    }

    /* Focus the close button so a keyboard user lands inside the panel that
       just appeared rather than back at the top of the row list. */
    var closeBtn = el.swap.querySelector('[data-gs-swap-close]');
    if (closeBtn) { try { closeBtn.focus({ preventScroll: true }); } catch (e) {} }
  }

  /* ── Add to cart ───────────────────────────────────────────────────────────
     A SYNC, not an append. Any lines a previous click put in the cart (they
     all carry `_bundle_source: gym-studio`) are zeroed first, then the current
     floor is added fresh under one new bundle id.

     Why it must work this way: /cart/add.js processes a multi-item payload
     item by item and stops at the first failure, so a sold-out machine used to
     leave HALF a gym in the cart — and the retry re-posted everything under a
     new bundle id, doubling what had landed. Sync-first makes the button
     idempotent: click it five times, the cart holds the floor exactly once.
     It is also what lets "Show in cart" promise that the cart MATCHES the
     drawing rather than merely containing it. Manually-added PDP items are
     untouched — only gym-studio lines are ever cleared. */

  function clearPriorBuild() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var updates = {};
        var stale = 0;
        (cart.items || []).forEach(function (line) {
          if (line.properties && line.properties._bundle_source === 'gym-studio') {
            updates[line.key] = 0;
            stale++;
          }
        });
        if (!stale) return null;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      });
  }

  function addToCart() {
    var items = BUILD.concat(TRAY);
    /* One machine is a sale even though it is not a bundle — the money panel's
       own button stays disabled below two (that button sells the discount),
       but Show in cart must honestly sync whatever is drawn. */
    if (!items.length) return;

    var uid = 'gym-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
    var tier = S.tierFor(subtotal());

    var payload = {
      items: items.map(function (it) {
        return {
          id: it.p.v,
          quantity: 1,
          properties: {
            _bundle_id: uid,
            _bundle_source: 'gym-studio',
            _bundle_discount: tier.pct
          }
        };
      })
    };

    /* The label is its own element now, because the price lives in the button
       too — writing to the button's textContent would wipe the price node. */
    var labelEl = el.ctaLabel || el.addBtn;
    var label = labelEl.textContent;
    el.addBtn.disabled = true;
    labelEl.textContent = 'Adding…';
    setCartError('');

    clearPriorBuild()
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
      })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (r) {
        if (!r.ok) throw new Error(r.d.description || r.d.message || 'Could not add your gym.');
        REACHED_CART = true;
        CART_VALUE = Math.round(subtotal() / 100);
        LAST_ACTION = 'Added to cart';
        track('add-to-cart');
        labelEl.textContent = 'Added ✓';
        /* THE BUTTON TAKES THE LOAD. This control is already drawn as a physical
           object sitting on a 4px hard shadow, so seating it — and holding it
           seated — reads as a plate settling onto a stack rather than a UI
           flourish. The right register for a company shipping freight pallets of
           steel, and it fills a real gap: openDrawer() re-fetches the cart
           section, so there is a few hundred milliseconds where this button is
           the only feedback there is.

           The colour and the label change live OUTSIDE the reduced-motion query;
           only the movement is inside it. So with motion off it still turns
           green and still says Added — the whole message survives. */
        el.addBtn.classList.add('is-seated');
        openDrawer();
        setTimeout(function () {
          labelEl.textContent = label;
          el.addBtn.disabled = false;
          el.addBtn.classList.remove('is-seated');
        }, 1800);
      })
      .catch(function (err) {
        labelEl.textContent = label;
        el.addBtn.disabled = false;
        el.addBtn.classList.remove('is-seated');
        /* Loud, named, and above the fold of the money panel — the old
           treatment was one grey line that doubled as the loading note, and a
           failed $40,000 cart deserves better than a whisper. Shopify's own
           message names the machine that could not be added. Because the next
           click re-syncs from zero, "try again" is safe advice: nothing can
           double. */
        setCartError((err.message || 'Could not add your gym.') +
          ' Nothing was double-added — fix or remove that item and press the button again.');
      });
  }

  function setCartError(msg) {
    if (!el.status) return;
    el.status.textContent = msg;
    el.status.classList.toggle('is-cart-error', !!msg);
    if (msg && el.status.scrollIntoView) {
      el.status.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  /* Same dance as the PDP bundler: the drawer was rendered with the page, so on
     an empty cart it still carries `is-empty` and would slide in claiming the
     cart is empty while a gym sits in it. Re-fetch, swap, then open. */
  function openDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') { window.location.href = '/cart'; return; }

    fetch('/?section_id=cart-drawer')
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
          var live = document.querySelector('#CartDrawer');
          if (fresh && live) live.innerHTML = fresh.innerHTML;
        }
        drawer.classList.remove('is-empty');
        drawer.open();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(function () {
        drawer.classList.remove('is-empty');
        drawer.open();
      });
  }

  /* ── The gate + quiz ───────────────────────────────────────────────────────
     The builder renders fully, then sits greyed and inert behind a Customize
     card until they choose to start. Dillon's reasoning, which I agree with: a
     modal in front of a page nobody has seen yet reads as a toll and gets
     dismissed on reflex, whereas a working gym sitting just out of reach makes
     the button an invitation.

     ONLY THE FIRST TWO QUESTIONS ARE REQUIRED — Dillon's instruction. Space and
     budget are the two that actually change what gets built; everything after
     that is refinement, and a shopper who bails on question four should still
     end up with a gym rather than nothing. Skip is offered from step 3 onward,
     plus a "skip the rest" that jumps straight to the build. */

  var QUIZ = [
    {
      id: 'space',
      required: true,
      q: 'Where is the gym going?',
      hint: 'We will draw the room to scale. You can change the exact size after.',
      options: [
        { label: 'Spare bedroom',       sub: '11 × 12 ft', v: 'spare-room' },
        { label: 'One-car garage',      sub: '12 × 20 ft', v: 'one-car' },
        { label: 'Two-car garage',      sub: '20 × 20 ft', v: 'two-car' },
        { label: 'Basement',            sub: '25 × 30 ft', v: 'basement' },
        { label: 'Studio',              sub: '1,500 sq ft', v: 'studio' },
        { label: 'Commercial floor',    sub: '5,000 sq ft', v: 'commercial' }
      ]
    },
    {
      id: 'budget',
      required: true,
      q: 'Roughly what are you looking to spend?',
      hint: 'A guide, not a limit — nothing gets blocked and you can move it later.',
      options: [
        { label: 'Under $10k',   sub: 'A first room',        v: 900000 },
        { label: '$10k – $25k',  sub: 'A serious garage',    v: 1900000 },
        { label: '$25k – $60k',  sub: 'A complete gym',      v: 4500000 },
        { label: '$60k – $120k', sub: 'Unlocks 10% off',     v: 9000000 },
        { label: '$120k +',      sub: 'Unlocks 15% off',     v: 18000000 }
      ]
    },
    {
      id: 'goal',
      required: false,
      q: 'What matters most?',
      hint: 'Shapes which machines get picked first.',
      options: [
        { label: 'All-round',        sub: 'A bit of everything', v: 'general' },
        { label: 'Strength',         sub: 'Racks and barbells',  v: 'strength' },
        { label: 'Muscle',           sub: 'Machines and cables', v: 'muscle' },
        { label: 'Conditioning',     sub: 'Cardio-led',          v: 'cardio' },
        { label: 'Commercial floor', sub: 'Members and traffic', v: 'commercial' }
      ]
    },
    {
      id: 'cardio',
      required: false,
      q: 'How much cardio?',
      hint: 'Last one.',
      options: [
        { label: 'None',              sub: 'Strength only',       v: 0.04 },
        { label: 'One good machine',  sub: 'The usual answer',    v: 0.35 },
        { label: 'A cardio corner',   sub: 'Two or more pieces',  v: 0.58 }
      ]
    }
  ];

  var quizStep = 0;
  var QUIZ_KEY = 'ctxGymStudio.customised';
  var editingPrefs = false;      /* re-running the quiz from Edit preferences */
  var prefsSnapshot = null;      /* answers as they were, so Cancel can restore */

  /* Captured from the Liquid-rendered intro card before JS ever replaces it, so
     the copy stays editable in the theme editor rather than hardcoded here. */
  var GATE = (function () {
    var card = el.gate ? el.gate.querySelector('.gs__gatecard') : null;
    var pick = function (sel, fallback) {
      var n = card && card.querySelector(sel);
      return n ? n.textContent.trim() : fallback;
    };
    return {
      kicker: pick('.gs__gatekicker', 'Your gym, your room'),
      title:  pick('.gs__gatetitle', 'Build a gym that actually fits'),
      text:   pick('.gs__gatetext', 'Two quick questions and we will lay out a complete room to scale.'),
      cta:    pick('.gs__cta--gate', 'Customize my gym')
    };
  })();

  function lockStage(locked) {
    root.classList.toggle('is-locked', !!locked);
    if (el.gate) {
      el.gate.hidden = !locked;
      /* Belt and braces. The class drives the CSS, the attribute keeps it out of
         the accessibility tree, and this inline style means the card cannot
         survive on screen even if a future stylesheet re-specifies display. */
      el.gate.style.display = locked ? '' : 'none';
    }
    if (el.stage) {
      /* aria-hidden AND inert-by-tabindex: a greyed panel the keyboard can still
         reach is worse than no gate at all. */
      el.stage.setAttribute('aria-hidden', locked ? 'true' : 'false');
      var focusables = el.stage.querySelectorAll('button, select, input, a, [tabindex]');
      Array.prototype.forEach.call(focusables, function (n) {
        if (locked) { n.setAttribute('data-gs-untab', n.getAttribute('tabindex') || ''); n.setAttribute('tabindex', '-1'); }
        else if (n.hasAttribute('data-gs-untab')) {
          var prev = n.getAttribute('data-gs-untab');
          if (prev) n.setAttribute('tabindex', prev); else n.removeAttribute('tabindex');
          n.removeAttribute('data-gs-untab');
        }
      });
    }
  }

  /* The intro card, rebuilt in JS so `Back` from question one has somewhere to
     go. Previously step 0 was the floor and Back dead-ended on itself, which is
     exactly the trap Dillon described — you move forward, hit Back expecting to
     get out, and nothing happens. */
  function renderIntro() {
    el.gate.innerHTML =
      '<div class="gs__gatecard">' +
        closeBtn() +
        '<span class="gs__gatekicker">' + escapeHtml(GATE.kicker) + '</span>' +
        '<h2 class="gs__gatetitle">' + escapeHtml(GATE.title) + '</h2>' +
        '<p class="gs__gatetext">' + escapeHtml(GATE.text) + '</p>' +
        '<button type="button" class="gs__cta gs__cta--gate" data-gs-gate-start>' + escapeHtml(GATE.cta) + '</button>' +
        '<button type="button" class="gs__gateskip" data-gs-gate-skip>Skip &mdash; just show me a gym</button>' +
      '</div>';
  }

  /* One way out from every screen. A gate with no exit is a trap, and the
     builder behind it already holds a complete gym they are allowed to have. */
  function closeBtn() {
    return '<button type="button" class="gs__gatex" data-gs-gate-close aria-label="Close and use the default gym">&times;</button>';
  }

  function currentAnswer(step) {
    if (step.id === 'space')  return STATE.preset;
    if (step.id === 'budget') return STATE.budget;
    if (step.id === 'goal')   return STATE.goal;
    if (step.id === 'cardio') return STATE.cardioBias;
    return null;
  }

  function renderQuiz() {
    var step = QUIZ[quizStep];
    if (!step) { finishQuiz(); return; }

    var canSkip = !step.required || editingPrefs;

    el.gate.innerHTML =
      '<div class="gs__gatecard gs__gatecard--quiz">' +
        closeBtn() +
        '<div class="gs__qtop">' +
          '<span class="gs__qcount">Question ' + (quizStep + 1) + ' of ' + QUIZ.length + '</span>' +
          '<span class="gs__qdots">' +
            QUIZ.map(function (_, i) {
              return '<i class="' + (i < quizStep ? 'is-done' : (i === quizStep ? 'is-now' : '')) + '"></i>';
            }).join('') +
          '</span>' +
        '</div>' +
        '<h2 class="gs__gatetitle">' + escapeHtml(step.q) + '</h2>' +
        '<p class="gs__gatetext">' + escapeHtml(step.hint) + '</p>' +
        '<div class="gs__qopts">' +
          step.options.map(function (o, i) {
            /* Reopening the questions shows what was answered last time, so it
               reads as editing rather than starting from nothing. */
            var on = o.v === currentAnswer(step);
            return '<button type="button" class="gs__qopt' + (on ? ' is-picked' : '') + '"' +
              (on ? ' aria-pressed="true"' : '') + ' data-gs-q-pick="' + i + '">' +
              '<span class="gs__qopt__label">' + escapeHtml(o.label) + '</span>' +
              (o.sub ? '<span class="gs__qopt__sub">' + escapeHtml(o.sub) + '</span>' : '') +
            '</button>';
          }).join('') +
        '</div>' +
        '<div class="gs__qfoot">' +
          '<button type="button" class="gs__qback" data-gs-q-back>' +
            (editingPrefs && quizStep === 0 ? 'Cancel' : 'Back') + '</button>' +
          (canSkip
            ? '<span class="gs__qskips">' +
                '<button type="button" class="gs__qskip" data-gs-q-skip>' +
                  (editingPrefs ? 'Leave unchanged' : 'Skip') + '</button>' +
                '<button type="button" class="gs__qskip gs__qskip--all" data-gs-q-skipall>' +
                  (editingPrefs ? 'Done' : 'Skip the rest') + '</button>' +
              '</span>'
            : '<span class="gs__qreq">Needed to draw your room</span>') +
        '</div>' +
      '</div>';
  }

  function applyAnswer(step, value) {
    if (step.id === 'space') {
      var p = PRESETS[value];
      STATE.preset = value;
      if (p) { STATE.w = p.w; STATE.d = p.d; }
      if (el.preset) el.preset.value = value;
      if (el.wIn) el.wIn.value = Math.round(STATE.w / 12);
      if (el.dIn) el.dIn.value = Math.round(STATE.d / 12);
    } else if (step.id === 'budget') {
      /* Clamped to the room type the shopper just chose. The quiz asks about
         space before money, so "$120k +" on a spare bedroom is a real sequence
         and it has to land at the residential ceiling rather than above it. */
      STATE.budget = S.clampBudget(value, STATE.preset);
      syncBudgetSlider();
    } else if (step.id === 'goal') {
      STATE.goal = value;
      if (el.goal) el.goal.value = value;
    } else if (step.id === 'cardio') {
      STATE.cardioBias = value;
    }
  }

  /* The machine someone arrived with from a product page. Module scope because
     it is needed in two places: at boot, and again after the quiz — `rebuild`
     regenerates the whole build from the answers, which silently threw the
     seeded machine away and left them looking at a gym missing the one item they
     clicked through for. */
  function applySeed() {
    var seed = seedHandle();
    if (!seed) return false;
    /* PDP links now carry `handle--v<variant id>`, so the seed can be
       variant-exact where variants are physically different objects — a
       shopper on the 5-100lb dumbbell page must get the 5-100 racks, not
       whichever set happens to be first. Resolution order:
         1. exact compound handle  (the expanded free-weight rows)
         2. any row selling that variant id
         3. the bare product handle (both forms) — covers colourway variants,
            where the footprint is the same object in different paint. */
    var m = seed.match(/^(.*)--v(\d+)$/);
    var bare = m ? m[1] : seed;
    var vid = m ? parseInt(m[2], 10) : 0;

    /* The already-here check matches at the same grain as the seed. A
       variant-exact seed only stands down for that exact variant: someone
       arriving from the 5-75lb set page whose suggested gym happens to hold
       the 5-25lb set is here for a DIFFERENT object and must still get it. A
       bare product seed keeps the old any-variant check. */
    var dup = vid
      ? BUILD.some(function (it) { return it.p.v === vid; })
      : BUILD.some(function (it) { return it.p.h === bare || it.p.ph === bare; });
    if (dup) return false;

    var p = CATALOG.filter(function (x) { return x.h === seed; })[0] ||
            (vid ? CATALOG.filter(function (x) { return x.v === vid; })[0] : null) ||
            CATALOG.filter(function (x) { return x.h === bare; })[0] ||
            CATALOG.filter(function (x) { return x.ph === bare; })[0];
    if (!p) return false;
    BUILD.unshift({ p: p, cls: S.classify(p), fp: S.footprint(p), x: 0, y: 0, rot: 0 });
    return true;
  }

  function finishQuiz() {
    try { localStorage.setItem(QUIZ_KEY, '1'); } catch (e) {}
    QUIZ_DONE = true;
    LAST_ACTION = 'Completed quiz';
    track('quiz');

    /* Re-running the quiz from Edit preferences ends at a choice, never at a
       silent replacement of the gym. */
    if (editingPrefs) { renderPrefsSave(); return; }

    lockStage(false);
    rebuild(false);
    /* rebuild() renders as its last step, so re-adding the seeded machine after
       it needs its own render — otherwise the machine is in the build and
       nowhere on screen. */
    if (applySeed()) { trimToFit(true); markOverlaps(); render(); saveState(); }
    /* Bring the plan into view — they just answered questions about a room they
       could only half see. */
    var wrap = root.querySelector('.gs__planwrap');
    if (wrap && wrap.scrollIntoView) wrap.scrollIntoView({ block: 'center', behavior: 'smooth' });
    /* The gate just lifted — this is the first moment a gated visitor's room
       is actually touchable, so it is also the first moment the drag hint is
       allowed to show. Deferred past the scroll above; positioning it mid
       smooth-scroll would measure a rect that is still moving. */
    setTimeout(maybeShowDragHint, 400);
  }

  /* ── Edit preferences / Start over ─────────────────────────────────────────
     One way in to changing answers, one way in to clearing — the brief is
     explicit that competing "Retake quiz / Reset / Clear build" controls beside
     each other are what makes this confusing.

     Editing preferences NEVER deletes placed equipment. Rebuilding is offered as
     a separate, clearly-labelled choice at the point of saving, so replacing the
     gym is always something the shopper asked for out loud. */
  function openPreferences() {
    quizStep = 0;
    editingPrefs = true;
    prefsSnapshot = { w: STATE.w, d: STATE.d, budget: STATE.budget, goal: STATE.goal, preset: STATE.preset, cardioBias: STATE.cardioBias };
    lockStage(true);
    renderQuiz();
  }

  function cancelPreferences() {
    if (prefsSnapshot) {
      STATE.w = prefsSnapshot.w; STATE.d = prefsSnapshot.d;
      STATE.budget = prefsSnapshot.budget; STATE.goal = prefsSnapshot.goal;
      STATE.preset = prefsSnapshot.preset; STATE.cardioBias = prefsSnapshot.cardioBias;
      syncControls();
    }
    editingPrefs = false;
    prefsSnapshot = null;
    lockStage(false);
    relayout();
  }

  function syncControls() {
    if (el.wIn) el.wIn.value = Math.round(STATE.w / 12);
    if (el.dIn) el.dIn.value = Math.round(STATE.d / 12);
    if (el.preset) el.preset.value = STATE.preset;
    if (el.goal) el.goal.value = STATE.goal;
    syncBudgetSlider();
    syncToggles();
  }

  /* THE SEGMENTED TOGGLES READ FROM STATE, not from the last click.

     `is-on` used to be written only inside the click handlers, so the buttons
     were correct exactly until the page reloaded. Landing on a saved link with
     rubber flooring drew a rubber floor under a control that said Grid — and
     pressing Grid then appeared to do nothing, because the state already
     matched what the button was about to set. Same fault on the walkway
     toggle. */
  function syncToggles() {
    if (el.floorBtns) {
      Array.prototype.forEach.call(el.floorBtns.querySelectorAll('[data-gs-floor]'), function (n) {
        var on = n.dataset.gsFloor === STATE.floor;
        n.classList.toggle('is-on', on);
        n.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    if (el.clearBtns) {
      Array.prototype.forEach.call(el.clearBtns.querySelectorAll('[data-gs-clear]'), function (n) {
        var on = (n.dataset.gsClear === 'on') === (STATE.showClear !== false);
        n.classList.toggle('is-on', on);
        n.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
  }

  function renderPrefsSave() {
    /* Nothing on the floor means there is nothing to protect, and asking "keep
       your gym or replace it?" about an empty room is a question with no
       meaning. Just build from the new answers. */
    if (!BUILD.length) {
      editingPrefs = false; prefsSnapshot = null;
      lockStage(false); rebuild(false); renderPrefSummary();
      return;
    }

    el.gate.innerHTML =
      '<div class="gs__gatecard">' +
        closeBtn() +
        '<span class="gs__gatekicker">Preferences updated</span>' +
        '<h2 class="gs__gatetitle">Use your new answers to re-suggest equipment?</h2>' +
        '<p class="gs__gatetext">Your answers are saved either way. This only decides whether we re-pick the ' +
          BUILD.length + ' piece' + (BUILD.length === 1 ? '' : 's') + ' on your floor, or leave them alone.</p>' +
        /* Dillon's wording, and he is right: the choice is about the SUGGESTIONS,
           not about the gym as an object. "Keep my gym as it is" invited the
           reading that the other button would delete the gym rather than
           re-suggest one. */
        '<button type="button" class="gs__cta gs__cta--gate" data-gs-prefs-keep>Keep old equipment suggestions</button>' +
        '<button type="button" class="gs__gateskip" data-gs-prefs-rebuild>Replace with new suggestions</button>' +
      '</div>';
  }

  function renderStartOver() {
    el.gate.innerHTML =
      '<div class="gs__gatecard">' +
        closeBtn() +
        '<span class="gs__gatekicker">Start over</span>' +
        '<h2 class="gs__gatetitle">What would you like to clear?</h2>' +
        '<p class="gs__gatetext">Nothing here touches your cart. Anything already added to the cart stays there.</p>' +
        '<button type="button" class="gs__cta gs__cta--gate" data-gs-clear-gym>Clear the gym only</button>' +
        '<button type="button" class="gs__gateskip" data-gs-clear-all>Clear everything and start the questions again</button>' +
      '</div>';
  }

  function bindGate() {
    if (!el.gate) return;

    el.gate.addEventListener('click', function (e) {
      if (e.target.closest('[data-gs-gate-start]')) { quizStep = 0; renderQuiz(); return; }
      if (e.target.closest('[data-gs-gate-skip]')) { finishQuiz(); return; }
      if (e.target.closest('[data-gs-gate-close]')) {
        if (editingPrefs) { cancelPreferences(); return; }
        finishQuiz(); return;
      }

      /* Preference save choices */
      if (e.target.closest('[data-gs-prefs-keep]')) {
        editingPrefs = false; prefsSnapshot = null;
        lockStage(false); relayout(); renderPrefSummary();
        return;
      }
      if (e.target.closest('[data-gs-prefs-rebuild]')) {
        editingPrefs = false; prefsSnapshot = null;
        lockStage(false); rebuild(false); renderPrefSummary();
        return;
      }

      /* Start over scopes.

         The gym-only scope runs the SAME clearGym() the Clear Gym button uses,
         rather than repeating the field-by-field reset it used to. Two copies of
         "empty the floor" drift the moment one of them learns about a new piece
         of state — and this copy had already diverged: it cleared FEATURES too,
         so choosing "clear the gym only" silently deleted the doors and columns
         someone had placed to design around. Room features are part of the room,
         not part of the gym. */
      if (e.target.closest('[data-gs-clear-gym]')) {
        lockStage(false);
        clearGym();
        return;
      }
      if (e.target.closest('[data-gs-clear-all]')) {
        BUILD = []; FEATURES = []; SELECTED = null; SELECTED_FEAT = null;
        try { localStorage.removeItem(QUIZ_KEY); localStorage.removeItem('ctxGymStudio.last'); } catch (err) {}
        history.replaceState(null, '', location.pathname + location.search);
        editingPrefs = false; prefsSnapshot = null;
        quizStep = 0;
        renderIntro();
        lockStage(true);
        return;
      }
      if (e.target.closest('[data-gs-q-skipall]')) { finishQuiz(); return; }
      if (e.target.closest('[data-gs-q-skip]')) { quizStep++; renderQuiz(); return; }

      /* Back from question one returns to the intro card rather than sitting on
         itself — Dillon's ask: someone who steps forward and then hits Back is
         trying to get out, and should find the way out there. */
      if (e.target.closest('[data-gs-q-back]')) {
        if (quizStep === 0 && editingPrefs) { cancelPreferences(); return; }
        if (quizStep === 0) { renderIntro(); return; }
        quizStep--; renderQuiz(); return;
      }

      var pick = e.target.closest('[data-gs-q-pick]');
      if (!pick) return;
      var step = QUIZ[quizStep];
      var opt = step.options[parseInt(pick.dataset.gsQPick, 10)];
      if (!opt) return;
      applyAnswer(step, opt.v);
      quizStep++;
      if (quizStep >= QUIZ.length) finishQuiz(); else renderQuiz();
    });
  }

  /* ── Email me my gym ───────────────────────────────────────────────────────
     Fires a Klaviyo event carrying the whole build plus a resume link, so the
     email can be designed in Klaviyo rather than hardcoded here, and the build
     lands on the customer's profile for follow-up.

     Deliberately offered AFTER the gym exists, never as a gate in front of it.
     Save-and-resume already works with no email at all, so declining costs the
     shopper nothing — which is the only reason the ask is fair.

     Uses Klaviyo's public client API. That endpoint takes a PUBLIC site key,
     not a private one: it is designed to be called from the browser and can
     only create events, never read profiles. No secret is exposed here. */
  function klaviyoKey() {
    return (root.getAttribute('data-klaviyo-key') || '').trim();
  }

  function emailMyGym(email, opts) {
    var key = klaviyoKey();
    opts = opts || {};
    /* FIRST, before the network. If the request fails the layout must still be in
       the URL — losing someone's gym because an email did not send would be the
       worst possible trade for a lead. */
    saveState();

    var items = BUILD.concat(TRAY);
    var sub = subtotal();
    var tier = S.tierFor(sub);

    var payload = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            /* Attribution, so this lead is identifiable as a builder lead in
               Klaviyo without having to infer it from the event name later. */
            source: 'gym builder',
            source_detail: 'emailed build',
            /* The consent answer is recorded as an EVENT PROPERTY, not as a
               profile subscription. Subscribing someone from the browser with a
               public key would be consent the store cannot evidence; a flow in
               Klaviyo can act on this property using Klaviyo's own consent
               machinery, which can. */
            marketing_opt_in: !!opts.consent,
            resume_url: location.href,
            room_width_ft: Math.round(STATE.w / 12),
            room_depth_ft: Math.round(STATE.d / 12),
            goal: STATE.goal,
            machine_count: items.length,
            subtotal: sub / 100,
            discount_percent: tier.pct,
            total: (sub - Math.round(sub * tier.pct / 100)) / 100,
            items: items.map(function (it) {
              return {
                title: cleanTitle(it.p.t),
                handle: it.p.h,
                url: location.origin + '/products/' + it.p.h,
                image: it.p.i,
                price: it.p.p / 100,
                condition: it.p.cn === 'reman' ? 'Remanufactured' : 'New',
                lead_time: it.p.ld || ''
              };
            })
          },
          metric: { data: { type: 'metric', attributes: { name: 'Built A Gym' } } },
          profile: { data: { type: 'profile', attributes: { email: email } } }
        }
      }
    };

    if (!key) {
      return Promise.reject(new Error('Email is not connected yet.'));
    }

    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(key), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      /* Klaviyo answers 202 with an empty body on success. */
      if (!r.ok) throw new Error('Could not send that just now.');
      return true;
    });
  }

  /* ── The email dialog ──────────────────────────────────────────────────────
     Opened only by the "Email my gym" button. Everything about it follows from
     one rule: the shopper must never be worse off for having pressed it.

       · Review / Print works with no email, and says so here.
       · The build is saved to the URL before anything is sent, so a failure
         cannot cost them the gym.
       · Marketing consent is a SEPARATE, UNCHECKED box. Sending yourself a copy
         of your own plan is not permission to market to you, and pre-ticking it
         would make the whole ask dishonest.
       · The address is validated before the request, and the error says what to
         do rather than reporting a status code. */
  function openEmailDialog(returnFocusTo) {
    if (!el.qty) return;

    if (!BUILD.length) {
      if (el.status) el.status.textContent = 'Add something to your gym first, then we can email it to you.';
      return;
    }

    var sub = subtotal();
    var tier = S.tierFor(sub);

    el.qty.innerHTML =
      '<div class="gs-qty__card gs-qty__card--email" role="dialog" aria-modal="true" aria-labelledby="gsEmailTitle">' +
        '<h2 class="gs-qty__title" id="gsEmailTitle">Email this gym to yourself</h2>' +
        '<p class="gs-qty__text">We will send the floor plan, the full equipment list with prices, and a link ' +
          'that reopens this exact layout so you can carry on later.</p>' +
        '<p class="gs-qty__which">' + BUILD.length + ' item' + (BUILD.length === 1 ? '' : 's') +
          ' &middot; ' + money(sub - Math.round(sub * tier.pct / 100)) + '</p>' +
        '<form class="gs-email__form" data-gs-email-form novalidate>' +
          '<label class="gs-email__lbl" for="gsEmailAddr">Your email address</label>' +
          '<input id="gsEmailAddr" type="email" inputmode="email" autocomplete="email" ' +
                 'placeholder="you@email.com" data-gs-email required>' +
          /* Unchecked, separate, and named for what it actually signs them up to.
             Nothing about it is required to receive the plan. */
          '<label class="gs-email__consent">' +
            '<input type="checkbox" data-gs-email-consent>' +
            '<span>Also send me occasional equipment deals and new arrivals from Build A Gym. ' +
              'Unsubscribe any time.</span>' +
          '</label>' +
          '<p class="gs-email__fine">Sending your plan does not sign you up for marketing. ' +
            'You can also just use <b>Review / Print Your Gym</b> without giving us an address.</p>' +
          '<div class="gs-qty__acts">' +
            '<button type="button" class="gs-qty__no" data-gs-email-cancel>Cancel</button>' +
            '<button type="submit" class="gs-qty__yes">Send my plan</button>' +
          '</div>' +
          '<p class="gs-email__msg" data-gs-email-msg role="status" aria-live="polite"></p>' +
        '</form>' +
      '</div>';
    el.qty.hidden = false;

    var form = el.qty.querySelector('[data-gs-email-form]');
    var input = el.qty.querySelector('[data-gs-email]');
    var consent = el.qty.querySelector('[data-gs-email-consent]');
    var msg = el.qty.querySelector('[data-gs-email-msg]');
    var cancel = el.qty.querySelector('[data-gs-email-cancel]');
    var submit = form ? form.querySelector('button[type="submit"]') : null;
    if (input) input.focus();

    function close() {
      el.qty.hidden = true;
      el.qty.innerHTML = '';
      document.removeEventListener('keydown', onKey);
      if (returnFocusTo && returnFocusTo.focus && document.contains(returnFocusTo)) returnFocusTo.focus();
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    if (cancel) cancel.addEventListener('click', close);

    /* Deliberately permissive. A regex that tries to enforce RFC 5322 rejects
       real addresses, and the only thing this needs to catch is a typo obvious
       enough that the send would certainly bounce. Klaviyo validates properly at
       its end. */
    function looksLikeEmail(v) {
      return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(v);
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var addr = (input.value || '').trim();

        if (!addr) { msg.className = 'gs-email__msg is-bad'; msg.textContent = 'Enter your email address.'; input.focus(); return; }
        if (!looksLikeEmail(addr)) {
          msg.className = 'gs-email__msg is-bad';
          msg.textContent = 'That does not look like an email address — check for a missing @ or a typo in the domain.';
          input.focus();
          return;
        }

        if (submit) submit.disabled = true;
        msg.className = 'gs-email__msg';
        msg.textContent = 'Sending…';

        emailMyGym(addr, { consent: !!(consent && consent.checked) })
          .then(function () {
            msg.className = 'gs-email__msg is-ok';
            msg.textContent = 'Sent. Check your inbox — the link in it reopens this layout.';
            if (submit) submit.textContent = 'Sent ✓';
            setTimeout(close, 2200);
          })
          .catch(function (err) {
            /* THE BUILD IS NOT LOST. saveState() already ran inside emailMyGym,
               so the URL holds the layout whatever happened to the request, and
               the dialog stays open with the address still in the field so they
               can simply press Send again. */
            msg.className = 'gs-email__msg is-bad';
            msg.textContent = err.message + ' Your gym is safe — the link in your address bar still opens it.';
            if (submit) submit.disabled = false;
          });
      });
    }
  }

  /* ── Report a bug ──────────────────────────────────────────────────────────
     Dillon: a button on both mobile and desktop, one message box, no other
     fields, emailed to him. The dialog is deliberately just the message —
     everything a bug report actually needs beyond that (which page, what
     room, what was on the floor, what device) is gathered automatically and
     folded into the same text, so nobody reporting a problem has to also
     stop and describe their setup.

     The send itself is a real submit of the hidden Shopify contact form
     lower in this file's markup — see the note there for why that means a
     full page round trip rather than a fetch, and where the email actually
     goes. */
  function bugContext() {
    var lines = [
      'Page: ' + window.location.href,
      'Device: ' + (window.matchMedia && window.matchMedia('(pointer: coarse)').matches ? 'Mobile/touch' : 'Desktop'),
      'Viewport: ' + window.innerWidth + ' x ' + window.innerHeight,
      'Room: ' + Math.round(STATE.w / 12) + 'ft x ' + Math.round(STATE.d / 12) + 'ft (' + STATE.preset + ')',
      'Budget: ' + money(STATE.budget),
      'Machines in gym: ' + BUILD.length
    ];
    return lines.join('\n');
  }

  function openBugDialog(returnFocusTo) {
    if (!el.qty) return;

    el.qty.innerHTML =
      '<div class="gs-qty__card gs-qty__card--bug" role="dialog" aria-modal="true" aria-labelledby="gsBugTitle">' +
        '<h2 class="gs-qty__title" id="gsBugTitle">Report a bug</h2>' +
        '<p class="gs-qty__text">Tell us what happened. The page, your room and what is on the floor are ' +
          'included automatically — you only need to describe the problem.</p>' +
        '<form class="gs-email__form" data-gs-bug-form novalidate>' +
          '<label class="gs-email__lbl" for="gsBugBody">What happened?</label>' +
          '<textarea id="gsBugBody" rows="5" data-gs-bug-input ' +
            'placeholder="What were you trying to do, and what happened instead?" required></textarea>' +
          '<div class="gs-qty__acts">' +
            '<button type="button" class="gs-qty__no" data-gs-bug-cancel>Cancel</button>' +
            '<button type="submit" class="gs-qty__yes">Send report</button>' +
          '</div>' +
          '<p class="gs-email__msg" data-gs-bug-msg role="status" aria-live="polite"></p>' +
        '</form>' +
      '</div>';
    el.qty.hidden = false;

    var form = el.qty.querySelector('[data-gs-bug-form]');
    var input = el.qty.querySelector('[data-gs-bug-input]');
    var msg = el.qty.querySelector('[data-gs-bug-msg]');
    var cancel = el.qty.querySelector('[data-gs-bug-cancel]');
    var submit = form ? form.querySelector('button[type="submit"]') : null;
    if (input) input.focus();

    function close() {
      el.qty.hidden = true;
      el.qty.innerHTML = '';
      document.removeEventListener('keydown', onKey);
      if (returnFocusTo && returnFocusTo.focus && document.contains(returnFocusTo)) returnFocusTo.focus();
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    if (cancel) cancel.addEventListener('click', close);

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var text = (input.value || '').trim();
        if (!text) { msg.className = 'gs-email__msg is-bad'; msg.textContent = 'Say a little about what happened.'; input.focus(); return; }

        if (!el.bugNativeForm || !el.bugNativeBody) {
          msg.className = 'gs-email__msg is-bad';
          msg.textContent = 'Could not send that here — email us directly instead.';
          return;
        }

        if (submit) submit.disabled = true;
        msg.className = 'gs-email__msg';
        msg.textContent = 'Sending — the page will reload once it is on its way…';

        /* saveState() first: this is a real page navigation, and the room
           must be exactly how they left it when Shopify sends the browser
           back here. */
        saveState();
        el.bugNativeBody.value = text + '\n\n---\n' + bugContext();
        el.bugNativeForm.submit();
      });
    }
  }

  /* On return from the real POST above, Shopify re-renders this page with
     `form.posted_successfully?` true for exactly this one load — see the
     hidden span carrying it in the markup. Told apart from every other
     reason this page loads by being read once, here, and nowhere else. */
  function maybeShowBugSent() {
    if (!el.bugPostedFlag) return;
    if (el.bugPostedFlag.getAttribute('data-gs-bug-posted') !== 'true') return;
    if (el.status) el.status.textContent = 'Bug report sent — thank you.';
  }

  /* ── Search and add ────────────────────────────────────────────────────────
     Runs entirely against the catalogue already in memory, so it costs nothing
     per keystroke and works the instant the page is usable.

     Ranking is deliberately dumb and predictable: a title that STARTS with what
     you typed beats one that merely contains it, which beats a type match. On a
     catalogue where thirty products share the words "BUILT Strength Foundation
     Series", anything cleverer just reshuffles near-identical rows. */
  function searchCatalog(q, limit) {
    q = String(q || '').trim().toLowerCase();
    if (q.length < 2) return [];

    var terms = q.split(/\s+/);
    var out = [];

    for (var i = 0; i < CATALOG.length; i++) {
      var p = CATALOG[i];
      if (!S.eligible(p)) continue;

      var title = String(p.t || '').toLowerCase();
      var type = String(p.ty || '').toLowerCase();
      var hay = title + ' ' + type;

      /* every word must appear somewhere */
      var all = true;
      for (var t = 0; t < terms.length; t++) {
        if (hay.indexOf(terms[t]) === -1) { all = false; break; }
      }
      if (!all) continue;

      var score = 3;
      if (title.indexOf(q) === 0) score = 0;
      else if (title.indexOf(q) > -1) score = 1;
      else if (type.indexOf(q) > -1) score = 2;

      out.push({ p: p, score: score });
    }

    out.sort(function (a, b) {
      if (a.score !== b.score) return a.score - b.score;
      return String(a.p.t).length - String(b.p.t).length;   /* shorter = more specific */
    });

    return out.slice(0, limit || 8).map(function (r) { return r.p; });
  }

  function renderResults(list) {
    if (!el.results) return;

    if (!list.length) {
      el.results.innerHTML = '<li class="gs-res gs-res--none">No machines match that.</li>';
      el.results.hidden = false;
      return;
    }

    /* Results are grouped and capped at five per type too. Searching "press"
       returns forty machines across six types; an ungrouped list of forty makes
       the shopper do the sorting we should have done. */
    el.results.innerHTML = groupedCatalogHtml(
      list.map(function (p) { return { p: p, cls: S.classify(p) }; }),
      'search'
    );
    el.results.hidden = false;
  }

  function closeResults() {
    if (el.results) { el.results.hidden = true; el.results.innerHTML = ''; }
  }

  /* ── The one add action ────────────────────────────────────────────────────
     Every path that can increase quantity goes through here — search results,
     suggestions, Browse All, the PDP seed, swap. The eight-of-a-kind guard is
     enforced at this level rather than on any one button, so a new entry point
     cannot quietly bypass it.

     Eight is a threshold, not a limit: a commercial floor genuinely orders ten
     treadmills. It only asks. */
  var QTY_PROMPT_AT = 8;

  /* Counted by VARIANT, not by product. Two dumbbell sets that differ only in
     weight range are different objects on the floor and must not pool. */
  function countOf(variantId) {
    return BUILD.filter(function (it) { return it.p.v === variantId; }).length;
  }

  /* `opts.at` = {x, y} in room inches: a deliberate drop from the shelf, rather
     than "put this somewhere sensible". Returns the new index so a drag can find
     the node it just created. */
  function addProduct(handle, opts) {
    var p = CATALOG.filter(function (x) { return x.h === handle; })[0];
    if (!p) return -1;

    opts = opts || {};

    /* The eight-of-a-kind prompt is skipped for a drop. A modal cannot open in
       the middle of a gesture, and dropping a ninth identical machine on a
       chosen spot is a stronger statement of intent than tapping Add. The tap
       path keeps the gate. `at` is forwarded through the recursion regardless,
       so this stays correct if the gate is ever reached with one. */
    if (!opts.at && !opts.confirmed && countOf(p.v) >= QTY_PROMPT_AT) {
      confirmMore(p, function () {
        addProduct(handle, { confirmed: true, from: opts.from, at: opts.at });
      }, opts.from);
      return -1;
    }

    var it = { p: p, cls: S.classify(p), fp: S.footprint(p), x: 0, y: 0, rot: 0 };
    var room = { w: STATE.w, d: STATE.d };

    /* ── FIND IT A REAL SPOT, DO NOT RE-PACK THE FLOOR ─────────────────────────
       This used to push the item in at 0,0 and re-run the shelf packer. Two
       things went wrong with that. The packer forces a row break per zone, so on
       a small floor the new piece landed past the back wall and was flagged
       "will not fit" with the room half empty. And re-packing moved machines the
       shopper had not asked to move.

       Now: search the actual free space for this one item and leave everything
       else exactly where it is. */
    /* A drop names its own spot, so no search runs at all — the shopper already
       decided where it goes, and second-guessing that is how a drag-and-drop
       tool starts feeling like it is arguing with you. */
    var spot = opts.at ? null : S.findSpot(it, BUILD, room, keepouts(), { step: 6 });

    if (opts.at) {
      var effAt = S.effFp(it);
      it.x = opts.at.x - effAt.w / 2;
      it.y = opts.at.y - effAt.d / 2;
    } else

    /* ── NO MODAL. EVER. ───────────────────────────────────────────────────────
       This used to open a dialog explaining why the machine could not be placed
       and asking whether to add it anyway. It was accurate and it was still the
       wrong design: a confirmation in front of every Add on a full floor is a
       toll gate on the main action, and Dillon's point is that parking something
       outside the room is a normal move rather than an error to approve.

       So the machine is always added. If there is a spot it goes there; if there
       is not, it is STAGED just outside the room, flagged with a small warning,
       and left for the shopper to place. Nothing is refused, nothing is
       confirmed, and nothing else on the floor moves. */
    if (spot) {
      it.x = spot.x;
      it.y = spot.y;
      it.rot = spot.rot;
    } else {
      var park = stagingSpot(it, room);
      it.x = park.x;
      it.y = park.y;
    }

    /* Pinned either way, so the next add or room change does not shelf-pack it
       somewhere the shopper did not ask for. */
    it.moved = true;
    it.mx = it.x;
    it.my = it.y;

    BUILD.push(it);
    MACHINES_ADDED++;
    LAST_ACTION = 'Added ' + cleanTitle(p.t);
    track('add');
    markOverlaps();
    SELECTED = it;
    /* Mark it as the arrival for exactly one render. When the tool chose the
       spot, the landing mark answers "where did it go" — on a 5,000 sq ft plan a
       machine is a few millimetres across. On a DROP the shopper is already
       looking at their own finger, so the mark is skipped. */
    if (!opts.at) FRESH = it;
    render();
    /* revealInPanel scrolls the side panel, which on a phone fights the scroll
       ensurePlanVisible just did to bring the floor into view. */
    if (!opts.at) revealInPanel(it);
    saveState();

    return BUILD.length - 1;

    /* NO STATUS NARRATION HERE ANY MORE. This used to announce "added just
       outside the room" / "tighter than the recommended walkway" / "added to your
       gym" on every single add. Two problems: the machine appearing on the plan
       and in the list already says it was added, and the two problem variants
       were a second voice saying the same confusing thing as the on-canvas
       badges. Placement advice now lives in one place — the "Things to look out
       for" panel — where it can be read calmly rather than caught in passing. */
  }

  /* Where a machine goes when the room has no space for it: immediately outside
     the nearest edge, in a tidy row rather than dumped on top of whatever is
     already staged there. Two machines parked out of the way must not land on the
     same square — that reads as a rendering fault rather than as staging. */
  function stagingSpot(it, room) {
    var eff = S.effFp(it);
    var gap = 12;
    var y = room.d + gap;
    var x = 0;

    /* Pack along the apron, wrapping at the room's width. Only things already
       staged below the back wall are considered, so this cannot be pushed out by
       a machine parked to the left or right. */
    var staged = BUILD.filter(function (q) {
      return (q.y || 0) >= room.d;
    }).map(function (q) {
      var e = S.effFp(q);
      return { x: q.x || 0, y: q.y || 0, w: e.w, d: e.d };
    });

    var guard = 0;
    while (guard++ < 200) {
      var hit = false;
      for (var i = 0; i < staged.length; i++) {
        var o = staged[i];
        if (x < o.x + o.w - 0.02 && x + eff.w > o.x + 0.02 &&
            y < o.y + o.d - 0.02 && y + eff.d > o.y + 0.02) { hit = true; break; }
      }
      if (!hit) break;
      x += 12;
      if (x + eff.w > room.w + STAGE_MARGIN) { x = 0; y += 24; }
    }

    return { x: x, y: y };
  }

  /* explainNoFit lived here and is deleted with the behaviour it served.
     It opened a dialog saying why a machine could not be placed and offering to
     add it outside anyway. Accurate, and still a toll gate in front of the main
     action on any full floor. Adding now always succeeds: the machine is staged
     outside the room with a small warning instead. Removed rather than left
     unreferenced so nobody wires it back up. */

  function confirmMore(p, onYes, returnFocusTo) {
    if (!el.qty) { onYes(); return; }

    el.qty.innerHTML =
      '<div class="gs-qty__card" role="dialog" aria-modal="true" aria-labelledby="gsQtyTitle">' +
        '<h2 class="gs-qty__title" id="gsQtyTitle">Add another one?</h2>' +
        '<p class="gs-qty__text">You already have ' + QTY_PROMPT_AT + ' of this machine in your gym. ' +
          'Are you sure you want to add another?</p>' +
        '<p class="gs-qty__which">' + escapeHtml(cleanTitle(p.t)) + '</p>' +
        '<div class="gs-qty__acts">' +
          '<button type="button" class="gs-qty__no" data-gs-qty-no>Cancel</button>' +
          '<button type="button" class="gs-qty__yes" data-gs-qty-yes>Add Anyway</button>' +
        '</div>' +
      '</div>';
    el.qty.hidden = false;

    var yes = el.qty.querySelector('[data-gs-qty-yes]');
    var no = el.qty.querySelector('[data-gs-qty-no]');
    if (no) no.focus();

    function close() {
      el.qty.hidden = true;
      el.qty.innerHTML = '';
      document.removeEventListener('keydown', onKey);
      /* Focus goes back where it came from, or the shopper is dumped at the top
         of the document with no idea what they just cancelled. */
      if (returnFocusTo && returnFocusTo.focus && document.contains(returnFocusTo)) returnFocusTo.focus();
    }
    function onKey(e) { if (e.key === 'Escape') close(); }

    document.addEventListener('keydown', onKey);
    if (no) no.addEventListener('click', close);
    if (yes) yes.addEventListener('click', function () { close(); onYes(); });
  }

  /* ── SCROLL POSITION SURVIVES A RE-RENDER ──────────────────────────────────
     Adding a machine re-renders the list it was added from, which replaces the
     innerHTML and resets every scrolling ancestor to the top. Someone forty rows
     into Browse All tapped Add and was thrown back to the first row, every time.
     That is most of the remaining "jumpy menus".

     Every scrollable ancestor is recorded, not just the nearest one, because
     which element actually scrolls differs between the drawer on desktop and the
     stacked layout on a phone — and on the phone it is the document.

     Restored on the next frame: the browser has to lay the replaced content out
     before scrollTop means anything, and assigning it in the same tick silently
     clamps to the old, shorter height. */
  function keepScroll(node, fn) {
    var saved = [];
    var n = node;
    while (n && n !== document.body) {
      if (n.scrollTop > 0) saved.push([n, n.scrollTop]);
      n = n.parentNode;
    }
    var page = window.pageYOffset || document.documentElement.scrollTop || 0;

    fn();

    function restore() {
      saved.forEach(function (pair) { pair[0].scrollTop = pair[1]; });
      if (page) window.scrollTo(0, page);
    }
    restore();
    /* Twice, deliberately. The first call covers the common case; the rAF covers
       a list whose images have not yet reserved their height. Cheap, and the
       alternative is an intermittent jump nobody can reproduce on demand. */
    if (window.requestAnimationFrame) window.requestAnimationFrame(restore);
  }

  /* ── The result-row stepper ────────────────────────────────────────────────
     Shared by both lists. Up goes through `addProduct`, which is the single add
     action — so the eight-of-a-kind confirmation, the placement search and the
     staging fallback all still apply exactly as they do everywhere else. There is
     no second code path that can quietly bypass them.

     Down removes the LAST instance of that exact variant. Last rather than first
     because the last one added is the one most likely to be the accident, and
     because removing the first would renumber every badge on the plan for no
     reason the shopper asked for. */
  function stepDown(handle) {
    var p = CATALOG.filter(function (x) { return x.h === handle; })[0];
    if (!p) return;
    for (var i = BUILD.length - 1; i >= 0; i--) {
      if (BUILD[i].p.v === p.v) { removeItem(i); return; }
    }
  }

  /* One delegated handler for both lists, so a new list gets the behaviour for
     free and cannot drift from the other. */
  function bindResultActions(listEl, rerender) {
    if (!listEl) return;
    listEl.addEventListener('click', function (e) {
      var feat = e.target.closest('[data-gs-feat-add]');
      if (feat) {
        e.preventDefault();
        keepScroll(listEl, function () {
          addFeature(feat.dataset.gsFeatAdd);
          rerender();
        });
        return;
      }

      var add = e.target.closest('[data-gs-add-handle]');
      var up = e.target.closest('[data-gs-qty-up]');
      var down = e.target.closest('[data-gs-qty-down]');
      if (!add && !up && !down) return;

      /* Steppers live inside the row but OUTSIDE the anchor, so they cannot
         navigate to the product page. Belt and braces anyway — a stray click that
         bubbled to the link would lose the build to a new tab. */
      e.preventDefault();

      keepScroll(listEl, function () {
        if (add) addProduct(add.dataset.gsAddHandle, { from: add });
        else if (up) addProduct(up.dataset.gsQtyUp, { from: up });
        else stepDown(down.dataset.gsQtyDown);
        rerender();
      });
    });
  }

  function bindSuggestions() {
    bindResultActions(el.suggList, renderSuggestions);
  }

  /* The room shelf reuses the SAME delegated handler as the product lists, so a
     feature row's Add and grip behave identically to a machine's and cannot
     drift apart. The group expanders come along for free. */
  function bindShelfList() {
    var listEl = root.querySelector('[data-gs-shelf]');
    if (listEl) {
      bindResultActions(listEl, renderShelf);
      listEl.addEventListener('click', function (e) {
        var more = e.target.closest('[data-gs-more]');
        if (!more) return;
        expandedGroups[more.dataset.gsMore] = !expandedGroups[more.dataset.gsMore];
        keepScroll(listEl, renderShelf);
      });
    }

    var filt = root.querySelector('[data-gs-feat-filter]');
    if (filt) {
      var t;
      filt.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () { featFilter = filt.value; renderShelf(); }, 120);
      });
    }
  }

  function bindSearch() {
    if (!el.search || !el.results) return;

    var timer = null;
    var run = function () {
      var q = el.search.value;
      if (String(q).trim().length < 2) { closeResults(); return; }
      /* Fetch wide, show narrow. The list is grouped by type and capped per
         group, so pulling only eight matches would have left most groups
         looking complete when they were merely truncated. Phones fetch
         narrower — see searchLimit(). */
      renderResults(searchCatalog(q, searchLimit()));
    };

    el.search.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, 120);
    });
    el.search.addEventListener('focus', run);

    el.search.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeResults(); el.search.blur(); return; }
      if (e.key === 'Enter') {
        e.preventDefault();
        var first = el.results.querySelector('[data-gs-add-handle]');
        if (first) first.click();
      }
    });

    /* Same shared handler as the suggestions list. The search term stays put —
       clearing it meant adding two treadmills required typing "treadmill" twice. */
    bindResultActions(el.results, function () {
      renderResults(searchCatalog(el.search.value, searchLimit()));
    });

    /* Click-away closes it. Scoped to the wrap so picking a result does not race
       the blur. Product links open in a new tab, so following one does not
       count as leaving. */
    document.addEventListener('click', function (e) {
      if (!el.search) return;
      if (e.target.closest('.gs__searchwrap')) return;
      closeResults();
    });
  }

  /* ── Export ────────────────────────────────────────────────────────────────
     Opens a purpose-built print document: the plan, every item with pricing, the
     totals, and a QR back to this exact build.

     It goes through the browser's own print-to-PDF rather than generating PDF
     bytes in JS. That is a deliberate trade: a real PDF writer means shipping a
     library and hand-embedding an SVG that carries gradients, a pattern fill and
     two photographic images, and it would still print worse than the engine
     already built into the browser. Every platform's print dialog offers Save as
     PDF, including iOS and Android. */
  /* Every shape in the plan is styled by CLASS from ctx-gym-studio.css — the
     floor, the grid, each part of every machine. The print window is a brand new
     document that loads none of that, so without it SVG falls back to its
     defaults and `fill` defaults to BLACK: the plan printed as a solid black
     rectangle with a couple of ghost machines on it.

     So the stylesheet has to travel with the markup. Read from the live
     document's own stylesheet objects (same origin, so the rules are readable)
     with a fetch of the asset as the fallback. */
  function collectPlanCss() {
    var css = [];
    var sheets = document.styleSheets;

    for (var i = 0; i < sheets.length; i++) {
      var href = sheets[i].href || '';
      if (href.indexOf('ctx-gym-studio') === -1) continue;
      var rules;
      try { rules = sheets[i].cssRules; } catch (e) { continue; }
      for (var r = 0; r < rules.length; r++) css.push(rules[r].cssText);
    }

    if (css.length) return Promise.resolve(css.join('\n'));

    /* Matches both the annotated and the .min stylesheet name — the section
       serves the .min one, and pinning the exact filename here is how the
       print export goes black the day the asset is renamed. */
    var link = document.querySelector('link[href*="ctx-gym-studio"][href*=".css"]');
    if (!link) return Promise.resolve('');
    return fetch(link.href).then(function (r) { return r.ok ? r.text() : ''; }).catch(function () { return ''; });
  }

  /* The QR pair (~60KB) used to load on every page view for a feature a small
     minority uses. Injected on the first export click instead; the URLs are
     published by the section as CTX_QR_SRC. Failure is non-fatal — the export
     ships without its QR, exactly as it always did when CTXQR was absent. */
  var qrLoading = null;

  function ensureQr() {
    if (window.CTXQR && window.CTXQR.svg) return Promise.resolve();
    if (qrLoading) return qrLoading;
    var srcs = window.CTX_QR_SRC || [];
    /* Sequential, not parallel: ctx-qr.js wraps the library, so it must see
       ctx-qr-lib.js already evaluated. */
    qrLoading = srcs.reduce(function (chain, src) {
      return chain.then(function () {
        return new Promise(function (res, rej) {
          var s = document.createElement('script');
          s.src = src;
          s.onload = res;
          s.onerror = rej;
          document.head.appendChild(s);
        });
      });
    }, Promise.resolve()).catch(function () { qrLoading = null; });
    return qrLoading;
  }

  function exportPlan() {
    ensureQr().then(exportPlanNow);
  }

  function exportPlanNow() {
    saveState();

    var live = el.plan.querySelector('svg');
    if (!live) return;

    collectPlanCss().then(function (planCss) { buildExport(live, planCss); });
  }

  function buildExport(live, planCss) {

    /* Protocol-relative asset URLs resolve against the document, and the print
       window has no scheme of its own — they must be absolute or the art and the
       watermark silently vanish from the export. */
    var markup = live.outerHTML.replace(/(href=")\/\//g, '$1https://');

    var items = BUILD.concat(TRAY);
    var sub = subtotal();
    var tier = S.tierFor(sub);
    var save = Math.round(sub * tier.pct / 100);
    var resumeUrl = location.href;

    /* If the build is somehow still too long to encode, fall back to a QR of the
       builder itself rather than printing nothing. A scan that lands them on the
       builder with their saved gym in localStorage is worth far more than a blank
       square, and the caption changes to match so the page never over-promises. */
    var qr = '', qrNote = 'Returns you to this exact gym, ready to add to cart.';
    if (window.CTXQR && window.CTXQR.svg) {
      qr = window.CTXQR.svg(resumeUrl, 150);
      if (!qr) {
        qr = window.CTXQR.svg(location.origin + location.pathname, 150);
        qrNote = 'Opens the builder. This build is saved in the browser you exported from.';
      }
    }

    var rows = items.map(function (it, i) {
      var fp = it.fp;
      /* Out-of-room items are LISTED, priced and flagged — never quietly dropped
         from the breakdown. Somebody who prints a plan with three machines staged
         outside needs the printout to say so, because the printout is what they
         take to the room with a tape measure. */
      /* Marked, not shouted. The row says where the machine is drawn; the notes
         section below the table is where it gets explained in a sentence. The old
         wording was the same two confusing labels the canvas used. */
      var est = STATES[i] || 'ok';
      var mark = est === 'outside' ? ' <b class="warn">drawn outside the room</b>'
               : est === 'collision' ? ' <b class="warn">drawn over another piece</b>' : '';
      return '<tr' + (est === 'outside' || est === 'collision' ? ' class="flagged"' : '') + '>' +
        '<td class="n">' + (i + 1) + '</td>' +
        '<td>' + escapeHtml(cleanTitle(it.p.t)) + mark +
          '<span class="sub">' + escapeHtml(S.CLASSES[it.cls].label) +
          ' · ' + Math.round(fp.w) + '&quot; × ' + Math.round(fp.d) + '&quot;' +
          (fp.est ? ' · size estimated' : '') + '</span></td>' +
        '<td>' + (it.p.cn === 'reman' ? 'Remanufactured' : 'New') + '</td>' +
        '<td>' + escapeHtml(it.p.ld || '—') + '</td>' +
        '<td class="r">' + money(it.p.p) + '</td>' +
      '</tr>';
    }).join('');

    var cov = S.coverage(BUILD).map(function (a) {
      return '<li><b>' + a.label + '</b> ' + (a.pct >= 100 ? 'covered' : (a.n ? 'light' : 'not covered')) + '</li>';
    }).join('');

    /* ── THINGS TO LOOK OUT FOR, ON PAPER ──────────────────────────────────────
       The same advice the panel under the plan carries, printed with the plan.
       Staging something outside the room is fine while editing; it is not fine to
       let somebody print a plan, walk into their garage with a tape measure and
       only then discover it.

       Tone differs from the screen version on purpose. This sheet gets handed to
       an installer or a delivery crew, so it says what to check rather than what
       to tap, and it opens by stating plainly that the layout is the customer's
       to approve. It never blocks printing, removes anything or repositions
       anything. */
    var advice = adviceReport();

    var placeWarn = '';
    if (advice.length) {
      placeWarn =
        '<div class="pwarn">' +
          '<b>Things to look out for</b> ' +
          'This layout has been drawn to scale from the dimensions we hold. ' +
          'Nothing below stops the gym working &mdash; they are the points worth ' +
          'checking against the real room before anything ships.' +
          '<ul>' +
            advice.map(function (a) {
              var names = (a.items || []).map(function (i) { return (i + 1); }).join(', ');
              return '<li><b>' + a.head + '</b>' +
                     (names ? ' <span class="pwarn__n">(item' +
                       ((a.items.length === 1) ? ' ' : 's ') + names + ')</span>' : '') +
                     '<br>' + a.body + '</li>';
            }).join('') +
          '</ul>' +
        '</div>';
    }

    var doc =
'<!doctype html><html><head><meta charset="utf-8"><title>My gym plan — Build A Gym</title><style>' +
/* The plan's own stylesheet, carried across. Without this every SVG shape
   falls back to a black fill. */
planCss +
/* THE NUMBERS MUST COME BACK FOR PRINT. On screen .gs-badge is hidden unless the
   machine is selected or hovered, because forty numbered pips over a drawing is
   clutter. On paper the opposite is true: the numbers are the only thing tying
   each shape to its row in the equipment list below, and nobody can hover a sheet
   of paper. Declared after planCss so it wins on cascade order. */
'\n.gs-badge{opacity:1 !important}' +
'\n.gs-wm{opacity:.085}' +        /* watermark a touch stronger on the copy that leaves the building */
'@page{size:letter;margin:12mm}' +
'*{box-sizing:border-box}' +
'body{margin:0;font:12px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif;color:#0c111d}' +
'.hd{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;border-bottom:2px solid #0c111d;padding-bottom:8px;margin-bottom:14px}' +
'.hd h1{font-size:19px;margin:0 0 2px;letter-spacing:-.01em}' +
'.hd .meta{font-size:11px;color:#5b6472}' +
'.hd img{height:26px;width:auto}' +
'.hd .brand{text-align:right;font-weight:800;font-size:15px;letter-spacing:.06em;line-height:1.15}' +
'.hd .brand span{display:block;font-size:9px;font-weight:700;letter-spacing:.14em;color:#6b7280}' +
'.plan{border:1px solid #d9d7d2;border-radius:6px;padding:8px;margin-bottom:14px;text-align:center;break-inside:avoid}' +
'.plan svg{width:100%;height:auto;max-height:118mm}' +
'.cols{display:flex;gap:16px;align-items:flex-start}' +
'table{width:100%;border-collapse:collapse;font-size:11px}' +
'th{text-align:left;font-size:9.5px;text-transform:uppercase;letter-spacing:.06em;color:#6b7280;border-bottom:1px solid #d9d7d2;padding:0 6px 4px}' +
'td{padding:5px 6px;border-bottom:1px solid #f0eeea;vertical-align:top}' +
'td.n{color:#9ca3af;width:18px}td.r{text-align:right;font-weight:700;white-space:nowrap}' +
'.sub{display:block;color:#6b7280;font-size:10px}' +
'.tot{margin-top:8px;margin-left:auto;width:250px;font-size:12px}' +
'.tot div{display:flex;justify-content:space-between;padding:3px 6px}' +
'.tot .save{color:#16a34a;font-weight:700}' +
'.tot .grand{border-top:1px solid #0c111d;margin-top:3px;padding-top:6px;font-size:15px;font-weight:800}' +
'.side{width:170px;flex:none}' +
'.side h3{font-size:9.5px;text-transform:uppercase;letter-spacing:.06em;color:#6b7280;margin:0 0 5px}' +
'.side ul{list-style:none;margin:0 0 12px;padding:0;font-size:10.5px}' +
'.side li{padding:1px 0}' +
'.qr{border:1px solid #d9d7d2;border-radius:6px;padding:8px;text-align:center}' +
'.qr svg{width:100%;height:auto;max-width:130px}' +
'.qr p{margin:5px 0 0;font-size:9.5px;color:#5b6472;line-height:1.35}' +
'.fine{margin-top:12px;font-size:9.5px;color:#6b7280;border-top:1px solid #d9d7d2;padding-top:7px}' +
/* The unresolved-placement notice. Amber rather than red: it is a job still to
   do, not a fault, and it must survive printing in greyscale — hence the left
   bar and the bold lead-in carrying the emphasis rather than colour alone. */
/* Neutral, like the on-screen panel. The amber it used to wear was correct for
   "warning" and wrong for "worth checking" — and on a sheet that gets handed to
   an installer, a yellow alert box reads as a fault report on the customer's
   own drawing. */
'.pwarn{border:1px solid #d9d7d2;border-left:4px solid #0c111d;background:#fbfcfd;' +
  'border-radius:5px;padding:9px 11px;margin-bottom:12px;font-size:11px;line-height:1.5;color:#2f3948;break-inside:avoid}' +
'.pwarn>b{color:#0c111d}' +
'.pwarn ul{margin:6px 0 0;padding-left:18px}' +
'.pwarn li{margin:5px 0}' +
'.pwarn__n{color:#6b7280;font-weight:600}' +
'tr.flagged td{background:#fffbeb}' +
'.warn{color:#92400e;font-weight:700;white-space:nowrap}' +
'@media print{.noprint{display:none}}' +
'.noprint{position:fixed;top:10px;right:10px;z-index:9;display:flex;gap:8px}' +
'.noprint button{font:600 13px sans-serif;padding:9px 14px;border:0;border-radius:8px;background:#e4002b;color:#fff;cursor:pointer}' +
'.noprint button.back{background:#fff;color:#0c111d;border:1px solid #0c111d}' +
'</style></head><body>' +
'<div class="noprint">' +
  '<button onclick="window.print()">Save as PDF</button>' +
  '<button class="back" onclick="window.close()">Back to editing</button>' +
'</div>' +
'<div class="hd"><div><h1>Your gym plan</h1><div class="meta">' +
  ft(STATE.w) + ' × ' + ft(STATE.d) + ' &middot; ' + items.length + ' pieces &middot; ' +
  Math.round(S.roomArea({ w: STATE.w, d: STATE.d })) + ' sq ft room</div></div>' +
  /* THE LOGO IS DELIBERATELY TEXT HERE.

     The only lockup in the theme (bagx-lockup.png) is white-on-transparent, and
     every logo in the theme settings is a white variant too. Printed on this
     white header it rendered as nothing but the red "Gym" tag floating on its
     own. A dark-on-light lockup has been requested; until it arrives, a plain
     wordmark is legible and honest, and swapping it back is one line. */
  '<div class="brand">BUILD A GYM<span>&times; BUILT STRENGTH</span></div>' +
'</div>' +
placeWarn +
'<div class="plan">' + markup + '</div>' +
'<div class="cols"><div style="flex:1;min-width:0">' +
'<table><thead><tr><th></th><th>Equipment</th><th>Condition</th><th>Lead time</th><th class="r">Price</th></tr></thead><tbody>' +
rows +
'</tbody></table>' +
'<div class="tot">' +
  '<div><span>Equipment</span><span>' + money(sub) + '</span></div>' +
  '<div class="save"><span>Bundle discount ' + tier.pct + '%</span><span>&minus;' + money(save) + '</span></div>' +
  '<div class="grand"><span>Total</span><span>' + money(sub - save) + '</span></div>' +
'</div></div>' +
'<div class="side"><h3>What it trains</h3><ul>' + cov + '</ul>' +
  (qr ? '<div class="qr">' + qr + '<p><b>Scan to reopen this build</b><br>' + qrNote + '</p></div>' : '') +
'</div></div>' +
'<p class="fine"><b>Shipping is not included.</b> Freight is calculated at checkout from your ZIP code, and inside delivery with assembly is quoted on the room rather than per machine. ' +
'Footprints marked <i>size estimated</i> are derived from the product&rsquo;s listed floor area. Lead times are current at the time of export. ' +
'Layout &copy; Build A Gym.</p>' +
'</body></html>';

    var win = window.open('', '_blank');
    if (!win) {
      if (el.status) el.status.textContent = 'Allow pop-ups to export your plan.';
      return;
    }
    win.document.open();
    win.document.write(doc);
    win.document.close();

    /* Let the art and the watermark decode before the print dialog freezes the
       page — printing a half-loaded image is the classic version of this bug. */
    var go = function () { try { win.focus(); win.print(); } catch (e) {} };
    if (win.document.readyState === 'complete') setTimeout(go, 700);
    else win.addEventListener('load', function () { setTimeout(go, 500); });
  }

  /* ── Save + resume ─────────────────────────────────────────────────────── */

  /* ── State encoding ────────────────────────────────────────────────────────
     COMPACT ON PURPOSE, and the reason is the QR code.

     The first version stored base64 JSON with each item as
     [handle, x, y, rot]. Correct, and unusable: a 51-machine commercial build
     produced a 4,233-character URL. QR tops out near 2,950 bytes even at the
     most permissive error-correction level, so the export silently shipped with
     no QR at all — the failure I only found by counting modules in the output.

     So: variant ids in base 36 instead of handles, and COORDINATES ONLY FOR
     MACHINES THE SHOPPER ACTUALLY MOVED. Everything auto-arranged is re-laid by
     the packer on open, which is deterministic and gives the identical result.
     The same 51-machine build now encodes in a few hundred characters.

       #g=<w>_<d>_<budget/100>_<goal>_<preset>~<item>-<item>-...
       item = <variantId36>[@<x>_<y>][!<rot/90>]

     The legacy `#gym=` links still open — someone may have saved or emailed one. */
  var GOALS = ['general', 'strength', 'muscle', 'cardio', 'commercial'];

  function encodeState() {
    var head = [
      Math.round(STATE.w), Math.round(STATE.d), Math.round(STATE.budget / 100),
      Math.max(0, GOALS.indexOf(STATE.goal)), STATE.preset,
      STATE.floor === 'rubber' ? 'r' : 'g',
      STATE.showClear === false ? '0' : '1'
    ].join('_');

    var items = BUILD.map(function (it) {
      var s = Number(it.p.v).toString(36);
      if (it.moved) s += '@' + Math.round(it.x) + '_' + Math.round(it.y);
      if (it.rot) s += '!' + (it.rot / 90);
      return s;
    }).join('-');

    /* Room features ride after a second `~`, so a link saved before they existed
       simply has no third segment and still opens. */
    var feats = FEATURES.map(function (f) {
      return [f.k, Math.round(f.x), Math.round(f.y), f.rot || 0, f.hinge || 1].join('.');
    }).join('-');

    return head + '~' + items + (feats ? '~' + feats : '');
  }

  function decodeCompact(str) {
    var parts = String(str).split('~');
    var head = parts[0].split('_');
    if (head.length < 4) return false;

    STATE.w = parseInt(head[0], 10) || STATE.w;
    STATE.d = parseInt(head[1], 10) || STATE.d;
    STATE.budget = (parseInt(head[2], 10) || 0) * 100;
    STATE.goal = GOALS[parseInt(head[3], 10)] || 'general';
    STATE.preset = head[4] || 'custom';
    STATE.floor = head[5] === 'r' ? 'rubber' : 'grid';
    STATE.showClear = head[6] !== '0';   /* absent on older links = on */

    FEATURES = (parts[2] ? parts[2].split('-') : []).map(function (tok) {
      var b = tok.split('.');
      var def = FEATDEF[b[0]];
      if (!def) return null;
      return {
        k: b[0], w: def.w, d: def.d,
        x: parseInt(b[1], 10) || 0, y: parseInt(b[2], 10) || 0,
        rot: parseInt(b[3], 10) || 0, hinge: parseInt(b[4], 10) || 1
      };
    }).filter(Boolean);

    var byVariant = {};
    CATALOG.forEach(function (p) { byVariant[String(p.v)] = p; });

    var dropped = 0;
    BUILD = (parts[1] ? parts[1].split('-') : []).map(function (tok) {
      var rot = 0, x = null, y = null;
      var bang = tok.indexOf('!');
      if (bang > -1) { rot = (parseInt(tok.slice(bang + 1), 10) || 0) * 90; tok = tok.slice(0, bang); }
      var at = tok.indexOf('@');
      if (at > -1) {
        var xy = tok.slice(at + 1).split('_');
        x = parseInt(xy[0], 10); y = parseInt(xy[1], 10);
        tok = tok.slice(0, at);
      }
      var p = byVariant[String(parseInt(tok, 36))];
      if (!p) { dropped++; return null; }
      var it = { p: p, cls: S.classify(p), fp: S.footprint(p), x: 0, y: 0, rot: rot };
      if (x !== null && !isNaN(x)) { it.moved = true; it.mx = x; it.my = y; }
      return it;
    }).filter(Boolean);

    /* A machine that sold out since the link was saved used to just vanish —
       an emailed $130K build could reopen at $118K and a worse discount tier
       with no explanation anywhere. The gym still opens; it also says what is
       different, because a silently smaller gym reads as a bug or a bait. */
    if (dropped && el.status) {
      el.status.textContent = dropped === 1
        ? 'One machine from this saved gym is no longer available and was removed. Prices and discount reflect what is still here.'
        : dropped + ' machines from this saved gym are no longer available and were removed. Prices and discount reflect what is still here.';
    }

    if (!BUILD.length) return false;

    /* Re-lay everything, THEN put the moved machines back. Order matters: the
       packer assigns x/y to every item it touches, so restoring the manual
       coordinates first would just have them overwritten. */
    S.arrange(BUILD, { w: STATE.w, d: STATE.d });
    BUILD.forEach(function (it) {
      if (it.moved) { it.x = it.mx; it.y = it.my; }
    });
    return true;
  }

  function saveState() {
    try {
      var enc = encodeState();
      history.replaceState(null, '', location.pathname + location.search + '#g=' + enc);
      localStorage.setItem('ctxGymStudio.last', enc);
    } catch (e) {}
  }

  function restoreState() {
    var enc = '';
    var legacy = false;

    if (location.hash.indexOf('#g=') === 0) enc = location.hash.slice(3);
    else if (location.hash.indexOf('#gym=') === 0) { enc = location.hash.slice(5); legacy = true; }

    if (!enc) {
      try { enc = localStorage.getItem('ctxGymStudio.last') || ''; } catch (e) {}
      /* Stored links from before the compact format are base64 JSON, which never
         contains ~ or _ in that position. */
      if (enc && enc.indexOf('~') === -1) legacy = true;
    }
    if (!enc) return false;

    if (!legacy) {
      try { return decodeCompact(enc); } catch (e) { return false; }
    }

    try {
      var payload = JSON.parse(decodeURIComponent(escape(atob(enc))));
      STATE.w = payload.w; STATE.d = payload.d;
      STATE.budget = payload.b; STATE.goal = payload.g; STATE.preset = payload.p || 'custom';

      var byHandle = {};
      CATALOG.forEach(function (p) { byHandle[p.h] = p; });

      BUILD = (payload.items || []).map(function (row) {
        var p = byHandle[row[0]];
        if (!p) return null;
        /* row[3] is absent in links saved before rotation existed — an old
           shared link must still open, so it defaults to unrotated. */
        return { p: p, cls: S.classify(p), fp: S.footprint(p), x: row[1], y: row[2], rot: row[3] || 0 };
      }).filter(Boolean);

      return BUILD.length > 0;
    } catch (e) { return false; }
  }

  /* ── PDP handoff ───────────────────────────────────────────────────────────
     Dillon: "they'll enter from the pdp more than likely." Arriving with
     ?seed=<handle> starts the build around that machine, so the product they
     were already looking at is the one their gym is built around. */
  function seedHandle() {
    var m = location.search.match(/[?&]seed=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  /* ── Controls ──────────────────────────────────────────────────────────── */

  function bindControls() {
    if (el.preset) {
      el.preset.addEventListener('change', function () {
        var next = el.preset.value;
        var p = PRESETS[next];
        if (!p) return;

        /* Room type carries the budget ceiling with it. Moving from a
           commercial floor to a spare bedroom must bring a $180,000 budget down
           to the residential $75,000 — but only the number changes; the gym
           itself is never touched by a room-type change. */
        proposeRoom(
          next === 'custom' ? STATE.w : p.w,
          next === 'custom' ? STATE.d : p.d,
          next
        );
      });
    }

    [el.wIn, el.dIn].forEach(function (input) {
      if (!input) return;
      input.addEventListener('change', function () {
        var ft = Math.max(6, Math.min(120, parseFloat(input.value) || 10));
        input.value = ft;
        proposeRoom(
          input === el.wIn ? ft * 12 : STATE.w,
          input === el.dIn ? ft * 12 : STATE.d,
          'custom'
        );
      });
    });

    /* THE STEP ARROWS. Dillon: "put up and down clickable arrows on the
       dimensions so you can click up and down instead of typing."

       A click only ever moves the input's own value and fires the input's
       own `change` event — never proposeRoom() directly — so typing and
       clicking run through the exact same clamp and the exact same
       "does the current build still fit" check above. Two ways in, one
       path through. */
    root.querySelectorAll('[data-gs-dim-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = btn.dataset.gsDimStep === 'w' ? el.wIn : el.dIn;
        var dir = parseInt(btn.dataset.gsDimDir, 10) || 0;
        if (!input) return;
        var ft = Math.max(6, Math.min(120, (parseFloat(input.value) || 10) + dir));
        if (ft === parseFloat(input.value)) return;
        input.value = ft;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    if (el.budget) {
      /* Dragging only previews. The value is not committed until release,
         because committing on every input event would open the over-budget
         resolution panel mid-drag, on a number the shopper is still passing
         through on their way somewhere else. */
      el.budget.addEventListener('input', function () {
        showBudgetPreview(S.budgetForPos(sliderPos(), STATE.preset));
      });

      /* Budget shapes what gets RECOMMENDED. It must not touch what is already
         placed — a shopper nudging the slider to see what else they could afford
         should not lose the gym they just built. Lowering it below what is on
         the floor opens the resolution panel rather than silently deleting
         machines or leaving the build invisibly over. */
      el.budget.addEventListener('change', function () {
        commitBudget(S.budgetForPos(sliderPos(), STATE.preset));
      });
    }

    if (el.goal) {
      el.goal.addEventListener('change', function () { STATE.goal = el.goal.value; renderMoney(); saveState(); });
    }

    if (el.addBtn) el.addBtn.addEventListener('click', addToCart);
    if (el.pdfBtn) el.pdfBtn.addEventListener('click', exportPlan);

    /* The guard rail's button releases whichever constraint it just named. A
       message that only diagnoses leaves them hunting for the slider. */
    if (el.guard) {
      el.guard.addEventListener('click', function (e) {
        var b = e.target.closest('[data-gs-guard]');
        if (!b) return;
        if (b.dataset.gsGuard === 'room') {
          STATE.w += 48; STATE.d += 48;
          STATE.preset = 'custom';
          if (el.preset) el.preset.value = 'custom';
          if (el.wIn) el.wIn.value = Math.round(STATE.w / 12);
          if (el.dIn) el.dIn.value = Math.round(STATE.d / 12);
        } else {
          /* Raising the budget is always safe and never opens the resolution
             panel — more money cannot invalidate a build. Clamped so the guard
             rail cannot walk a residential room past its own ceiling. */
          STATE.budget = S.clampBudget(STATE.budget + 500000, STATE.preset);
          syncBudgetSlider();
        }
        GUARD_COUNT++;
        LAST_ACTION = b.dataset.gsGuard === 'room' ? 'Expanded room from guard rail' : 'Raised budget from guard rail';
        track('guard');
        relayout();
      });
    }

    if (el.helpBtn && el.help) {
      el.helpBtn.addEventListener('click', function () { el.help.hidden = !el.help.hidden; });
      el.help.addEventListener('click', function (e) {
        if (e.target.closest('[data-gs-help-close]')) el.help.hidden = true;
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !el.help.hidden) el.help.hidden = true;
      });
    }

    if (el.prefsBtn) el.prefsBtn.addEventListener('click', openPreferences);
    if (el.startOverBtn) {
      el.startOverBtn.addEventListener('click', function () {
        lockStage(true);
        renderStartOver();
      });
    }

    if (el.clearBtns) {
      el.clearBtns.addEventListener('click', function (e) {
        var b = e.target.closest('[data-gs-clear]');
        if (!b) return;
        STATE.showClear = b.dataset.gsClear === 'on';
        syncToggles();
        render();
        saveState();
      });
    }

    if (el.floorBtns) {
      el.floorBtns.addEventListener('click', function (e) {
        var b = e.target.closest('[data-gs-floor]');
        if (!b) return;
        STATE.floor = b.dataset.gsFloor;
        syncToggles();
        render();
        saveState();
      });
    }

    /* The feature-bar handlers went with the accordion they were bound to — the
       grouped <select>, its Add button, its preview and its Remove button. All
       of that is the Room shelf's job now, through the same delegated handler
       the product lists use. Removing a selected feature is still the trash
       button in the on-plan handle cluster, which is where removing anything
       else already lives. */

    /* R rotates the selection — the convention in every layout tool, and far
       faster than reaching for the handle when arranging a whole room. Ignored
       while typing so it cannot fire from the email or size fields. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'r' && e.key !== 'R') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (!SELECTED) return;
      var idx = BUILD.indexOf(SELECTED);
      if (idx < 0) return;
      e.preventDefault();
      rotateItem(idx);
    });

    if (el.shareBtn) {
      el.shareBtn.addEventListener('click', function () {
        saveState();
        var url = location.href;
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () {
            el.shareBtn.textContent = 'Link copied ✓';
            setTimeout(function () { el.shareBtn.textContent = 'Save my gym'; }, 2000);
          });
        }
      });
    }

    var emailOpen = root.querySelector('[data-gs-email-open]');
    if (emailOpen) emailOpen.addEventListener('click', function () { openEmailDialog(emailOpen); });

    if (el.bugBtn) el.bugBtn.addEventListener('click', function () { openBugDialog(el.bugBtn); });

    /* Tray actions, delegated so re-renders never orphan a handler. */
    el.tray.addEventListener('click', function (e) {
      if (e.target.closest('[data-gs-suggest]')) { rebuild(false); return; }

      var rm = e.target.closest('[data-gs-remove]');
      if (rm) {
        /* Same path as the on-plan trash button — one delete behaviour, one
           undo, wherever it is triggered from. It also stops going through
           rebuild(), which re-laid the whole floor and threw away every manual
           placement as a side effect of removing one machine. */
        removeItem(parseInt(rm.dataset.gsRemove, 10));
        return;
      }
      var sw = e.target.closest('[data-gs-swap-btn]');
      if (sw) { openSwap(parseInt(sw.dataset.gsSwapBtn, 10)); return; }

      /* THE TITLE LINK MUST NOT ALSO SELECT. The catch-all below claims any
         click inside a row that no earlier branch returned on, so without this
         bail, following the product link would ALSO re-select the machine and
         re-render the whole plan behind the tab that is opening. Harmless-
         looking, but it is a full plan rebuild fired on a navigation. */
      if (e.target.closest('a[href]')) return;

      var row = e.target.closest('.gs-row');
      if (row) { SELECTED = BUILD[parseInt(row.dataset.idx, 10)]; render(); }
    });

    el.swap.addEventListener('click', function (e) {
      if (e.target.closest('[data-gs-swap-close]')) { el.swap.hidden = true; return; }
      var pick = e.target.closest('[data-gs-swap-pick]');
      if (!pick) return;

      var idx = parseInt(pick.dataset.gsSwapPick, 10);
      var altIdx = parseInt(pick.dataset.alt, 10);
      var handles = JSON.parse(el.swap.dataset.alts || '[]');
      var handle = handles[altIdx];
      var p = CATALOG.filter(function (x) { return x.h === handle; })[0];
      if (!p || !BUILD[idx]) return;

      /* Swap in place — same spot on the floor, so the room does not reshuffle
         under them and lose the layout they just arranged. */
      var old = BUILD[idx];
      BUILD[idx] = { p: p, cls: S.classify(p), fp: S.footprint(p), x: old.x, y: old.y, rot: old.rot || 0 };
      SELECTED = BUILD[idx];
      el.swap.hidden = true;
      markOverlaps();
      render();
      saveState();
    });
  }

  /* ── Popup suppression, this page only ─────────────────────────────────────
     The Alia discount popup renders full-screen at z-index 2147483647 — the
     32-bit maximum — so nothing can be layered above it. Measured with
     elementFromPoint: it was covering two of the six quiz answers, which is why
     the buttons "did nothing".

     This is a STOPGAP and it is deliberately narrow: it only runs inside the
     builder section, so it cannot affect the popup anywhere else on the site.
     The proper fix is excluding this URL in Alia's own page targeting, because
     doing it there also keeps Alia's impression stats honest — suppressing the
     node here may still let it count a view it never really got.

     Removing rather than hiding: `display:none` leaves a live, focusable subtree
     that can still steal the keyboard, and Alia re-shows on its own timer. */
  function suppressPopups() {
    if (root.getAttribute('data-suppress-popups') !== 'on') return;

    var SEL = '[id^="alia-root-"], [id^="alia-popup-root-"]';

    function sweep() {
      var found = document.querySelectorAll(SEL);
      Array.prototype.forEach.call(found, function (n) {
        if (n.parentNode) n.parentNode.removeChild(n);
      });
    }

    sweep();
    /* It mounts on a timer after page load, and again after a dismissal, so a
       one-shot removal is not enough. */
    var obs = new MutationObserver(sweep);
    obs.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(sweep, 1500);
    setTimeout(sweep, 5000);
  }

  /* ── The View sheet (narrow screens only) ──────────────────────────────────
     Moves the real floor-finish, clearance and help controls between the bar and
     the sheet as the viewport crosses the breakpoint. MOVED, not duplicated: a
     second copy of a segmented toggle is a second thing to keep in sync, and
     `syncToggles` would only ever have found one of them. */
  function bindViewSheet() {
    var btn = root.querySelector('[data-gs-view-open]');
    var sheet = root.querySelector('[data-gs-view-sheet]');
    var body = root.querySelector('[data-gs-view-body]');
    var main = root.querySelector('[data-gs-plantop-main]');
    if (!btn || !sheet || !body || !main) return;

    var mq = window.matchMedia('(max-width: 749px)');
    var inSheet = false;

    function closeSheet() {
      sheet.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
    }

    function apply() {
      if (mq.matches && !inSheet) {
        while (main.firstChild) body.appendChild(main.firstChild);
        inSheet = true;
        btn.hidden = false;
        closeSheet();
      } else if (!mq.matches && inSheet) {
        while (body.firstChild) main.appendChild(body.firstChild);
        inSheet = false;
        btn.hidden = true;
        closeSheet();
      }
    }

    btn.addEventListener('click', function () {
      var open = sheet.hidden;
      sheet.hidden = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    var close = root.querySelector('[data-gs-view-close]');
    if (close) close.addEventListener('click', closeSheet);

    /* Picking a floor or a clearance mode is a complete action, so the sheet
       gets out of the way rather than waiting to be dismissed. The help panel is
       the exception — it opens its own panel below, and closing the sheet first
       is what makes that panel visible. */
    body.addEventListener('click', function (e) {
      if (e.target.closest('[data-gs-floor], [data-gs-clear], [data-gs-help-open]')) closeSheet();
    });

    /* addListener is the old name; Safari before 14 has only that one, and this
       store's traffic includes it. */
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
    apply();
  }

  function bindClearGym() {
    var b = root.querySelector('[data-gs-clear-floor]');
    if (!b) return;
    b.addEventListener('click', function () { confirmClearGym(b); });
    syncClearGym();
  }

  /* ══ SHELF DRAG-AND-DROP: REMOVED ═══════════════════════════════════════
     A place-then-drag mechanism used to live here: dragging a row grip added
     the item under the pointer and handed the gesture to the machine drag.

     It worked on a mouse. On a phone it did not, and Dillon called it:
     dragging from the library to the gym floor "just does not work on mobile,
     let us delete that and any associated buttons".

     Why it was never going to work there: on a phone the shelf is page
     content BELOW the plan, so the shopper cannot see the row and the floor
     at the same time on a 375px screen. The drag had nowhere to go, and no
     amount of gesture plumbing fixes that — the mechanism was never the hard
     part.

     Tap-to-add does the same job in one tap and always worked. Positioning is
     then a drag ON THE PLAN, where the thing being moved and the space it is
     moving into are both on screen. See the touch drag in bindPlan.

     DO NOT REBUILD THIS without solving the two-things-on-screen-at-once
     problem first. */


  /* The advice panel: one delegated handler on the shell, which is never
     replaced, so this binds once. A chip selects the machine it names and brings
     it into view — the panel says "the leg press" and the shopper needs to know
     WHICH shape that is on a plan of forty. Selecting also draws that machine's
     walkway halo and its number badge, so the click has a visible payoff.

     Nothing here moves anything. The panel is advice; the shopper does the
     moving. */
  function bindAdvice() {
    if (!el.advice) return;

    el.advice.addEventListener('toggle', function () {
      ADVICE_OPEN = el.advice.open;
    });

    el.advice.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-gs-adv-item]');
      if (!chip) return;
      var it = BUILD[parseInt(chip.dataset.gsAdvItem, 10)];
      if (!it) return;
      SELECTED = it;
      SELECTED_FEAT = null;
      render();
      scrollItemIntoView(it);
    });
  }

  /* Bring a machine into the middle of the plan viewport. Only scrolls the
     viewport, never the page — a chip tap that jumped the whole document would
     take the panel you just tapped off screen. */
  function scrollItemIntoView(it) {
    if (!el.viewport || !el.plan) return;
    var node = el.plan.querySelector('.gs-item[data-idx="' + BUILD.indexOf(it) + '"]');
    if (!node || !node.getBoundingClientRect) return;
    var nr = node.getBoundingClientRect();
    var vr = el.viewport.getBoundingClientRect();
    if (nr.left >= vr.left && nr.right <= vr.right && nr.top >= vr.top && nr.bottom <= vr.bottom) return;
    el.viewport.scrollLeft += (nr.left + nr.width / 2) - (vr.left + vr.width / 2);
    el.viewport.scrollTop += (nr.top + nr.height / 2) - (vr.top + vr.height / 2);
  }

  /* Show in cart — the control bar's cart action. Same sync-then-open path as
     the money panel button; hidden while there is nothing to show. */
  function bindCartSync() {
    var b = root.querySelector('[data-gs-cart-sync]');
    if (!b) return;
    b.addEventListener('click', function () { addToCart(); });
  }

  function syncCartSyncBtn() {
    var b = root.querySelector('[data-gs-cart-sync]');
    if (b) b.hidden = !BUILD.length;
  }

  /* ── Boot ──────────────────────────────────────────────────────────────── */

  /* Verify the art assets alongside the catalogue fetch — both are network work
     and there is no reason to do them in series. A failed probe just means that
     machine draws as vector. */
  Promise.all([
    S.loadCatalog(),
    window.CTXGymArt ? window.CTXGymArt.verify() : Promise.resolve({})
  ])
    .then(function (both) {
      var cat = both[0];
      CATALOG = cat;
      if (el.status) el.status.textContent = '';
      maybeShowBugSent();
      if (el.shopAllCount) {
        el.shopAllCount.textContent = CATALOG.filter(function (p) { return S.eligible(p); }).length + ' machines';
      }

      bindControls();
      bindGate();
      bindSearch();
      bindTabs();
      bindSuggestions();
      bindShopAll();
      bindHandles();
      bindDragPad();
      bindZoom();
      bindResolve();
      bindDeselect();
      bindViewSheet();
      bindClearGym();
      bindCartSync();
      bindAdvice();
      bindShelfList();
      if (el.hintOk) el.hintOk.addEventListener('click', dismissGestureHint);
      maybeShowGestureHint();
      suppressPopups();

      var resumed = restoreState();

      /* WHEN TO GATE. Only a first-time visitor with no gym gets stopped. A saved
         build, a PDP seed, or anyone who has answered before goes straight in —
         being asked "where is your gym going?" about a gym you already built
         would be insulting, and it is the fastest way to make a returning
         customer close the tab. */
      var gateOn = root.getAttribute('data-gate') === 'on';
      var answered = false;
      try { answered = localStorage.getItem(QUIZ_KEY) === '1'; } catch (e) {}

      /* A SEEDED ARRIVAL STILL GETS THE GATE. Dillon's design: coming from a
         product page, the machine is visibly placed in the room but the room
         stays greyed until they answer as much of the questionnaire as they want
         to. Seeing their machine already in a gym is the reason to start, so
         bypassing the gate here would throw away the strongest moment the PDP
         entry point has.

         A returning visitor — saved build, or already answered — is never
         re-gated, seeded or not. Being asked "where is your gym going?" about a
         gym you already built is how you lose someone. */
      var shouldGate = gateOn && !resumed && !answered;

      if (resumed) {
        /* One call rather than five hand-written assignments that drift — the
           floor and walkway toggles were missing from this list, which is why a
           resumed session drew a rubber floor under a control still showing
           Grid. */
        ENTRY_POINT = 'Resumed From URL';
        LAST_ACTION = 'Resumed session';
        syncControls();
        if (applySeed()) { trimToFit(true); render(); saveState(); track('init'); maybeShowDragHint(); return; }
        markOverlaps();
        render();
        track('init');
        maybeShowDragHint();
        return;
      }

      syncControls();

      /* Build the default gym FIRST, then lock it. The whole point of the gate is
         that they can see a real, populated, to-scale room behind it — an empty
         floor behind a Customize button sells nothing. */
      rebuild(false);
      var seeded = applySeed();
      ENTRY_POINT = seeded ? 'Seeded From Product' : 'Direct';
      LAST_ACTION = seeded ? 'Arrived from product page' : 'Started builder';
      if (seeded) { trimToFit(true); render(); saveState(); }

      /* Arriving from a product page, put the builder in view. The hash anchor
         fires before the catalogue has loaded and the section has grown to its
         real height, so the browser's own jump lands short. */
      if (seeded) {
        setTimeout(function () {
          var target = root.querySelector('.gs__planwrap') || root;
          if (target.scrollIntoView) target.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, 120);
      }

      if (shouldGate) {
        /* Name the machine they arrived with, so the gate reads as continuing
           what they were already doing rather than starting something new. */
        if (seeded && BUILD.length) {
          GATE.kicker = 'Added to your gym';
          GATE.title = cleanTitle(BUILD[0].p.t);
          GATE.text = 'It is in the room below. Answer a couple of questions and we will build the rest of the gym around it.';
          GATE.cta = 'Build my gym around it';
          renderIntro();
        }
        lockStage(true);
      } else {
        /* Gated visitors get the hint from finishQuiz() instead, once the
           gate actually lifts and the room becomes something they can
           touch. Offering it here too would just burn the one-time show on
           a machine sitting behind a locked stage. */
        maybeShowDragHint();
      }
      track('init');
    })
    .catch(function (err) {
      if (el.status) el.status.textContent = 'Could not load the catalogue. ' + err.message;
    });
})();
